import React, { useState, useEffect } from 'react';
import { Mail, Lock, Globe, ArrowRight, Check, Sparkles, BarChart3, DollarSign, FileText, Search, User } from 'lucide-react';
import { 
  supabase,
  createUser, 
  getUserByEmail, 
  getAllUsers as supabaseGetAllUsers,
  createPublisherProfile,
  updatePublisherProfile,
  getPublisherProfile,
  getAllPublishers as supabaseGetAllPublishers,
  createAdvertiserProfile,
  getAllAdvertisers as supabaseGetAllAdvertisers,
  createPartnership,
  getAllPartnerships as supabaseGetAllPartnerships,
  updatePartnershipStatus as supabaseUpdatePartnershipStatus,
  getPartnershipsByEmail,
  createCreative,
  getAllCreatives as supabaseGetAllCreatives,
  updateCreativeStatus as supabaseUpdateCreativeStatus,
  getCreativesByPublisherEmail,
  getCreativesByAdvertiserEmail
} from './supabaseClient';

// ============================================
// MOBILE RESPONSIVE STYLES
// ============================================
const MobileStyles = () => (
  <style>{`
    /* Mobile responsive styles */
    @media (max-width: 768px) {
      /* Navigation */
      .nav-links {
        display: none !important;
      }
      .nav-container {
        padding: 0.75rem 1rem !important;
      }
      .nav-logo {
        font-size: 1.25rem !important;
      }
      
      /* Hero section */
      .hero-container {
        padding: 1rem !important;
        padding-top: 80px !important;
      }
      .hero-grid {
        grid-template-columns: 1fr !important;
        gap: 2rem !important;
      }
      .hero-title {
        font-size: 2rem !important;
        line-height: 1.2 !important;
      }
      .hero-subtitle {
        font-size: 1rem !important;
      }
      
      /* Cards */
      .feature-card, .info-card {
        padding: 1.25rem !important;
      }
      .card-grid {
        grid-template-columns: 1fr !important;
      }
      
      /* Forms */
      .form-container {
        padding: 1.5rem !important;
        margin: 0 1rem !important;
      }
      .form-input {
        font-size: 16px !important; /* Prevents zoom on iOS */
      }
      
      /* Buttons */
      .btn-primary, .btn-secondary {
        width: 100% !important;
        padding: 1rem !important;
      }
      
      /* Dashboard */
      .dashboard-container {
        padding: 1rem !important;
      }
      .dashboard-header {
        flex-direction: column !important;
        gap: 1rem !important;
        text-align: center !important;
      }
      .dashboard-grid {
        grid-template-columns: 1fr !important;
      }
      .stats-grid {
        grid-template-columns: 1fr 1fr !important;
      }
      
      /* Onboarding */
      .onboarding-container {
        padding: 1rem !important;
      }
      .step-content {
        padding: 1.5rem !important;
      }
      .checkbox-grid {
        grid-template-columns: 1fr !important;
      }
      
      /* Typography adjustments */
      h1 { font-size: 1.75rem !important; }
      h2 { font-size: 1.5rem !important; }
      h3 { font-size: 1.25rem !important; }
      
      /* FAQ section */
      .faq-grid {
        grid-template-columns: 1fr !important;
      }
      .faq-question {
        font-size: 1rem !important;
      }
      
      /* Hide desktop-only elements */
      .desktop-only {
        display: none !important;
      }
      
      /* Show mobile-only elements */
      .mobile-only {
        display: block !important;
      }
      
      /* Fix horizontal overflow */
      body, html {
        overflow-x: hidden;
      }
      
      /* Improve touch targets */
      button, a, input, select {
        min-height: 44px;
      }
    }
    
    /* Small mobile (iPhone SE, etc.) */
    @media (max-width: 375px) {
      .hero-title {
        font-size: 1.5rem !important;
      }
      .stats-grid {
        grid-template-columns: 1fr !important;
      }
      .form-container {
        padding: 1rem !important;
      }
    }
    
    /* Tablet */
    @media (min-width: 769px) and (max-width: 1024px) {
      .hero-grid {
        grid-template-columns: 1fr !important;
        max-width: 600px !important;
        margin: 0 auto !important;
      }
      .card-grid {
        grid-template-columns: 1fr 1fr !important;
      }
    }
    
    /* Default mobile-only hidden */
    .mobile-only {
      display: none;
    }
  `}</style>
);

// ============================================
// HELPER FUNCTIONS - HYBRID (localStorage + Supabase)
// ============================================

// Safe localStorage access (still used for session management)
const safeLocalStorage = {
  getItem: (key) => {
    if (typeof window !== 'undefined' && window.localStorage) {
      return localStorage.getItem(key);
    }
    return null;
  },
  setItem: (key, value) => {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem(key, value);
    }
  }
};

// Get all users from localStorage (fallback/cache)
function getAllUsers() {
  try {
    const users = safeLocalStorage.getItem('mavstream_all_users');
    return users ? JSON.parse(users) : [];
  } catch (e) {
    console.error('Error reading users:', e);
    return [];
  }
}

// Save a user to the all_users list (localStorage cache)
function saveUserToDirectory(user) {
  try {
    const users = getAllUsers();
    const existingIndex = users.findIndex(u => u.authData?.email === user.authData?.email);
    if (existingIndex >= 0) {
      users[existingIndex] = user;
    } else {
      users.push(user);
    }
    safeLocalStorage.setItem('mavstream_all_users', JSON.stringify(users));
  } catch (e) {
    console.error('Error saving user to directory:', e);
  }
}

// Get all publishers - tries Supabase first, falls back to localStorage
async function getAllPublishersAsync() {
  try {
    const publishers = await supabaseGetAllPublishers();
    if (publishers && publishers.length > 0) {
      return publishers.map(u => ({
        authData: {
          email: u.email,
          companyLegalName: u.company_legal_name,
          brandNameDBA: u.brand_name_dba,
          firstName: u.first_name,
          lastName: u.last_name,
          phone: u.phone,
          website: u.website
        },
        accountType: u.account_type,
        publisherProfile: u.publisher_profiles?.[0] ? {
          companyDescription: u.publisher_profiles[0].company_description,
          city: u.publisher_profiles[0].city,
          state: u.publisher_profiles[0].state,
          primaryCategory: u.publisher_profiles[0].primary_category,
          listSize: u.publisher_profiles[0].list_size,
          engagedSubscribers: u.publisher_profiles[0].engaged_subscribers,
          websiteVisitors: u.publisher_profiles[0].website_visitors,
          socialFollowers: u.publisher_profiles[0].social_followers,
          drScore: u.publisher_profiles[0].dr_score,
          placementTypes: u.publisher_profiles[0].placement_types || [],
          regions: u.publisher_profiles[0].regions || [],
          genderMix: u.publisher_profiles[0].gender_mix,
          ageRanges: u.publisher_profiles[0].age_ranges || [],
          householdIncomes: u.publisher_profiles[0].household_incomes || [],
          monthlyRevenue: u.publisher_profiles[0].monthly_revenue,
          averageOrderValue: u.publisher_profiles[0].average_order_value,
          campaignFrequency: u.publisher_profiles[0].campaign_frequency,
          primaryBusinessModels: u.publisher_profiles[0].primary_business_models || [],
          maxPlacementsPerMonth: u.publisher_profiles[0].max_placements_per_month
        } : null,
        publisherOnboardingCompleted: u.publisher_profiles?.[0]?.onboarding_completed || false,
        createdAt: u.created_at,
        id: u.id
      }));
    }
  } catch (e) {
    console.error('Error fetching publishers from Supabase:', e);
  }
  // Fallback to localStorage
  const users = getAllUsers();
  return users.filter(u => u.publisherOnboardingCompleted && u.publisherProfile);
}

// Synchronous version for components that can't use async
function getAllPublishers() {
  const users = getAllUsers();
  return users.filter(u => u.publisherOnboardingCompleted && u.publisherProfile);
}

// Get all advertisers - tries Supabase first
async function getAllAdvertisersAsync() {
  try {
    const advertisers = await supabaseGetAllAdvertisers();
    if (advertisers && advertisers.length > 0) {
      return advertisers.map(u => ({
        authData: {
          email: u.email,
          companyLegalName: u.company_legal_name,
          brandNameDBA: u.brand_name_dba,
          firstName: u.first_name,
          lastName: u.last_name,
          phone: u.phone,
          website: u.website
        },
        accountType: u.account_type,
        advertiserProfile: u.advertiser_profiles?.[0] ? {
          companyDescription: u.advertiser_profiles[0].company_description,
          city: u.advertiser_profiles[0].city,
          state: u.advertiser_profiles[0].state,
          primaryCategory: u.advertiser_profiles[0].primary_category,
          monthlyBudget: u.advertiser_profiles[0].monthly_budget,
          campaignGoal: u.advertiser_profiles[0].campaign_goal,
          targetCategories: u.advertiser_profiles[0].target_categories || [],
          targetRegions: u.advertiser_profiles[0].target_regions || []
        } : null,
        advertiserOnboardingCompleted: u.advertiser_profiles?.[0]?.onboarding_completed || false,
        createdAt: u.created_at,
        id: u.id
      }));
    }
  } catch (e) {
    console.error('Error fetching advertisers from Supabase:', e);
  }
  // Fallback to localStorage
  const users = getAllUsers();
  return users.filter(u => u.advertiserOnboardingCompleted && u.advertiserProfile);
}

// Synchronous version
function getAllAdvertisers() {
  const users = getAllUsers();
  return users.filter(u => u.advertiserOnboardingCompleted && u.advertiserProfile);
}

// Get all partnerships - from Supabase with localStorage cache
async function getAllPartnershipsAsync() {
  try {
    const partnerships = await supabaseGetAllPartnerships();
    if (partnerships && partnerships.length > 0) {
      // Also cache to localStorage
      safeLocalStorage.setItem('mavstream_partnerships', JSON.stringify(partnerships));
      return partnerships;
    }
  } catch (e) {
    console.error('Error fetching partnerships from Supabase:', e);
  }
  // Fallback to localStorage
  try {
    const partnerships = safeLocalStorage.getItem('mavstream_partnerships');
    return partnerships ? JSON.parse(partnerships) : [];
  } catch (e) {
    return [];
  }
}

// Synchronous version for immediate use
function getAllPartnerships() {
  try {
    const partnerships = safeLocalStorage.getItem('mavstream_partnerships');
    return partnerships ? JSON.parse(partnerships) : [];
  } catch (e) {
    console.error('Error reading partnerships:', e);
    return [];
  }
}

// Save a new partnership - to both Supabase and localStorage
async function savePartnershipAsync(partnership) {
  try {
    const result = await createPartnership(partnership);
    // Also save to localStorage cache
    const partnerships = getAllPartnerships();
    partnerships.push(result);
    safeLocalStorage.setItem('mavstream_partnerships', JSON.stringify(partnerships));
    return result;
  } catch (e) {
    console.error('Error saving partnership to Supabase:', e);
    // Fallback to localStorage only
    return savePartnership(partnership);
  }
}

// Synchronous version (localStorage only)
function savePartnership(partnership) {
  try {
    const partnerships = getAllPartnerships();
    partnership.id = partnership.id || `ptr-${Date.now()}`;
    partnership.createdAt = partnership.createdAt || new Date().toISOString();
    partnerships.push(partnership);
    safeLocalStorage.setItem('mavstream_partnerships', JSON.stringify(partnerships));
    return partnership;
  } catch (e) {
    console.error('Error saving partnership:', e);
    return null;
  }
}

// Update partnership status - both Supabase and localStorage
async function updatePartnershipStatusAsync(partnershipId, newStatus) {
  try {
    const result = await supabaseUpdatePartnershipStatus(partnershipId, newStatus);
    // Update localStorage cache
    const partnerships = getAllPartnerships();
    const index = partnerships.findIndex(p => p.id === partnershipId);
    if (index >= 0) {
      partnerships[index].status = newStatus;
      partnerships[index].updatedAt = new Date().toISOString();
      safeLocalStorage.setItem('mavstream_partnerships', JSON.stringify(partnerships));
    }
    return result;
  } catch (e) {
    console.error('Error updating partnership in Supabase:', e);
    return updatePartnershipStatus(partnershipId, newStatus);
  }
}

// Synchronous version
function updatePartnershipStatus(partnershipId, newStatus) {
  try {
    const partnerships = getAllPartnerships();
    const index = partnerships.findIndex(p => p.id === partnershipId);
    if (index >= 0) {
      partnerships[index].status = newStatus;
      partnerships[index].updatedAt = new Date().toISOString();
      safeLocalStorage.setItem('mavstream_partnerships', JSON.stringify(partnerships));
      return partnerships[index];
    }
    return null;
  } catch (e) {
    console.error('Error updating partnership:', e);
    return null;
  }
}

// ============================================
// CREATIVE SUBMISSION FUNCTIONS
// ============================================

// Get all creatives - from Supabase with localStorage cache
async function getAllCreativesAsync() {
  try {
    const creatives = await supabaseGetAllCreatives();
    if (creatives && creatives.length > 0) {
      safeLocalStorage.setItem('mavstream_creatives', JSON.stringify(creatives));
      return creatives;
    }
  } catch (e) {
    console.error('Error fetching creatives from Supabase:', e);
  }
  // Fallback to localStorage
  try {
    const creatives = safeLocalStorage.getItem('mavstream_creatives');
    return creatives ? JSON.parse(creatives) : [];
  } catch (e) {
    return [];
  }
}

// Synchronous version
function getAllCreatives() {
  try {
    const creatives = safeLocalStorage.getItem('mavstream_creatives');
    return creatives ? JSON.parse(creatives) : [];
  } catch (e) {
    console.error('Error reading creatives:', e);
    return [];
  }
}

// Save a new creative - to both Supabase and localStorage
async function saveCreativeAsync(creative) {
  try {
    const result = await createCreative(creative);
    // Also save to localStorage cache
    const creatives = getAllCreatives();
    creatives.push(result);
    safeLocalStorage.setItem('mavstream_creatives', JSON.stringify(creatives));
    return result;
  } catch (e) {
    console.error('Error saving creative to Supabase:', e);
    return saveCreative(creative);
  }
}

// Synchronous version
function saveCreative(creative) {
  try {
    const creatives = getAllCreatives();
    creative.id = creative.id || `cre-${Date.now()}`;
    creative.createdAt = creative.createdAt || new Date().toISOString();
    creative.status = creative.status || 'pending';
    creatives.push(creative);
    safeLocalStorage.setItem('mavstream_creatives', JSON.stringify(creatives));
    return creative;
  } catch (e) {
    console.error('Error saving creative:', e);
    return null;
  }
}

// Update creative status - both Supabase and localStorage
async function updateCreativeStatusAsync(creativeId, newStatus, feedback = '') {
  try {
    const result = await supabaseUpdateCreativeStatus(creativeId, newStatus, feedback);
    // Update localStorage cache
    const creatives = getAllCreatives();
    const index = creatives.findIndex(c => c.id === creativeId);
    if (index >= 0) {
      creatives[index].status = newStatus;
      creatives[index].updatedAt = new Date().toISOString();
      if (feedback) creatives[index].feedback = feedback;
      if (newStatus === 'approved') creatives[index].approvedAt = new Date().toISOString();
      safeLocalStorage.setItem('mavstream_creatives', JSON.stringify(creatives));
    }
    return result;
  } catch (e) {
    console.error('Error updating creative in Supabase:', e);
    return updateCreativeStatus(creativeId, newStatus, feedback);
  }
}

// Synchronous version
function updateCreativeStatus(creativeId, newStatus, feedback = '') {
  try {
    const creatives = getAllCreatives();
    const index = creatives.findIndex(c => c.id === creativeId);
    if (index >= 0) {
      creatives[index].status = newStatus;
      creatives[index].updatedAt = new Date().toISOString();
      if (feedback) {
        creatives[index].feedback = feedback;
      }
      if (newStatus === 'approved') {
        creatives[index].approvedAt = new Date().toISOString();
      }
      safeLocalStorage.setItem('mavstream_creatives', JSON.stringify(creatives));
      return creatives[index];
    }
    return null;
  } catch (e) {
    console.error('Error updating creative:', e);
    return null;
  }
}

// Get creatives for a specific partnership
function getCreativesForPartnership(partnershipId) {
  const creatives = getAllCreatives();
  return creatives.filter(c => c.partnershipId === partnershipId);
}

// Get creatives pending approval for a publisher
function getPendingCreativesForPublisher(publisherEmail) {
  const creatives = getAllCreatives();
  return creatives.filter(c => c.publisherEmail === publisherEmail && c.status === 'pending');
}

// Format date for display
function formatDate(dateString) {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) {
    return 'N/A';
  }
}

// ============================================
// MAIN APPLICATION
// ============================================

export default function SupplyMarketplace() {
  // Launch flag - flip to true when ready to launch advertiser onboarding
  const ADVERTISER_ONBOARDING_LIVE = true;
  
  const [currentScreen, setCurrentScreen] = useState('auth'); // auth, about, how-it-works, faqs, accountType, onboarding, advertiserWaitlist, dashboard, admin
  const [authMode, setAuthMode] = useState('signup'); // login, signup
  const [accountType, setAccountType] = useState(''); // supplier, advertiser, both - no default
  const [activeRole, setActiveRole] = useState('publisher'); // publisher or advertiser - which view they're currently seeing
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [onboardingType, setOnboardingType] = useState(''); // 'publisher' or 'advertiser'
  const [activeTab, setActiveTab] = useState('overview');
  
  // Admin login state
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminUsername, setAdminUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminLoginError, setAdminLoginError] = useState('');
  
  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentModalType, setPaymentModalType] = useState(''); // 'accept-opportunity' or 'create-campaign'
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  
  // Admin credentials (in production, use proper auth)
  const ADMIN_CREDENTIALS = {
    username: 'admin',
    password: 'mavstream2024'
  };
  
  // Handle admin login
  const handleAdminLogin = (e) => {
    e.preventDefault();
    if (adminUsername === ADMIN_CREDENTIALS.username && adminPassword === ADMIN_CREDENTIALS.password) {
      setCurrentScreen('admin');
      setShowAdminLogin(false);
      setAdminUsername('');
      setAdminPassword('');
      setAdminLoginError('');
    } else {
      setAdminLoginError('Invalid username or password');
    }
  };
  
  // Check for admin access via URL parameter on load
  React.useEffect(() => {
    // Debug: Check localStorage on page load
    console.log('=== PAGE LOAD ===');
    console.log('localStorage mavstream_user on load:', localStorage.getItem('mavstream_user') ? 'EXISTS' : 'NULL');
    
    // Check URL for admin access: ?admin=mavstream2024
    const urlParams = new URLSearchParams(window.location.search);
    const adminParam = urlParams.get('admin');
    if (adminParam === 'mavstream2024') {
      setCurrentScreen('admin');
    }
    
    // Load stored user data on init
    const storedUser = localStorage.getItem('mavstream_user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      
      // Load auth data
      if (user.authData) {
        setAuthData(user.authData);
      }
      
      // Load account type
      if (user.accountType) {
        setAccountType(user.accountType);
      }
      
      // Load publisher profile
      if (user.publisherProfile) {
        setPublisherProfile(prev => ({
          ...prev,
          ...user.publisherProfile
        }));
      }
      
      // Load advertiser profile
      if (user.advertiserProfile) {
        setAdvertiserProfile(prev => ({
          ...prev,
          ...user.advertiserProfile
        }));
      }
      
      // If user has completed onboarding, go to dashboard
      if (user.publisherOnboardingCompleted || user.advertiserOnboardingCompleted) {
        setCurrentScreen('dashboard');
      }
    }
    
    // Uncomment the line below ONLY to reset and start completely fresh
    // localStorage.removeItem('mavstream_user');
    // localStorage.removeItem('mavstream_all_users');
    // localStorage.removeItem('mavstream_partnerships');
  }, []);
  
  // Auth form state
  const [authData, setAuthData] = useState({
    companyLegalName: '',
    brandNameDBA: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    password: '',
    website: ''
  });

  // Validation state
  const [validationErrors, setValidationErrors] = useState({
    email: '',
    website: ''
  });

  // Onboarding form state - Publisher profile
  const [publisherProfile, setPublisherProfile] = useState({
    // Step 1 fields
    companyDescription: '', // New field for one-sentence company description
    city: '',
    state: '',
    country: 'United States',
    rolesAtCompany: [], // Changed to array for multiple selections
    howDidYouHear: '',
    howDidYouHearOther: '', // New field for "Other" text input
    excludedCategories: '',
    primaryBusinessModels: [], // Changed to array for multiple selections
    businessModelOther: '', // New field for "Other" text input
    // Existing fields
    primaryCategory: '',
    primaryCategories: [], // New field for multiple category selections
    listSize: '',
    engagedSubscribers: '',
    socialFollowers: '', // New field for total social media followers
    websiteVisitors: '', // New field for monthly website visitors
    campaignFrequency: '',
    regions: [],
    internationalDetails: '',
    genderMix: '',
    ageRanges: [], // Changed to array for multiple selections
    householdIncomes: [], // Changed to array for multiple selections
    monthlyRevenue: '',
    averageOrderValue: '', // New optional field
    drScore: '', // New field for Domain Rating score
    acceptedCategories: [],
    placementTypes: [],
    packagingOther: '', // New field for "Other" packaging option
    maxPlacementsPerMonth: '',
    creativeApproval: '',
    consentGiven: false,
    // Affinity fields
    customerInterests: [], // Fitness, Beauty, Nutrition, etc.
    customerProducts: [], // Supplements, Cosmetics, etc. or ['N/A']
    brandAttributes: [] // Premium, Value-focused, etc.
  });

  // Advertiser profile data
  const [advertiserProfile, setAdvertiserProfile] = useState({
    // Step 1: Basic Context
    city: '',
    state: '',
    country: 'United States',
    roleAtCompany: '',
    howDidYouHear: '',
    howDidYouHearOther: '',
    primaryBusinessModel: '', // DTC, Retailer, Service, etc.
    businessType: '', // Legacy field
    businessCategory: '', // Legacy field
    customerLocations: [], // US, Canada, UK, etc.
    
    // Step 2: Purpose & Goals
    primaryPurpose: '', // Brand awareness, Acquisition, etc.
    primaryOutcome: '', // New customers, Traffic, etc.
    
    // Step 3: Economics & Constraints
    targetCAC: '', // Under $50, $50-$100, etc.
    averageOrderValue: '', // Under $50, $50-$100, etc.
    
    // Step 4: Placement Preferences
    interestedPlacements: [], // Email, Website, Social, etc.
    preferredPartnerBrands: '', // Free text
    excludedCategories: '', // Free text or checkboxes
    
    // Step 5: Budget & Timing
    startingBudget: '', // $500, $1000, etc.
    startTimeline: '', // ASAP, 30 days, exploring
    
    // Step 6: Creative & Approvals
    creativeReady: '', // Yes, No, Not sure
    
    // Step 7: Consent
    consentGiven: false,
    
    // Affinity fields (target customer affinities)
    customerInterests: [], // Fitness, Beauty, Nutrition, etc.
    customerProducts: [], // Supplements, Cosmetics, etc. or ['N/A']
    brandAttributes: [] // Premium, Value-focused, etc.
  });

  // Advertiser waitlist data (short form)
  const [advertiserWaitlist, setAdvertiserWaitlist] = useState({
    monthlyBudget: '',
    advertisingGoal: '',
    targetAudience: [],
    launchTimeline: ''
  });

  // Sample dashboard data - will use stored data if available
  const getPartnerData = () => {
    const storedUser = localStorage.getItem('mavstream_user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      return {
        name: user.authData.brandNameDBA || user.authData.companyLegalName || `${user.authData.firstName} ${user.authData.lastName}`,
        contactName: `${user.authData.firstName} ${user.authData.lastName}`,
        email: user.authData.email,
        website: user.authData.website,
        emailDatabase: user.publisherProfile?.listSize || 0,
        monthlyEmails: 0,
        adSlots: 0,
        earnings: {
          thisMonth: 0,
          lastMonth: 0,
          total: 0
        },
        campaigns: [],
        campaignsToDate: 0,
        impressions: 0,
        clicks: 0,
        conversions: 0
      };
    }
    // Fallback uses current authData state
    return {
      name: authData.brandNameDBA || authData.companyLegalName || `${authData.firstName} ${authData.lastName}` || "Your Company",
      contactName: `${authData.firstName} ${authData.lastName}`,
      email: authData.email,
      website: authData.website,
      emailDatabase: 0,
      monthlyEmails: 0,
      adSlots: 0,
      earnings: {
        thisMonth: 0,
        lastMonth: 0,
        total: 0
      },
      campaigns: [],
      campaignsToDate: 0,
      impressions: 0,
      clicks: 0,
      conversions: 0
    };
  };

  const [partnerData, setPartnerData] = useState(getPartnerData());

  // Update partnerData whenever authData changes (after login/signup)
  React.useEffect(() => {
    setPartnerData(getPartnerData());
  }, [authData.brandNameDBA, authData.companyLegalName, authData.firstName, authData.lastName]);

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return 'Email is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  // Normalize website URL - auto-add https:// if missing
  const normalizeWebsite = (website) => {
    if (!website) return '';
    
    let normalized = website.trim();
    
    // Remove any leading/trailing whitespace
    normalized = normalized.trim();
    
    // If it already has http:// or https://, use as-is
    if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
      return normalized;
    }
    
    // Remove www. temporarily to check the base domain
    let baseUrl = normalized.replace(/^www\./i, '');
    
    // Add https:// and www. if it was there originally
    if (normalized.startsWith('www.')) {
      return `https://www.${baseUrl}`;
    } else {
      return `https://${normalized}`;
    }
  };

  const validateWebsite = (website) => {
    if (!website) {
      return 'Website URL is required';
    }
    
    // Normalize the URL first
    const normalizedUrl = normalizeWebsite(website);
    
    // Basic URL validation
    try {
      const url = new URL(normalizedUrl);
      // Check if domain has at least one dot (e.g., example.com)
      if (!url.hostname.includes('.')) {
        return 'Please enter a valid website URL (e.g., example.com)';
      }
      return '';
    } catch (e) {
      return 'Please enter a valid website URL (e.g., example.com or www.example.com)';
    }
  };

  const handleAuth = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    if (authMode === 'signup') {
      // Normalize website URL before validation
      const normalizedWebsite = normalizeWebsite(authData.website);
      
      // Validate email and website
      const emailError = validateEmail(authData.email);
      const websiteError = validateWebsite(authData.website);
      
      if (emailError || websiteError) {
        setValidationErrors({
          email: emailError,
          website: websiteError
        });
        
        // Scroll to first error
        if (emailError) {
          alert('Please enter a valid email address');
        } else if (websiteError) {
          alert(websiteError);
        }
        return;
      }
      
      // Clear validation errors
      setValidationErrors({ email: '', website: '' });
      
      // Update authData with normalized website
      const normalizedAuthData = {
        ...authData,
        website: normalizedWebsite
      };
      
      // Save to Supabase
      let supabaseId = null;
      try {
        const supabaseUser = await createUser({
          email: normalizedAuthData.email,
          password: normalizedAuthData.password,
          companyLegalName: normalizedAuthData.companyLegalName,
          brandNameDBA: normalizedAuthData.brandNameDBA,
          firstName: normalizedAuthData.firstName,
          lastName: normalizedAuthData.lastName,
          phone: normalizedAuthData.phone,
          website: normalizedAuthData.website,
          accountType: ''
        });
        console.log('User saved to Supabase:', supabaseUser);
        supabaseId = supabaseUser?.id;
      } catch (error) {
        console.error('Error saving to Supabase (will continue with localStorage):', error);
      }
      
      // Always save to localStorage (primary storage for session)
      const userData = {
        authData: normalizedAuthData,
        accountType: '',
        publisherProfile,
        advertiserProfile,
        createdAt: new Date().toISOString(),
        supabaseId: supabaseId
      };
      
      // Debug: Log before saving
      console.log('=== SAVING TO LOCALSTORAGE ===');
      console.log('userData to save:', JSON.stringify(userData));
      
      try {
        localStorage.setItem('mavstream_user', JSON.stringify(userData));
        
        // Verify it was saved
        const verification = localStorage.getItem('mavstream_user');
        console.log('Verification - localStorage after save:', verification ? 'SUCCESS' : 'FAILED');
        console.log('Saved email:', JSON.parse(verification)?.authData?.email);
      } catch (storageError) {
        console.error('localStorage.setItem FAILED:', storageError);
        alert('Error saving to browser storage. Please try a regular browser window (not incognito).');
        return;
      }
      
      saveUserToDirectory(userData);
      console.log('Account created successfully');
      setCurrentScreen('accountType');
    } else {
      // LOGIN - Check localStorage first (most reliable), then Supabase as backup
      const enteredEmail = authData.email.trim().toLowerCase();
      const enteredPassword = authData.password;
      
      console.log('=== LOGIN ATTEMPT ===');
      console.log('Entered email:', enteredEmail);
      console.log('Entered password length:', enteredPassword?.length);
      
      // First try localStorage (fastest and most reliable for same browser)
      const storedUser = localStorage.getItem('mavstream_user');
      console.log('localStorage mavstream_user:', storedUser ? 'EXISTS' : 'NULL');
      
      if (storedUser) {
        const localUser = JSON.parse(storedUser);
        console.log('Parsed localStorage user:', localUser);
        const storedEmail = localUser.authData?.email?.trim().toLowerCase();
        const storedPassword = localUser.authData?.password;
        console.log('Stored email:', storedEmail);
        console.log('Stored password length:', storedPassword?.length);
        console.log('Email match:', enteredEmail === storedEmail);
        console.log('Password match:', enteredPassword === storedPassword);
        
        if (enteredEmail === storedEmail && enteredPassword === storedPassword) {
          console.log('Login successful via localStorage!');
          setAuthData(localUser.authData);
          setAccountType(localUser.accountType || 'supplier');
          setPublisherProfile(localUser.publisherProfile || publisherProfile);
          setAdvertiserProfile(localUser.advertiserProfile || advertiserProfile);
          
          // Route to dashboard or onboarding based on completion status
          if (localUser.publisherOnboardingCompleted || localUser.advertiserOnboardingCompleted) {
            setCurrentScreen('dashboard');
          } else if (localUser.accountType) {
            if (localUser.accountType === 'supplier' || localUser.accountType === 'both') {
              setOnboardingType('publisher');
              setOnboardingStep(1);
              setCurrentScreen('onboarding');
            } else {
              setOnboardingType('advertiser');
              setOnboardingStep(1);
              setCurrentScreen('onboarding');
            }
          } else {
            setCurrentScreen('accountType');
          }
          return;
        } else if (enteredEmail === storedEmail) {
          // Email matches but password doesn't
          console.log('Email matched but password did not');
          alert('Invalid password. Please try again.');
          return;
        } else {
          console.log('Email did not match stored email');
        }
      } else {
        console.log('No user found in localStorage');
      }
      
      // If not in localStorage, try Supabase (for cross-device login)
      console.log('Checking Supabase...');
      try {
        const supabaseUser = await getUserByEmail(enteredEmail);
        console.log('Supabase response:', supabaseUser);
        if (supabaseUser) {
          console.log('Found user in Supabase:', supabaseUser);
          
          // Check if Supabase has the password stored
          if (supabaseUser.password && supabaseUser.password === enteredPassword) {
            console.log('Login successful via Supabase!');
            
            // Reconstruct user data from Supabase
            const userData = {
              authData: {
                email: supabaseUser.email,
                password: enteredPassword,
                companyLegalName: supabaseUser.company_legal_name || '',
                brandNameDBA: supabaseUser.brand_name_dba || '',
                firstName: supabaseUser.first_name || '',
                lastName: supabaseUser.last_name || '',
                phone: supabaseUser.phone || '',
                website: supabaseUser.website || ''
              },
              accountType: supabaseUser.account_type || 'supplier',
              publisherProfile: supabaseUser.publisherProfile || publisherProfile,
              advertiserProfile: supabaseUser.advertiserProfile || advertiserProfile,
              supabaseId: supabaseUser.id,
              publisherOnboardingCompleted: supabaseUser.publisher_onboarding_completed || false,
              advertiserOnboardingCompleted: supabaseUser.advertiser_onboarding_completed || false
            };
            
            // Save to localStorage for future sessions
            localStorage.setItem('mavstream_user', JSON.stringify(userData));
            
            setAuthData(userData.authData);
            setAccountType(userData.accountType);
            setPublisherProfile(userData.publisherProfile);
            setAdvertiserProfile(userData.advertiserProfile);
            
            // Route based on onboarding status
            if (userData.publisherOnboardingCompleted || userData.advertiserOnboardingCompleted) {
              setCurrentScreen('dashboard');
            } else if (userData.accountType) {
              if (userData.accountType === 'supplier' || userData.accountType === 'both') {
                setOnboardingType('publisher');
                setOnboardingStep(1);
                setCurrentScreen('onboarding');
              } else {
                setOnboardingType('advertiser');
                setOnboardingStep(1);
                setCurrentScreen('onboarding');
              }
            } else {
              setCurrentScreen('accountType');
            }
            return;
          } else if (supabaseUser.password) {
            // User exists in Supabase with a password, but it doesn't match
            alert('Invalid password. Please try again.');
            return;
          } else {
            // User exists in Supabase but no password stored (old user)
            // They need to log in from original device or reset password
            alert('Account found but password not stored. Please log in from your original device, or contact support to reset your password.');
            return;
          }
        } else {
          console.log('No user found in Supabase');
        }
      } catch (error) {
        console.error('Error checking Supabase:', error);
      }
      
      // No account found anywhere
      console.log('No account found anywhere');
      alert('No account found with that email. Please sign up first.');
    }
  };

  const handleAccountTypeSelection = async (selectedType) => {
    setAccountType(selectedType);
    
    // Save account type to localStorage and Supabase
    const storedUser = localStorage.getItem('mavstream_user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      user.accountType = selectedType;
      localStorage.setItem('mavstream_user', JSON.stringify(user));
      
      // Update account type in Supabase
      if (user.supabaseId) {
        try {
          await supabase
            .from('users')
            .update({ account_type: selectedType })
            .eq('id', user.supabaseId);
          console.log('Account type updated in Supabase:', selectedType);
        } catch (err) {
          console.error('Error updating account type in Supabase:', err);
        }
      }
    }
    
    // Route based on account type and launch flag
    if (selectedType === 'supplier') {
      setOnboardingType('publisher');
      setOnboardingStep(1);
      setCurrentScreen('onboarding');
    } else if (selectedType === 'advertiser') {
      if (ADVERTISER_ONBOARDING_LIVE) {
        setOnboardingType('advertiser');
        setOnboardingStep(1);
        setCurrentScreen('onboarding');
      } else {
        setCurrentScreen('advertiserWaitlist');
      }
    } else if (selectedType === 'both') {
      // Start with publisher onboarding
      setOnboardingType('publisher');
      setOnboardingStep(1);
      setCurrentScreen('onboarding');
    }
  };

  const handleOnboardingNext = () => {
    const totalSteps = onboardingType === 'publisher' ? 8 : 7;
    const profileData = onboardingType === 'publisher' ? publisherProfile : advertiserProfile;
    
    // Validation for publisher onboarding
    if (onboardingType === 'publisher') {
      if (onboardingStep === 1) {
        // Step 1: Business Profile (simplified)
        if (!profileData.companyDescription || !profileData.rolesAtCompany?.length || 
            !profileData.primaryBusinessModels?.length) {
          alert('Please complete all required fields before continuing');
          return;
        }
      } else if (onboardingStep === 2) {
        // Step 2: Primary Category (now allows multiple)
        const hasCategories = profileData.primaryCategories?.length > 0 || profileData.primaryCategory;
        if (!hasCategories) {
          alert('Please select at least one customer category');
          return;
        }
      } else if (onboardingStep === 3) {
        // Step 3: Email List Size & Social Followers
        if (!profileData.listSize || !profileData.engagedSubscribers || !profileData.socialFollowers || !profileData.websiteVisitors || !profileData.campaignFrequency) {
          alert('Please complete all required fields before continuing');
          return;
        }
      } else if (onboardingStep === 4) {
        // Step 4: Geography
        if (!profileData.audienceType) {
          alert('Please select where your primary audience is located');
          return;
        }
      } else if (onboardingStep === 5) {
        // Step 5: Demographics
        if (!profileData.genderMix || !profileData.ageRanges.length || 
            !profileData.householdIncomes.length || !profileData.monthlyRevenue) {
          alert('Please complete all required fields before continuing');
          return;
        }
        if (!profileData.drScore) {
          alert('Please enter your DR Score or select N/A');
          return;
        }
      } else if (onboardingStep === 6) {
        // Step 6: Brand Attributes
        if (!profileData.brandAttributes?.length) {
          alert('Please select at least one brand attribute');
          return;
        }
      } else if (onboardingStep === 7) {
        // Step 7: Participation (simplified)
        if (!profileData.placementTypes?.length) {
          alert('Please select at least one placement type');
          return;
        }
      } else if (onboardingStep === 8) {
        // Step 8: Consent
        if (!profileData.consentGiven) {
          alert('Please accept the terms and conditions to continue');
          return;
        }
      }
    }
    
    // Validation for advertiser onboarding
    if (onboardingType === 'advertiser') {
      const profileData = advertiserProfile;
      
      if (onboardingStep === 1) {
        // Step 1: Business Profile (simplified)
        if (!profileData.primaryBusinessModel || !profileData.roleAtCompany) {
          alert('Please complete all required fields before continuing');
          return;
        }
      } else if (onboardingStep === 2) {
        // Step 2: Economics (CAC & AOV) - optional, no validation
      } else if (onboardingStep === 3) {
        // Step 3: Placement Preferences
        if (!profileData.interestedPlacements?.length) {
          alert('Please select at least one placement type');
          return;
        }
      } else if (onboardingStep === 4) {
        // Step 4: Brand Attributes only
        if (!profileData.brandAttributes?.length) {
          alert('Please select at least one brand attribute');
          return;
        }
      } else if (onboardingStep === 5) {
        // Step 5: Budget & Timing
        if (!profileData.startingBudget) {
          alert('Please select a starting budget');
          return;
        }
      } else if (onboardingStep === 6) {
        // Step 6: Creative & Approvals (optional)
      } else if (onboardingStep === 7) {
        // Step 7: Consent
        if (!profileData.consentGiven) {
          alert('Please accept the terms and conditions to continue');
          return;
        }
      }
    }
    
    if (onboardingStep < totalSteps) {
      setOnboardingStep(onboardingStep + 1);
      // Scroll to top of page when moving to next step
      window.scrollTo(0, 0);
    } else {
      // Onboarding complete - save to Supabase and localStorage
      const storedUser = localStorage.getItem('mavstream_user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        if (onboardingType === 'publisher') {
          user.publisherProfile = publisherProfile;
          user.publisherOnboardingCompleted = true;
          
          // Save publisher profile to Supabase with user data for company/brand name
          if (user.supabaseId) {
            const userData = {
              companyLegalName: user.companyLegalName || authData.companyLegalName,
              brandNameDBA: user.brandNameDBA || authData.brandNameDBA,
              email: user.email || authData.email
            };
            createPublisherProfile(user.supabaseId, publisherProfile, userData)
              .then(result => console.log('Publisher profile saved to Supabase:', result))
              .catch(err => console.error('Error saving publisher profile to Supabase:', err));
          }
        } else {
          user.advertiserProfile = advertiserProfile;
          user.advertiserOnboardingCompleted = true;
          
          // Save advertiser profile to Supabase with user data for company/brand name
          if (user.supabaseId) {
            const userData = {
              companyLegalName: user.companyLegalName || authData.companyLegalName,
              brandNameDBA: user.brandNameDBA || authData.brandNameDBA,
              email: user.email || authData.email
            };
            createAdvertiserProfile(user.supabaseId, advertiserProfile, userData)
              .then(result => console.log('Advertiser profile saved to Supabase:', result))
              .catch(err => console.error('Error saving advertiser profile to Supabase:', err));
          }
        }
        localStorage.setItem('mavstream_user', JSON.stringify(user));
        // Also update in all_users directory
        saveUserToDirectory(user);
      }
      
      // Check if we need to do advertiser onboarding next (for "both" users)
      if (accountType === 'both' && onboardingType === 'publisher') {
        if (ADVERTISER_ONBOARDING_LIVE) {
          // Show transition screen before advertiser onboarding
          setCurrentScreen('earnToReachTransition');
        } else {
          // Go to advertiser waitlist
          setCurrentScreen('advertiserWaitlist');
        }
      } else {
        // All done - go to dashboard
        setCurrentScreen('dashboard');
      }
    }
  };

  const handleOnboardingBack = () => {
    if (onboardingStep > 1) {
      setOnboardingStep(onboardingStep - 1);
      // Scroll to top of page when going back
      window.scrollTo(0, 0);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      localStorage.removeItem('mavstream_user');
      setCurrentScreen('auth');
      setAuthMode('login');
      // Reset form data
      setAuthData({
        companyLegalName: '',
        brandNameDBA: '',
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        password: '',
        website: ''
      });
      setPublisherProfile({
        listSize: '',
        engagedSubscribers: '',
        campaignFrequency: '',
        regions: [],
        genderMix: '',
        ageRange: '',
        householdIncome: '',
        primaryCategory: '',
        acceptedCategories: [],
        placementTypes: [],
        maxPlacementsPerMonth: '',
        creativeApproval: '',
        consentGiven: false
      });
    }
  };

  if (currentScreen === 'auth') {
    return (
      <>
        <MobileStyles />
        <AuthScreen 
          authMode={authMode} 
          setAuthMode={setAuthMode}
          authData={authData}
          setAuthData={setAuthData}
          handleAuth={handleAuth}
          validationErrors={validationErrors}
          setValidationErrors={setValidationErrors}
          setCurrentScreen={setCurrentScreen}
          showAdminLogin={showAdminLogin}
          setShowAdminLogin={setShowAdminLogin}
          adminUsername={adminUsername}
          setAdminUsername={setAdminUsername}
          adminPassword={adminPassword}
          setAdminPassword={setAdminPassword}
          adminLoginError={adminLoginError}
          setAdminLoginError={setAdminLoginError}
          handleAdminLogin={handleAdminLogin}
        />
      </>
    );
  }

  if (currentScreen === 'about') {
    return (
      <>
        <MobileStyles />
        <AboutScreen setCurrentScreen={setCurrentScreen} />
      </>
    );
  }

  if (currentScreen === 'how-it-works') {
    return (
      <>
        <MobileStyles />
        <HowItWorksScreen setCurrentScreen={setCurrentScreen} />
      </>
    );
  }

  if (currentScreen === 'faqs') {
    return (
      <>
        <MobileStyles />
        <FAQsScreen setCurrentScreen={setCurrentScreen} />
      </>
    );
  }

  if (currentScreen === 'accountType') {
    return (
      <>
        <MobileStyles />
        <AccountTypeScreen 
          accountType={accountType}
          handleAccountTypeSelection={handleAccountTypeSelection}
        />
      </>
    );
  }

  if (currentScreen === 'advertiserWaitlist') {
    return (
      <>
        <MobileStyles />
        <AdvertiserWaitlistScreen
          advertiserWaitlist={advertiserWaitlist}
          setAdvertiserWaitlist={setAdvertiserWaitlist}
          setCurrentScreen={setCurrentScreen}
          accountType={accountType}
        />
      </>
    );
  }

  if (currentScreen === 'earnToReachTransition') {
    return (
      <>
        <MobileStyles />
        <EarnToReachTransitionScreen
          setCurrentScreen={setCurrentScreen}
          setOnboardingType={setOnboardingType}
          setOnboardingStep={setOnboardingStep}
          publisherProfile={publisherProfile}
          advertiserProfile={advertiserProfile}
          setAdvertiserProfile={setAdvertiserProfile}
        />
      </>
    );
  }

  if (currentScreen === 'onboarding') {
    return (
      <>
        <MobileStyles />
        <OnboardingScreen 
          step={onboardingStep}
          onboardingType={onboardingType}
          publisherProfile={publisherProfile}
          setPublisherProfile={setPublisherProfile}
          advertiserProfile={advertiserProfile}
          setAdvertiserProfile={setAdvertiserProfile}
          handleNext={handleOnboardingNext}
          handleBack={handleOnboardingBack}
        />
      </>
    );
  }

  if (currentScreen === 'admin') {
    return (
      <>
        <MobileStyles />
        <AdminDashboard 
          setCurrentScreen={setCurrentScreen}
        />
      </>
    );
  }

  return (
    <>
      <MobileStyles />
      <DashboardScreen 
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        partnerData={partnerData}
        publisherProfile={publisherProfile}
    advertiserProfile={advertiserProfile}
    handleLogout={handleLogout}
    activeRole={activeRole}
    setActiveRole={setActiveRole}
    accountType={accountType}
    showPaymentModal={showPaymentModal}
    setShowPaymentModal={setShowPaymentModal}
    paymentModalType={paymentModalType}
    setPaymentModalType={setPaymentModalType}
    selectedOpportunity={selectedOpportunity}
    setSelectedOpportunity={setSelectedOpportunity}
  />
    </>
  );
}

// ============================================
// ACCOUNT TYPE SELECTION SCREEN
// ============================================

// ============================================
// NAVIGATION BAR COMPONENT (REUSABLE)
// ============================================

function NavigationBar({ setCurrentScreen, currentPage }) {
  return (
    <nav style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      background: 'rgba(10,14,39,0.95)',
      backdropFilter: 'blur(20px)',
      borderBottom: '1px solid rgba(232,230,227,0.1)',
      padding: '1rem 2rem'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        {/* Logo */}
        <div style={{
          fontSize: '1.5rem',
          fontWeight: 700,
          background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          cursor: 'pointer'
        }}
        onClick={() => setCurrentScreen('auth')}>
          MavStream
        </div>

        {/* Navigation Links */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '2.5rem'
        }}>
          {['about', 'how-it-works', 'faqs'].map(page => (
            <a
              key={page}
              href={`#${page}`}
              onClick={(e) => {
                e.preventDefault();
                setCurrentScreen(page);
              }}
              style={{
                fontSize: '0.9375rem',
                color: currentPage === page ? '#a855f7' : 'rgba(232,230,227,0.8)',
                textDecoration: 'none',
                fontFamily: "'Inter', sans-serif",
                fontWeight: currentPage === page ? 600 : 500,
                cursor: 'pointer',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#a855f7'}
              onMouseLeave={(e) => e.currentTarget.style.color = currentPage === page ? '#a855f7' : 'rgba(232,230,227,0.8)'}
            >
              {page === 'how-it-works' ? 'How It Works' : page === 'faqs' ? 'FAQs' : 'About'}
            </a>
          ))}

          {/* Sign Up Button */}
          <button
            onClick={() => setCurrentScreen('auth')}
            style={{
              padding: '0.625rem 1.5rem',
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '0.9375rem',
              fontWeight: 600,
              fontFamily: "'Inter', sans-serif",
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 12px rgba(168,85,247,0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(168,85,247,0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(168,85,247,0.3)';
            }}
          >
            Sign Up for Free
          </button>
        </div>
      </div>
    </nav>
  );
}

// ============================================
// ABOUT SCREEN
// ============================================

function AboutScreen({ setCurrentScreen }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #2a1f3a 100%)',
      fontFamily: "'Inter', sans-serif",
      color: '#e8e6e3'
    }}>
      <NavigationBar setCurrentScreen={setCurrentScreen} currentPage="about" />
      
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '8rem 2rem 4rem'
      }}>
        {/* Hero Section */}
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🤝</div>
          <h1 style={{
            fontSize: '3rem',
            fontWeight: 700,
            marginBottom: '1rem',
            letterSpacing: '-0.02em'
          }}>
            About MavStream
          </h1>
          <p style={{
            fontSize: '1.25rem',
            color: 'rgba(232,230,227,0.7)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            A new way for brands to grow together
          </p>
        </div>

        {/* Mission Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem',
          marginBottom: '4rem'
        }}>
          <div style={{
            background: 'rgba(34,211,238,0.05)',
            border: '1px solid rgba(34,211,238,0.2)',
            borderRadius: '16px',
            padding: '2rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💰</div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.75rem', color: '#22d3ee' }}>
              For Audience Owners
            </h3>
            <p style={{ fontSize: '1rem', color: 'rgba(232,230,227,0.8)', lineHeight: 1.6 }}>
              Monetize your owned channels — email lists, websites, social, and more — through sponsored placements you control.
            </p>
          </div>
          <div style={{
            background: 'rgba(249,115,22,0.05)',
            border: '1px solid rgba(249,115,22,0.2)',
            borderRadius: '16px',
            padding: '2rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚀</div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.75rem', color: '#f97316' }}>
              For Advertisers
            </h3>
            <p style={{ fontSize: '1rem', color: 'rgba(232,230,227,0.8)', lineHeight: 1.6 }}>
              Reach new customers through trusted brands, publishers, and retailers who already have your audience's attention.
            </p>
          </div>
        </div>
        
        {/* Story Section */}
        <div style={{
          background: 'rgba(232,230,227,0.03)',
          border: '1px solid rgba(232,230,227,0.1)',
          borderRadius: '20px',
          padding: '3rem',
          marginBottom: '4rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <span style={{ fontSize: '2rem' }}>📖</span>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 700, margin: 0 }}>Our Story</h2>
          </div>
          <div style={{
            fontSize: '1.125rem',
            lineHeight: '1.8',
            color: 'rgba(232,230,227,0.9)'
          }}>
            <p style={{ marginBottom: '1.5rem' }}>
              🎯 Our platform is a shared marketplace where businesses partner with one another to earn incremental revenue and reach new customers through trusted, brand-safe sponsored placements across owned channels — including email, websites, social, and more.
            </p>
            <p style={{ marginBottom: '1.5rem' }}>
              🔄 Instead of relying solely on crowded ad platforms like Google and Meta, we make it easy for businesses to collaborate, recommend, and feature complementary brands in ways that feel natural to customers and aligned with each brand's values.
            </p>
            <p style={{ margin: 0 }}>
              ✨ This is not an open ad exchange. It's a curated, partnership-first ecosystem built on trust, control, and mutual benefit.
            </p>
          </div>
        </div>

        <h2 style={{
          fontSize: '2rem',
          fontWeight: 700,
          marginBottom: '2rem',
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.75rem'
        }}>
          <span>⭐</span> What Makes Us Different
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '2rem'
        }}>
          {[
            { emoji: '🤝', title: 'Partner-first, not ad-tech', desc: 'Every placement is sponsored, approved, and brand-safe. There is no open bidding, no forced automation, and no loss of control.' },
            { emoji: '📧', title: 'Built for owned channels', desc: 'Email, websites, social, post-purchase touchpoints, and physical inserts — not third-party tracking or rented audiences.' },
            { emoji: '🧪', title: 'Designed for low-risk testing', desc: 'Start small, stay in control, and scale only when it works. There are no long-term commitments or required minimums.' },
            { emoji: '💎', title: 'Mutual benefit by design', desc: 'One side earns from what they already own. The other side pays to reach new customers through trusted relationships.' }
          ].map((item, i) => (
            <div key={i} style={{
              background: 'rgba(232,230,227,0.03)',
              border: '1px solid rgba(232,230,227,0.1)',
              borderRadius: '16px',
              padding: '2rem',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(34,211,238,0.3)';
              e.currentTarget.style.transform = 'translateY(-4px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(232,230,227,0.1)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>{item.emoji}</div>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 600,
                marginBottom: '1rem',
                color: '#22d3ee'
              }}>
                {item.title}
              </h3>
              <p style={{
                fontSize: '1rem',
                lineHeight: '1.6',
                color: 'rgba(232,230,227,0.8)',
                margin: 0
              }}>
                {item.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div style={{
          textAlign: 'center',
          marginTop: '4rem',
          padding: '3rem',
          background: 'linear-gradient(135deg, rgba(34,211,238,0.1) 0%, rgba(249,115,22,0.1) 100%)',
          borderRadius: '20px',
          border: '1px solid rgba(232,230,227,0.1)'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🌟</div>
          <h3 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem' }}>Ready to get started?</h3>
          <p style={{ fontSize: '1rem', color: 'rgba(232,230,227,0.7)', marginBottom: '1.5rem' }}>
            Join the marketplace and start growing your business today.
          </p>
          <button
            onClick={() => setCurrentScreen('auth')}
            style={{
              padding: '1rem 2.5rem',
              background: 'linear-gradient(135deg, #22d3ee 0%, #06b6d4 100%)',
              border: 'none',
              borderRadius: '8px',
              color: '#0a0e27',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(34,211,238,0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Sign Up Free →
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// HOW IT WORKS SCREEN  
// ============================================

function HowItWorksScreen({ setCurrentScreen }) {
  const steps = [
    {
      emoji: '🚪',
      title: 'Join the marketplace',
      desc: 'Create an account and choose how you would like to participate:',
      items: [
        { icon: '💰', text: 'Earn by featuring sponsored partner brands and generating revenue' },
        { icon: '🚀', text: 'Reach by funding placements that help you reach new customers' },
        { icon: '🔄', text: '(Some businesses do both.)' }
      ]
    },
    {
      emoji: '⚙️',
      title: 'Set your preferences',
      desc: 'You stay in full control.',
      items: [
        { icon: '📧', text: 'Choose which channels you are open to (email, website, social, and more)' },
        { icon: '🚫', text: 'Set frequency limits and category exclusions' },
        { icon: '💵', text: 'Decide how many campaigns you want to run and start with as little as a few hundred dollars per campaign' }
      ],
      note: '🔒 No sponsored placements run without approval.'
    },
    {
      emoji: '🎯',
      title: 'Get matched',
      desc: 'We curate and recommend partnerships based on:',
      items: [
        { icon: '📂', text: 'Category alignment' },
        { icon: '👥', text: 'Audience relevance' },
        { icon: '📍', text: 'Placement type' },
        { icon: '🎯', text: 'Campaign goals' }
      ],
      note: '✨ No cold outreach. No negotiations. No clutter.'
    },
    {
      emoji: '✅',
      title: 'Approve and launch',
      desc: 'Once a placement is approved:',
      items: [
        { icon: '💳', text: 'The Reach side funds the campaign' },
        { icon: '🎨', text: 'Creative is reviewed and approved' },
        { icon: '📅', text: 'The placement runs as scheduled' }
      ],
      note: '👀 Both sides always know where content appears and when.'
    },
    {
      emoji: '📊',
      title: 'Review results and payouts',
      desc: 'Track performance and get paid:',
      items: [
        { icon: '📈', text: 'The Reach side sees delivery and engagement metrics' },
        { icon: '💸', text: 'The Earn side sees earnings and receives payouts through a secure payment flow' },
        { icon: '🔧', text: 'Preferences can be updated at any time' }
      ]
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #2a1f3a 100%)',
      fontFamily: "'Inter', sans-serif",
      color: '#e8e6e3'
    }}>
      <NavigationBar setCurrentScreen={setCurrentScreen} currentPage="how-it-works" />
      
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '8rem 2rem 4rem'
      }}>
        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⚡</div>
          <h1 style={{
            fontSize: '3rem',
            fontWeight: 700,
            marginBottom: '1rem',
            letterSpacing: '-0.02em'
          }}>
            How It Works
          </h1>
          <p style={{
            fontSize: '1.25rem',
            lineHeight: '1.8',
            color: 'rgba(232,230,227,0.8)',
            maxWidth: '700px',
            margin: '0 auto'
          }}>
            Our marketplace has two sides — <span style={{ color: '#22d3ee' }}>Earn</span> and <span style={{ color: '#f97316' }}>Reach</span> — designed to work together seamlessly.
          </p>
        </div>

        {/* Two-sided explanation cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '2rem',
          marginBottom: '4rem'
        }}>
          <div style={{
            background: 'rgba(34,211,238,0.05)',
            border: '1px solid rgba(34,211,238,0.2)',
            borderRadius: '16px',
            padding: '2rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💰</div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#22d3ee', marginBottom: '0.5rem' }}>Earn Side</h3>
            <p style={{ color: 'rgba(232,230,227,0.8)', lineHeight: 1.6 }}>
              Feature sponsored brands in your owned channels and generate incremental revenue.
            </p>
          </div>
          <div style={{
            background: 'rgba(249,115,22,0.05)',
            border: '1px solid rgba(249,115,22,0.2)',
            borderRadius: '16px',
            padding: '2rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚀</div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f97316', marginBottom: '0.5rem' }}>Reach Side</h3>
            <p style={{ color: 'rgba(232,230,227,0.8)', lineHeight: 1.6 }}>
              Fund placements with trusted partners and reach new customers beyond Google & Meta.
            </p>
          </div>
        </div>

        {/* Steps */}
        <h2 style={{
          fontSize: '2rem',
          fontWeight: 700,
          textAlign: 'center',
          marginBottom: '3rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.75rem'
        }}>
          <span>📋</span> The Process
        </h2>

        <div style={{ display: 'grid', gap: '2.5rem' }}>
          {steps.map((step, i) => (
            <div key={i} style={{
              background: 'rgba(232,230,227,0.03)',
              border: '1px solid rgba(232,230,227,0.1)',
              borderRadius: '16px',
              padding: '2rem',
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: '2rem',
              alignItems: 'start',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(34,211,238,0.3)';
              e.currentTarget.style.transform = 'translateX(8px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(232,230,227,0.1)';
              e.currentTarget.style.transform = 'translateX(0)';
            }}>
              <div style={{
                width: '70px',
                height: '70px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #22d3ee 0%, #06b6d4 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2rem',
                flexShrink: 0
              }}>
                {step.emoji}
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <span style={{
                    background: 'rgba(34,211,238,0.2)',
                    color: '#22d3ee',
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    padding: '0.25rem 0.75rem',
                    borderRadius: '100px'
                  }}>
                    Step {i + 1}
                  </span>
                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    margin: 0
                  }}>
                    {step.title}
                  </h3>
                </div>
                {step.desc && (
                  <p style={{
                    fontSize: '1.0625rem',
                    lineHeight: '1.7',
                    color: 'rgba(232,230,227,0.8)',
                    marginBottom: '1rem'
                  }}>
                    {step.desc}
                  </p>
                )}
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {step.items.map((item, j) => (
                    <div key={j} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      fontSize: '1.0625rem',
                      color: 'rgba(232,230,227,0.85)'
                    }}>
                      <span style={{ fontSize: '1.25rem' }}>{item.icon}</span>
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
                {step.note && (
                  <p style={{
                    fontSize: '1rem',
                    lineHeight: '1.7',
                    color: '#22d3ee',
                    fontWeight: 500,
                    marginTop: '1rem',
                    margin: '1rem 0 0 0'
                  }}>
                    {step.note}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div style={{
          textAlign: 'center',
          marginTop: '4rem',
          padding: '3rem',
          background: 'linear-gradient(135deg, rgba(34,211,238,0.1) 0%, rgba(249,115,22,0.1) 100%)',
          borderRadius: '20px',
          border: '1px solid rgba(232,230,227,0.1)'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
          <h3 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem' }}>Ready to get started?</h3>
          <p style={{ fontSize: '1rem', color: 'rgba(232,230,227,0.7)', marginBottom: '1.5rem' }}>
            Join the marketplace and start growing your business today.
          </p>
          <button
            onClick={() => setCurrentScreen('auth')}
            style={{
              padding: '1rem 2.5rem',
              background: 'linear-gradient(135deg, #22d3ee 0%, #06b6d4 100%)',
              border: 'none',
              borderRadius: '8px',
              color: '#0a0e27',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(34,211,238,0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Sign Up Free →
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// FAQS SCREEN
// ============================================

function FAQsScreen({ setCurrentScreen }) {
  const faqs = [
    { emoji: '🤔', q: 'Is this just advertising?', a: 'No. This platform is built around sponsored partnerships, not traditional ads. All placements are sponsored, clearly labeled, and designed to feel native to the customer experience — similar to how retail media works currently in other places.' },
    { emoji: '🔒', q: 'Do I lose control over what appears to my customers?', a: 'Never. The Earn side approves: which brands can appear, where placements run, and how often they appear. Nothing runs automatically without approval.' },
    { emoji: '🛡️', q: 'Do you share customer data?', a: 'No. Customer data always stays with the business that owns it. Partners never receive access to email lists, customer identities, or personal information.' },
    { emoji: '📧', q: 'What channels can be used?', a: 'Depending on preferences, sponsored partner placements may appear across newsletters and promotional emails, post-purchase and transactional emails, websites and content pages, social placements, and physical inserts and mailers (where applicable). Participation is always opt-in.' },
    { emoji: '💳', q: 'How do payments work?', a: 'Businesses on the Reach side fund campaigns before they run. The platform manages pricing, payments, and payouts. Businesses on the Earn side receive earnings for approved placements. Payment details are only required when needed.' },
    { emoji: '📝', q: 'Is there a minimum commitment?', a: 'No. There are no long-term contracts, no required minimum spend, and no obligation to accept opportunities. Most partners start with small tests and expand from there.' },
    { emoji: '👥', q: 'Who is this platform for?', a: 'This platform is designed for DTC brands, retailers, service businesses, and other companies with engaged customer relationships. If you have an audience and want to grow or monetize through trusted partnerships, this platform is built for you.' },
    { emoji: '🧪', q: 'Can I use the platform for testing only?', a: 'Yes. Many partners begin with trial placements to evaluate fit and performance before expanding participation.' }
  ];

  const categories = [
    { emoji: '🎯', title: 'Getting Started', description: 'Questions about joining and setup' },
    { emoji: '💰', title: 'Pricing & Payments', description: 'How money flows on the platform' },
    { emoji: '🔐', title: 'Privacy & Control', description: 'Data protection and approval processes' }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #2a1f3a 100%)',
      fontFamily: "'Inter', sans-serif",
      color: '#e8e6e3'
    }}>
      <NavigationBar setCurrentScreen={setCurrentScreen} currentPage="faqs" />
      
      <div style={{
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '8rem 2rem 4rem'
      }}>
        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>❓</div>
          <h1 style={{
            fontSize: '3rem',
            fontWeight: 700,
            marginBottom: '1rem',
            letterSpacing: '-0.02em'
          }}>
            Frequently Asked Questions
          </h1>
          <p style={{
            fontSize: '1.25rem',
            color: 'rgba(232,230,227,0.7)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Everything you need to know about MavStream
          </p>
        </div>

        {/* Category Pills */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1rem',
          justifyContent: 'center',
          marginBottom: '3rem'
        }}>
          {categories.map((cat, i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'rgba(232,230,227,0.05)',
              border: '1px solid rgba(232,230,227,0.1)',
              borderRadius: '100px',
              padding: '0.75rem 1.25rem'
            }}>
              <span style={{ fontSize: '1.25rem' }}>{cat.emoji}</span>
              <span style={{ fontSize: '0.9375rem', fontWeight: 500 }}>{cat.title}</span>
            </div>
          ))}
        </div>

        {/* FAQ Cards */}
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {faqs.map((faq, i) => (
            <div key={i} style={{
              background: 'rgba(232,230,227,0.03)',
              border: '1px solid rgba(232,230,227,0.1)',
              borderRadius: '16px',
              padding: '2rem',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(34,211,238,0.3)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(232,230,227,0.1)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1rem'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, rgba(34,211,238,0.2) 0%, rgba(34,211,238,0.1) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  flexShrink: 0
                }}>
                  {faq.emoji}
                </div>
                <div>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    marginBottom: '0.75rem',
                    color: '#22d3ee'
                  }}>
                    {faq.q}
                  </h3>
                  <p style={{
                    fontSize: '1.0625rem',
                    lineHeight: '1.7',
                    color: 'rgba(232,230,227,0.85)',
                    margin: 0
                  }}>
                    {faq.a}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Still have questions */}
        <div style={{
          textAlign: 'center',
          marginTop: '4rem',
          padding: '3rem',
          background: 'linear-gradient(135deg, rgba(34,211,238,0.1) 0%, rgba(249,115,22,0.1) 100%)',
          borderRadius: '20px',
          border: '1px solid rgba(232,230,227,0.1)'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💬</div>
          <h3 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem' }}>Still have questions?</h3>
          <p style={{ fontSize: '1rem', color: 'rgba(232,230,227,0.7)', marginBottom: '1.5rem' }}>
            Sign up for free and explore the platform, or reach out to our team.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => setCurrentScreen('auth')}
              style={{
                padding: '1rem 2rem',
                background: 'linear-gradient(135deg, #22d3ee 0%, #06b6d4 100%)',
                border: 'none',
                borderRadius: '8px',
                color: '#0a0e27',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(34,211,238,0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Get Started Free →
            </button>
            <button
              onClick={() => setCurrentScreen('how-it-works')}
              style={{
                padding: '1rem 2rem',
                background: 'transparent',
                border: '1px solid rgba(232,230,227,0.2)',
                borderRadius: '8px',
                color: 'rgba(232,230,227,0.9)',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#22d3ee';
                e.currentTarget.style.color = '#22d3ee';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(232,230,227,0.2)';
                e.currentTarget.style.color = 'rgba(232,230,227,0.9)';
              }}
            >
              📖 How It Works
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// ACCOUNT TYPE SELECTION SCREEN
// ============================================

function AccountTypeScreen({ accountType, handleAccountTypeSelection }) {
  const [selectedType, setSelectedType] = useState(accountType);
  
  const handleContinue = () => {
    if (!selectedType) {
      alert('Please select how you want to use MavStream');
      return;
    }
    handleAccountTypeSelection(selectedType);
  };

  const accountTypes = [
    {
      id: 'supplier',
      title: 'I want to monetize my owned channels (email, website, social, etc.)',
      description: 'Complete setup now (7 minutes)',
      icon: '📊',
      color: '#a855f7'
    },
    {
      id: 'advertiser',
      title: 'I want to reach new customers by advertising with other businesses',
      description: 'Run campaigns across email, websites, social and other owned properties',
      icon: '📢',
      color: '#ec4899'
    },
    {
      id: 'both',
      title: 'Both - I want to do both',
      description: '',
      icon: '🔄',
      color: '#3b82f6'
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #2a1f3a 100%)',
      fontFamily: "'Crimson Pro', 'Fraunces', Georgia, serif",
      color: '#e8e6e3',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background effects */}
      <div style={{
        position: 'fixed',
        top: '-50%',
        right: '-20%',
        width: '800px',
        height: '800px',
        background: 'radial-gradient(circle, rgba(168,85,247,0.15) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        pointerEvents: 'none'
      }} />
      <div style={{
        position: 'fixed',
        bottom: '-30%',
        left: '-10%',
        width: '600px',
        height: '600px',
        background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        pointerEvents: 'none'
      }} />

      <div style={{
        width: '100%',
        maxWidth: '900px',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '3rem'
        }}>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: 700,
            margin: '0 0 0.75rem 0',
            letterSpacing: '-0.02em'
          }}>
            How do you want to use MavStream?
          </h1>
          <p style={{
            fontSize: '1.125rem',
            color: 'rgba(232,230,227,0.7)',
            margin: 0,
            fontFamily: "'Inter', sans-serif"
          }}>
            Select the option that best describes your goals
          </p>
        </div>

        {/* Account Type Cards */}
        <div style={{
          display: 'grid',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          {accountTypes.map((type, idx) => (
            <button
              key={type.id}
              onClick={() => setSelectedType(type.id)}
              style={{
                padding: '2rem',
                background: selectedType === type.id 
                  ? `linear-gradient(135deg, ${type.color}15 0%, ${type.color}08 100%)`
                  : 'rgba(232,230,227,0.03)',
                border: `2px solid ${selectedType === type.id ? type.color : 'rgba(232,230,227,0.1)'}`,
                borderRadius: '16px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textAlign: 'left',
                position: 'relative',
                animation: `slideUp 0.6s ease-out ${idx * 0.1}s backwards`
              }}
              onMouseEnter={(e) => {
                if (accountType !== type.id) {
                  e.currentTarget.style.borderColor = `${type.color}80`;
                  e.currentTarget.style.background = 'rgba(232,230,227,0.06)';
                }
              }}
              onMouseLeave={(e) => {
                if (accountType !== type.id) {
                  e.currentTarget.style.borderColor = 'rgba(232,230,227,0.1)';
                  e.currentTarget.style.background = 'rgba(232,230,227,0.03)';
                }
              }}
            >
              <style>{`
                @keyframes slideUp {
                  from { opacity: 0; transform: translateY(30px); }
                  to { opacity: 1; transform: translateY(0); }
                }
              `}</style>
              
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1.5rem'
              }}>
                {/* Icon */}
                <div style={{
                  fontSize: '3rem',
                  lineHeight: 1,
                  flexShrink: 0
                }}>
                  {type.icon}
                </div>

                {/* Content */}
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    margin: '0 0 0.5rem 0',
                    color: '#e8e6e3',
                    fontFamily: "'Inter', sans-serif",
                    lineHeight: 1.4
                  }}>
                    {type.title}
                  </h3>
                  {type.description && (
                    <p style={{
                      fontSize: '0.9375rem',
                      color: 'rgba(232,230,227,0.6)',
                      margin: 0,
                      fontFamily: "'Inter', sans-serif",
                      lineHeight: 1.6
                    }}>
                      {type.description}
                    </p>
                  )}
                </div>

                {/* Checkmark */}
                {selectedType === type.id && (
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: type.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Check size={20} color="#fff" />
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Continue Button */}
        <button
          onClick={handleContinue}
          disabled={!selectedType}
          style={{
            width: '100%',
            padding: '1.25rem',
            background: selectedType 
              ? 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)'
              : 'rgba(232,230,227,0.1)',
            border: 'none',
            borderRadius: '12px',
            color: selectedType ? '#fff' : 'rgba(232,230,227,0.4)',
            fontSize: '1.125rem',
            fontWeight: 600,
            fontFamily: "'Inter', sans-serif",
            cursor: accountType ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s ease',
            boxShadow: accountType ? '0 4px 12px rgba(168,85,247,0.3)' : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
          onMouseEnter={(e) => {
            if (selectedType) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(168,85,247,0.4)';
            }
          }}
          onMouseLeave={(e) => {
            if (selectedType) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(168,85,247,0.3)';
            }
          }}
        >
          Continue
          <ArrowRight size={20} />
        </button>
      </div>
    </div>
  );
}

// ============================================
// AUTH SCREEN COMPONENT
// ============================================

function AuthScreen({ authMode, setAuthMode, authData, setAuthData, handleAuth, validationErrors, setValidationErrors, setCurrentScreen, showAdminLogin, setShowAdminLogin, adminUsername, setAdminUsername, adminPassword, setAdminPassword, adminLoginError, setAdminLoginError, handleAdminLogin }) {
  const [useMagicLink, setUseMagicLink] = useState(false);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #2a1f3a 100%)',
      fontFamily: "'Crimson Pro', 'Fraunces', Georgia, serif",
      color: '#e8e6e3',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background effects */}
      <div style={{
        position: 'fixed',
        top: '-50%',
        right: '-20%',
        width: '800px',
        height: '800px',
        background: 'radial-gradient(circle, rgba(168,85,247,0.15) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        pointerEvents: 'none'
      }} />
      <div style={{
        position: 'fixed',
        bottom: '-30%',
        left: '-10%',
        width: '600px',
        height: '600px',
        background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        pointerEvents: 'none'
      }} />

      {/* Top Navigation Bar */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: 'rgba(10,14,39,0.8)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(232,230,227,0.1)',
        padding: '1rem 2rem'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          {/* Logo */}
          <div style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            cursor: 'pointer'
          }}
          onClick={() => setCurrentScreen('auth')}>
            MavStream
          </div>

          {/* Navigation Links */}
          <div 
            className="nav-links"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '2.5rem'
            }}>
            <a
              href="#about"
              onClick={(e) => {
                e.preventDefault();
                setCurrentScreen('about');
              }}
              style={{
                fontSize: '0.9375rem',
                color: 'rgba(232,230,227,0.8)',
                textDecoration: 'none',
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#a855f7'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(232,230,227,0.8)'}
            >
              About
            </a>
            <a
              href="#how-it-works"
              onClick={(e) => {
                e.preventDefault();
                setCurrentScreen('how-it-works');
              }}
              style={{
                fontSize: '0.9375rem',
                color: 'rgba(232,230,227,0.8)',
                textDecoration: 'none',
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#a855f7'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(232,230,227,0.8)'}
            >
              How It Works
            </a>
            <a
              href="#faqs"
              onClick={(e) => {
                e.preventDefault();
                setCurrentScreen('faqs');
              }}
              style={{
                fontSize: '0.9375rem',
                color: 'rgba(232,230,227,0.8)',
                textDecoration: 'none',
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#a855f7'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(232,230,227,0.8)'}
            >
              FAQs
            </a>

            {/* Sign Up Button */}
            <button
              onClick={() => {
                setCurrentScreen('auth');
                setTimeout(() => {
                  const signupCard = document.getElementById('signup-card');
                  if (signupCard) signupCard.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
              style={{
                padding: '0.625rem 1.5rem',
                background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '0.9375rem',
                fontWeight: 600,
                fontFamily: "'Inter', sans-serif",
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(168,85,247,0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(168,85,247,0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(168,85,247,0.3)';
              }}
            >
              Sign Up for Free
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content - Centered Signup */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '6rem 2rem 2rem 2rem'
      }}>
        <div id="signup" style={{
          width: '100%',
          maxWidth: '480px',
          position: 'relative'
        }}>
          {/* Logo/Header */}
          <div style={{
            textAlign: 'center',
            marginBottom: '3rem'
          }}>
            {/* New Hero Headlines - Two value props */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              marginBottom: '2rem'
            }}>
              <h1 style={{
                fontSize: '2.5rem',
                fontWeight: 700,
                margin: 0,
                letterSpacing: '-0.02em',
                lineHeight: '1.2',
                color: '#e8e6e3'
              }}>
                <span style={{ color: '#f97316' }}>For Advertisers:</span> Acquire Customers Beyond Google and Meta
              </h1>
              <h1 style={{
                fontSize: '2.5rem',
                fontWeight: 700,
                margin: 0,
                letterSpacing: '-0.02em',
                lineHeight: '1.2',
                color: '#e8e6e3'
              }}>
                <span style={{ color: '#22d3ee' }}>For Audience Owners:</span> Get Paid for Your Owned Audiences
              </h1>
            </div>
            
            {/* Subheadline */}
            <p style={{
              margin: '0 0 2rem 0',
              fontSize: '1.25rem',
              color: 'rgba(232,230,227,0.85)',
              fontFamily: "'Inter', sans-serif",
              lineHeight: '1.6',
              maxWidth: '800px'
            }}>
              Discover paid partnerships with trusted brands, publishers, and retailers — or monetize your owned audiences through sponsored placements.
            </p>

            {/* Above-the-fold Benefits - Two columns */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2.5rem',
              maxWidth: '800px'
            }}>
              {/* Advertisers Benefits Preview */}
              <div style={{
                background: 'rgba(249,115,22,0.08)',
                border: '1px solid rgba(249,115,22,0.25)',
                borderRadius: '12px',
                padding: '1.25rem'
              }}>
                <div style={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: '#f97316',
                  marginBottom: '0.75rem',
                  fontFamily: "'Inter', sans-serif",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  🚀 For Advertisers
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.4rem',
                  fontSize: '0.9rem',
                  color: '#e8e6e3',
                  fontFamily: "'Inter', sans-serif"
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ color: '#f97316' }}>📈</span> Gain Incremental Customers
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ color: '#f97316' }}>💎</span> Increase Your LTV
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ color: '#f97316' }}>🔐</span> Own Your Data
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ color: '#f97316' }}>🔄</span> Recapture Churned Customers
                  </div>
                </div>
              </div>

              {/* Audience Owners Benefits Preview */}
              <div style={{
                background: 'rgba(34,211,238,0.08)',
                border: '1px solid rgba(34,211,238,0.25)',
                borderRadius: '12px',
                padding: '1.25rem'
              }}>
                <div style={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: '#22d3ee',
                  marginBottom: '0.75rem',
                  fontFamily: "'Inter', sans-serif",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  💰 For Audience Owners
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.4rem',
                  fontSize: '0.9rem',
                  color: '#e8e6e3',
                  fontFamily: "'Inter', sans-serif"
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 600, color: '#22d3ee' }}>
                    <span>⭐</span> ALL ADVERTISER BENEFITS+
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ color: '#22d3ee' }}>💵</span> Generate Passive Income
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ color: '#22d3ee' }}>📧</span> Monetize Your Email List
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ color: '#22d3ee' }}>🛡️</span> Full Control Over Placements
                  </div>
                </div>
              </div>
            </div>

            {/* Hero CTAs */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              marginBottom: '4rem'
            }}>
              <button
                onClick={() => {
                  const signupCard = document.getElementById('signup-card');
                  if (signupCard) signupCard.scrollIntoView({ behavior: 'smooth' });
                }}
                style={{
                  padding: '1rem 2rem',
                  background: 'linear-gradient(135deg, #22d3ee 0%, #06b6d4 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#0a0e27',
                  fontSize: '1rem',
                  fontWeight: 600,
                  fontFamily: "'Inter', sans-serif",
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 12px rgba(34,211,238,0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(34,211,238,0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(34,211,238,0.3)';
                }}
              >
                Get Started Free
              </button>
              <button
                onClick={() => setCurrentScreen('how-it-works')}
                style={{
                  padding: '1rem 2rem',
                  background: 'transparent',
                  border: '1px solid rgba(232,230,227,0.2)',
                  borderRadius: '8px',
                  color: 'rgba(232,230,227,0.9)',
                  fontSize: '1rem',
                  fontWeight: 600,
                  fontFamily: "'Inter', sans-serif",
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#22d3ee';
                  e.currentTarget.style.color = '#22d3ee';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(232,230,227,0.2)';
                  e.currentTarget.style.color = 'rgba(232,230,227,0.9)';
                }}
              >
                How it works
              </button>
            </div>
          </div>

          {/* SECTION 2: Quick FAQs */}
          <div style={{
            maxWidth: '1000px',
            margin: '0 auto 5rem',
            padding: '2rem 2rem',
            background: 'rgba(255,255,255,0.02)',
            borderRadius: '20px',
            border: '1px solid rgba(232,230,227,0.08)'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              textAlign: 'center',
              marginBottom: '2.5rem',
              fontFamily: "'Inter', sans-serif",
              color: '#e8e6e3',
              letterSpacing: '-0.01em'
            }}>
              Quick Answers
            </h2>
            <div style={{
              display: 'grid',
              gap: '2rem'
            }}>
              {[
                { q: 'What is this?', a: 'A two-sided marketplace for customer acquisition and audience monetization.' },
                { q: 'What makes it different?', a: 'It uses owned audiences, not just Google and Meta.' },
                { q: 'Who is it for?', a: 'Brands that want to acquire customers, and audience owners that want to monetize their reach.' },
                { q: 'Where are ads placed?', a: 'In emails, banner ads, backlinks, organic social posts, and other owned placements.' },
                { q: 'How do approvals work?', a: 'Creative is approved by both sides before sponsored ads are placed.' }
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'grid',
                  gridTemplateColumns: '220px 1fr',
                  gap: '1.5rem',
                  alignItems: 'baseline',
                  paddingBottom: i < 4 ? '2rem' : 0,
                  borderBottom: i < 4 ? '1px solid rgba(232,230,227,0.08)' : 'none'
                }}>
                  <div style={{
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    color: '#22d3ee',
                    fontFamily: "'Inter', sans-serif"
                  }}>
                    {item.q}
                  </div>
                  <div style={{
                    fontSize: '1.25rem',
                    color: '#e8e6e3',
                    fontFamily: "'Inter', sans-serif",
                    lineHeight: '1.6'
                  }}>
                    {item.a}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SECTION 3: Who this is for - Cards */}
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto 6rem',
            padding: '0 2rem'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '2rem'
            }}>
              {/* Advertisers Card */}
              <div style={{
                background: 'rgba(249,115,22,0.03)',
                border: '1px solid rgba(249,115,22,0.15)',
                borderRadius: '16px',
                padding: '2.5rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(249,115,22,0.4)';
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(249,115,22,0.15)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}>
                <div style={{
                  fontSize: '2.5rem',
                  marginBottom: '1rem'
                }}>🚀</div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  margin: '0 0 0.5rem 0',
                  fontFamily: "'Inter', sans-serif",
                  color: '#f97316'
                }}>
                  Advertisers
                </h3>
                <p style={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: '#e8e6e3',
                  fontFamily: "'Inter', sans-serif",
                  marginBottom: '1rem'
                }}>
                  Acquire customers beyond Google and Meta
                </p>
                <p style={{
                  fontSize: '1rem',
                  lineHeight: '1.6',
                  color: 'rgba(232,230,227,0.7)',
                  fontFamily: "'Inter', sans-serif",
                  marginBottom: '1.5rem'
                }}>
                  Reach high-intent audiences through trusted brands, publishers, and retailers who already have your customers' attention.
                </p>
                
                {/* Key Benefits Highlight */}
                <div style={{
                  background: 'linear-gradient(135deg, rgba(249,115,22,0.15) 0%, rgba(234,88,12,0.1) 100%)',
                  border: '1px solid rgba(249,115,22,0.3)',
                  borderRadius: '12px',
                  padding: '1.25rem',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: '#f97316',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '0.75rem',
                    fontFamily: "'Inter', sans-serif"
                  }}>
                    🎯 Key Benefits
                  </div>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem'
                  }}>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#e8e6e3',
                      fontFamily: "'Inter', sans-serif",
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ color: '#f97316', fontSize: '1.25rem' }}>📈</span> Gain Incremental Customers
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#e8e6e3',
                      fontFamily: "'Inter', sans-serif",
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ color: '#f97316', fontSize: '1.25rem' }}>💎</span> Increase Your LTV
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#e8e6e3',
                      fontFamily: "'Inter', sans-serif",
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ color: '#f97316', fontSize: '1.25rem' }}>🛒</span> Find Out Where They Are Shopping
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#e8e6e3',
                      fontFamily: "'Inter', sans-serif",
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ color: '#f97316', fontSize: '1.25rem' }}>🔍</span> Find Out What Else They're Buying
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#e8e6e3',
                      fontFamily: "'Inter', sans-serif",
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ color: '#f97316', fontSize: '1.25rem' }}>🔐</span> Own Your Data & Increase Data Ownership
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#e8e6e3',
                      fontFamily: "'Inter', sans-serif",
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ color: '#f97316', fontSize: '1.25rem' }}>🎯</span> Target & Re-Target with Precision
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#e8e6e3',
                      fontFamily: "'Inter', sans-serif",
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ color: '#f97316', fontSize: '1.25rem' }}>👥</span> Build Out Look-Alike Audiences
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#e8e6e3',
                      fontFamily: "'Inter', sans-serif",
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ color: '#f97316', fontSize: '1.25rem' }}>🔄</span> Recapture Churned Customers
                    </div>
                  </div>
                </div>
                
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: '0 0 2rem 0',
                  fontFamily: "'Inter', sans-serif"
                }}>
                  <li style={{
                    fontSize: '0.9375rem',
                    color: 'rgba(232,230,227,0.8)',
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span style={{ color: '#f97316' }}>✓</span> Curated partner inventory
                  </li>
                  <li style={{
                    fontSize: '0.9375rem',
                    color: 'rgba(232,230,227,0.8)',
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span style={{ color: '#f97316' }}>✓</span> High-intent, engaged audiences
                  </li>
                  <li style={{
                    fontSize: '0.9375rem',
                    color: 'rgba(232,230,227,0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span style={{ color: '#f97316' }}>✓</span> Start with a few hundred dollars
                  </li>
                </ul>
                <button
                  onClick={() => {
                    const signupCard = document.getElementById('signup-card');
                    if (signupCard) signupCard.scrollIntoView({ behavior: 'smooth' });
                  }}
                  style={{
                    width: '100%',
                    padding: '0.875rem 1.5rem',
                    background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    fontFamily: "'Inter', sans-serif",
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(249,115,22,0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  Reach new customers
                </button>
              </div>

              {/* Audience Owners Card */}
              <div style={{
                background: 'rgba(34,211,238,0.03)',
                border: '1px solid rgba(34,211,238,0.15)',
                borderRadius: '16px',
                padding: '2.5rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(34,211,238,0.4)';
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(34,211,238,0.15)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}>
                <div style={{
                  fontSize: '2.5rem',
                  marginBottom: '1rem'
                }}>💰</div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  margin: '0 0 0.5rem 0',
                  fontFamily: "'Inter', sans-serif",
                  color: '#22d3ee'
                }}>
                  Audience Owners
                </h3>
                <p style={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: '#e8e6e3',
                  fontFamily: "'Inter', sans-serif",
                  marginBottom: '1rem'
                }}>
                  Get paid for your owned audiences
                </p>
                <p style={{
                  fontSize: '1rem',
                  lineHeight: '1.6',
                  color: 'rgba(232,230,227,0.7)',
                  fontFamily: "'Inter', sans-serif",
                  marginBottom: '1.5rem'
                }}>
                  Monetize your email lists, websites, social channels, and customer touchpoints through sponsored placements you control.
                </p>
                
                {/* Key Benefits Highlight */}
                <div style={{
                  background: 'linear-gradient(135deg, rgba(34,211,238,0.15) 0%, rgba(6,182,212,0.1) 100%)',
                  border: '1px solid rgba(34,211,238,0.3)',
                  borderRadius: '12px',
                  padding: '1.25rem',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: '#22d3ee',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '0.75rem',
                    fontFamily: "'Inter', sans-serif"
                  }}>
                    💰 Key Benefits
                  </div>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem'
                  }}>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: 800,
                      color: '#22d3ee',
                      fontFamily: "'Inter', sans-serif",
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}>
                      <span style={{ fontSize: '1.25rem' }}>⭐</span> ALL BENEFITS OF ADVERTISERS+
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#e8e6e3',
                      fontFamily: "'Inter', sans-serif",
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ color: '#22d3ee', fontSize: '1.25rem' }}>💵</span> Generate Passive Income
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#e8e6e3',
                      fontFamily: "'Inter', sans-serif",
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ color: '#22d3ee', fontSize: '1.25rem' }}>📧</span> Monetize Your Email List
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#e8e6e3',
                      fontFamily: "'Inter', sans-serif",
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ color: '#22d3ee', fontSize: '1.25rem' }}>🛡️</span> Full Control Over Placements
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#e8e6e3',
                      fontFamily: "'Inter', sans-serif",
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ color: '#22d3ee', fontSize: '1.25rem' }}>✅</span> Approve Every Ad Before It Runs
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#e8e6e3',
                      fontFamily: "'Inter', sans-serif",
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ color: '#22d3ee', fontSize: '1.25rem' }}>📈</span> Add a New Revenue Stream
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#e8e6e3',
                      fontFamily: "'Inter', sans-serif",
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ color: '#22d3ee', fontSize: '1.25rem' }}>💝</span> Add Value to Your Current Customers
                    </div>
                  </div>
                </div>
                
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: '0 0 2rem 0',
                  fontFamily: "'Inter', sans-serif"
                }}>
                  <li style={{
                    fontSize: '0.9375rem',
                    color: 'rgba(232,230,227,0.8)',
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span style={{ color: '#22d3ee' }}>✓</span> Full approval on every placement
                  </li>
                  <li style={{
                    fontSize: '0.9375rem',
                    color: 'rgba(232,230,227,0.8)',
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span style={{ color: '#22d3ee' }}>✓</span> Brand-safe partnerships only
                  </li>
                  <li style={{
                    fontSize: '0.9375rem',
                    color: 'rgba(232,230,227,0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span style={{ color: '#22d3ee' }}>✓</span> Incremental revenue stream
                  </li>
                </ul>
                <button
                  onClick={() => {
                    const signupCard = document.getElementById('signup-card');
                    if (signupCard) signupCard.scrollIntoView({ behavior: 'smooth' });
                  }}
                  style={{
                    width: '100%',
                    padding: '0.875rem 1.5rem',
                    background: 'linear-gradient(135deg, #22d3ee 0%, #06b6d4 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#0a0e27',
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    fontFamily: "'Inter', sans-serif",
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(34,211,238,0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  Start earning
                </button>
              </div>
            </div>
          </div>

          {/* SECTION 4: How one partnership works */}
          <div style={{
            maxWidth: '1000px',
            margin: '0 auto 6rem',
            padding: '0 2rem'
          }}>
            <h2 style={{
              fontSize: '2rem',
              fontWeight: 700,
              textAlign: 'center',
              marginBottom: '3rem',
              fontFamily: "'Inter', sans-serif",
              color: '#e8e6e3'
            }}>
              How one partnership works
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '2rem',
              marginBottom: '2rem'
            }}>
              {[
                { num: '1', text: 'A brand proposes a sponsored placement and we help match with the right company' },
                { num: '2', text: 'A partner reviews and approves' },
                { num: '3', text: 'The placement runs in an owned channel' },
                { num: '4', text: 'Results are tracked and earnings are paid' }
              ].map((step, i) => (
                <div key={i} style={{
                  textAlign: 'center'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem',
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    color: '#fff'
                  }}>
                    {step.num}
                  </div>
                  <p style={{
                    fontSize: '0.9375rem',
                    color: 'rgba(232,230,227,0.8)',
                    fontFamily: "'Inter', sans-serif",
                    lineHeight: '1.5',
                    margin: 0
                  }}>
                    {step.text}
                  </p>
                </div>
              ))}
            </div>
            <p style={{
              textAlign: 'center',
              fontSize: '0.9375rem',
              color: 'rgba(232,230,227,0.6)',
              fontFamily: "'Inter', sans-serif",
              fontStyle: 'italic',
              margin: 0
            }}>
              No sponsored placements run without approval.
            </p>
          </div>

          {/* SECTION 4: Signup Card - Moved down */}
          <div id="signup-card" style={{
            maxWidth: '460px',
            margin: '0 auto 6rem',
            padding: '0 1rem'
          }}>
            <div style={{
          background: 'rgba(232,230,227,0.03)',
          border: '1px solid rgba(232,230,227,0.1)',
          borderRadius: '16px',
          padding: '1.75rem',
          backdropFilter: 'blur(20px)'
        }}>
          <h2 style={{
            fontSize: '1.75rem',
            fontWeight: 700,
            margin: '0 0 0.5rem 0',
            letterSpacing: '-0.01em',
            fontFamily: "'Inter', sans-serif"
          }}>
            {authMode === 'signup' ? 'Create your account' : 'Welcome back'}
          </h2>
          
          {authMode === 'signup' && (
            <>
              <p style={{
                fontSize: '1rem',
                color: 'rgba(232,230,227,0.7)',
                fontFamily: "'Inter', sans-serif",
                margin: '0 0 0.5rem 0',
                lineHeight: '1.5'
              }}>
                Earn from your owned channels or reach new customers through trusted brands.
              </p>
              <p style={{
                fontSize: '0.875rem',
                color: 'rgba(232,230,227,0.5)',
                fontFamily: "'Inter', sans-serif",
                margin: '0 0 1.75rem 0'
              }}>
                Free to join • No commitment
              </p>
            </>
          )}

          <form onSubmit={handleAuth}>
            {authMode === 'signup' && (
              <>
                <FormFieldWithTooltip
                  label="Company Legal Name"
                  type="text"
                  value={authData.companyLegalName}
                  onChange={(e) => setAuthData({...authData, companyLegalName: e.target.value})}
                  required
                  placeholder="Acme Brand LLC"
                  tooltip="The legal and full name of the company (e.g., Acme Brand LLC or Acme Brand Inc.)"
                />
                <FormFieldWithTooltip
                  label="Company Name DBA"
                  type="text"
                  value={authData.brandNameDBA}
                  onChange={(e) => setAuthData({...authData, brandNameDBA: e.target.value})}
                  required
                  placeholder="Acme Brand"
                  tooltip="DBA stands for 'doing business as' – the name you use publicly"
                />
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '1rem'
                }}>
                  <FormField
                    label="First Name"
                    type="text"
                    value={authData.firstName}
                    onChange={(e) => setAuthData({...authData, firstName: e.target.value})}
                    required
                    placeholder="Jane"
                  />
                  <FormField
                    label="Last Name"
                    type="text"
                    value={authData.lastName}
                    onChange={(e) => setAuthData({...authData, lastName: e.target.value})}
                    required
                    placeholder="Smith"
                  />
                </div>
                <FormField
                  label="Phone Number"
                  type="tel"
                  value={authData.phone}
                  onChange={(e) => setAuthData({...authData, phone: e.target.value})}
                  required
                  placeholder="+1 (555) 123-4567"
                />
                <FormField
                  label="Website URL"
                  type="url"
                  value={authData.website}
                  onChange={(e) => {
                    setAuthData({...authData, website: e.target.value});
                    // Clear error on change
                    if (validationErrors.website) {
                      setValidationErrors({...validationErrors, website: ''});
                    }
                  }}
                  required
                  placeholder="acmebrand.com or www.acmebrand.com"
                  icon={<Globe size={18} />}
                  error={validationErrors.website}
                />
              </>
            )}
            
            <FormField
              label="Email"
              type="email"
              value={authData.email}
              onChange={(e) => {
                setAuthData({...authData, email: e.target.value});
                // Clear error on change
                if (validationErrors.email) {
                  setValidationErrors({...validationErrors, email: ''});
                }
              }}
              required
              placeholder="jane@acmebrand.com"
              icon={<Mail size={18} />}
              error={validationErrors.email}
            />

            {!useMagicLink && (
              <FormField
                label="Password"
                type="password"
                value={authData.password}
                onChange={(e) => setAuthData({...authData, password: e.target.value})}
                required={!useMagicLink}
                placeholder="••••••••"
                icon={<Lock size={18} />}
              />
            )}

            {authMode === 'login' && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem',
                  color: 'rgba(232,230,227,0.7)',
                  fontFamily: "'Inter', sans-serif",
                  cursor: 'pointer'
                }}>
                  <input 
                    type="checkbox" 
                    checked={useMagicLink}
                    onChange={(e) => setUseMagicLink(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  Use magic link instead
                </label>
                {!useMagicLink && (
                  <a href="#" style={{
                    fontSize: '0.875rem',
                    color: '#a855f7',
                    textDecoration: 'none',
                    fontFamily: "'Inter', sans-serif"
                  }}>
                    Forgot password?
                  </a>
                )}
              </div>
            )}

            <button 
              type="button"
              onClick={(e) => {
                // Different validation for login vs signup
                if (authMode === 'signup') {
                  // Check if all required fields are filled for signup
                  if (!authData.companyLegalName || !authData.brandNameDBA || 
                      !authData.firstName || !authData.lastName || 
                      !authData.phone || !authData.email || 
                      (!authData.password && !useMagicLink) || !authData.website) {
                    alert('Please fill out all required fields');
                    return;
                  }
                } else {
                  // Check if email and password are filled for login
                  if (!authData.email || (!authData.password && !useMagicLink)) {
                    alert('Please enter your email and password');
                    return;
                  }
                }
                handleAuth(e);
              }}
              style={{
              width: '100%',
              padding: '1rem',
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              border: 'none',
              borderRadius: '10px',
              color: '#fff',
              fontSize: '1rem',
              fontWeight: 600,
              fontFamily: "'Inter', sans-serif",
              cursor: 'pointer',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              boxShadow: '0 4px 12px rgba(168,85,247,0.3)',
              marginBottom: '1rem'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(168,85,247,0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(168,85,247,0.3)';
            }}>
              {authMode === 'signup' 
                ? (useMagicLink ? 'Send Magic Link' : 'Create Account')
                : (useMagicLink ? 'Send Magic Link' : 'Sign In')}
            </button>

            <div style={{
              textAlign: 'center',
              fontSize: '0.9375rem',
              color: 'rgba(232,230,227,0.6)',
              fontFamily: "'Inter', sans-serif"
            }}>
              {authMode === 'signup' ? (
                <>
                  Already have an account?{' '}
                  <a 
                    href="#" 
                    onClick={(e) => {
                      e.preventDefault();
                      setAuthMode('login');
                    }}
                    style={{
                      color: '#a855f7',
                      textDecoration: 'none',
                      fontWeight: 600
                    }}
                  >
                    Sign in
                  </a>
                </>
              ) : (
                <>
                  Don't have an account?{' '}
                  <a 
                    href="#" 
                    onClick={(e) => {
                      e.preventDefault();
                      setAuthMode('signup');
                    }}
                    style={{
                      color: '#a855f7',
                      textDecoration: 'none',
                      fontWeight: 600
                    }}
                  >
                    Sign up
                  </a>
                </>
              )}
            </div>
            
          </form>
        </div>
      </div>
      </div>
        </div>


      {/* SECTION 6: Trust & Safety */}
      <div style={{
        maxWidth: '1000px',
        margin: '0 auto 6rem',
        padding: '0 2rem'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '2rem'
        }}>
          {[
            { icon: '✓', text: 'Full approval control' },
            { icon: '💳', text: 'Secure payments & payouts' },
            { icon: '🚫', text: 'No long-term commitments' }
          ].map((item, i) => (
            <div key={i} style={{
              textAlign: 'center',
              padding: '1.5rem',
              background: 'rgba(232,230,227,0.02)',
              border: '1px solid rgba(232,230,227,0.1)',
              borderRadius: '12px'
            }}>
              <div style={{
                fontSize: '2rem',
                marginBottom: '0.75rem'
              }}>
                {item.icon}
              </div>
              <p style={{
                fontSize: '0.9375rem',
                color: 'rgba(232,230,227,0.8)',
                fontFamily: "'Inter', sans-serif",
                margin: 0
              }}>
                {item.text}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* SECTION 7: Final CTA */}
      <div style={{
        maxWidth: '600px',
        margin: '0 auto 4rem',
        padding: '0 2rem',
        textAlign: 'center'
      }}>
        <h2 style={{
          fontSize: '2rem',
          fontWeight: 700,
          margin: '0 0 1rem 0',
          fontFamily: "'Inter', sans-serif",
          color: '#e8e6e3'
        }}>
          Join the marketplace
        </h2>
        <p style={{
          fontSize: '1rem',
          color: 'rgba(232,230,227,0.7)',
          fontFamily: "'Inter', sans-serif",
          margin: '0 0 2rem 0'
        }}>
          Free to create an account. No commitment required.
        </p>
        <button
          onClick={() => {
            const signupCard = document.getElementById('signup-card');
            if (signupCard) signupCard.scrollIntoView({ behavior: 'smooth' });
          }}
          style={{
            padding: '1rem 3rem',
            background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '1rem',
            fontWeight: 600,
            fontFamily: "'Inter', sans-serif",
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 12px rgba(168,85,247,0.3)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(168,85,247,0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(168,85,247,0.3)';
          }}
        >
          Get Started
        </button>
      </div>

      {/* Admin Access Section - Bottom of Page */}
      <div style={{
        maxWidth: '400px',
        margin: '0 auto 4rem',
        padding: '0 2rem'
      }}>
        <div style={{
          borderTop: '1px solid rgba(232,230,227,0.1)',
          paddingTop: '2rem',
          textAlign: 'center'
        }}>
          {!showAdminLogin ? (
            <button
              onClick={() => setShowAdminLogin(true)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'rgba(232,230,227,0.4)',
                fontSize: '0.8125rem',
                fontFamily: "'Inter', sans-serif",
                cursor: 'pointer',
                padding: '0.5rem 1rem'
              }}
            >
              🔒 Admin Access
            </button>
          ) : (
            <div style={{
              background: 'rgba(232,230,227,0.03)',
              border: '1px solid rgba(232,230,227,0.1)',
              borderRadius: '12px',
              padding: '1.5rem',
              textAlign: 'left'
            }}>
              <h4 style={{
                fontSize: '1rem',
                fontWeight: 600,
                margin: '0 0 1rem 0',
                fontFamily: "'Inter', sans-serif",
                color: '#e8e6e3',
                textAlign: 'center'
              }}>
                🔒 Admin Login
              </h4>
              
              <form onSubmit={handleAdminLogin}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    marginBottom: '0.375rem',
                    fontFamily: "'Inter', sans-serif",
                    color: 'rgba(232,230,227,0.8)'
                  }}>
                    Username
                  </label>
                  <input
                    type="text"
                    value={adminUsername}
                    onChange={(e) => setAdminUsername(e.target.value)}
                    placeholder="Enter username"
                    style={{
                      width: '100%',
                      padding: '0.625rem 0.875rem',
                      background: 'rgba(232,230,227,0.05)',
                      border: '1px solid rgba(232,230,227,0.2)',
                      borderRadius: '6px',
                      color: '#e8e6e3',
                      fontSize: '0.875rem',
                      fontFamily: "'Inter', sans-serif",
                      outline: 'none'
                    }}
                  />
                </div>
                
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    marginBottom: '0.375rem',
                    fontFamily: "'Inter', sans-serif",
                    color: 'rgba(232,230,227,0.8)'
                  }}>
                    Password
                  </label>
                  <input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    placeholder="Enter password"
                    style={{
                      width: '100%',
                      padding: '0.625rem 0.875rem',
                      background: 'rgba(232,230,227,0.05)',
                      border: '1px solid rgba(232,230,227,0.2)',
                      borderRadius: '6px',
                      color: '#e8e6e3',
                      fontSize: '0.875rem',
                      fontFamily: "'Inter', sans-serif",
                      outline: 'none'
                    }}
                  />
                </div>
                
                {adminLoginError && (
                  <div style={{
                    color: '#ef4444',
                    fontSize: '0.8125rem',
                    marginBottom: '1rem',
                    fontFamily: "'Inter', sans-serif",
                    textAlign: 'center'
                  }}>
                    {adminLoginError}
                  </div>
                )}
                
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    type="submit"
                    style={{
                      flex: 1,
                      padding: '0.625rem 1rem',
                      background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#fff',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      fontFamily: "'Inter', sans-serif",
                      cursor: 'pointer'
                    }}
                  >
                    Login
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAdminLogin(false);
                      setAdminUsername('');
                      setAdminPassword('');
                      setAdminLoginError('');
                    }}
                    style={{
                      padding: '0.625rem 1rem',
                      background: 'transparent',
                      border: '1px solid rgba(232,230,227,0.2)',
                      borderRadius: '6px',
                      color: 'rgba(232,230,227,0.7)',
                      fontSize: '0.875rem',
                      fontFamily: "'Inter', sans-serif",
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

function FormField({ label, type, value, onChange, required, placeholder, icon, error }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{
        display: 'block',
        fontSize: '0.8125rem',
        fontWeight: 600,
        marginBottom: '0.375rem',
        fontFamily: "'Inter', sans-serif",
        color: 'rgba(232,230,227,0.9)'
      }}>
        {label} {required && <span style={{ color: '#ec4899' }}>*</span>}
      </label>
      <div style={{ position: 'relative' }}>
        {icon && (
          <div style={{
            position: 'absolute',
            left: '0.875rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'rgba(232,230,227,0.4)',
            pointerEvents: 'none'
          }}>
            {icon}
          </div>
        )}
        <input
          type={type}
          value={value}
          onChange={onChange}
          required={required}
          placeholder={placeholder}
          style={{
            width: '100%',
            padding: icon ? '0.625rem 0.875rem 0.625rem 2.5rem' : '0.625rem 0.875rem',
            background: 'rgba(232,230,227,0.05)',
            border: error ? '1px solid #ef4444' : '1px solid rgba(232,230,227,0.2)',
            borderRadius: '6px',
            color: '#e8e6e3',
            fontSize: '0.875rem',
            fontFamily: "'Inter', sans-serif",
            outline: 'none',
            transition: 'all 0.2s ease'
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = error ? '#ef4444' : '#a855f7';
            e.currentTarget.style.background = 'rgba(232,230,227,0.08)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = error ? '#ef4444' : 'rgba(232,230,227,0.2)';
              e.currentTarget.style.background = 'rgba(232,230,227,0.05)';
            }}
          />
        </div>
        {error && (
          <div style={{
            marginTop: '0.5rem',
            fontSize: '0.875rem',
            color: '#ef4444',
            fontFamily: "'Inter', sans-serif"
          }}>
            {error}
          </div>
        )}
    </div>
  );
}

function FormFieldWithTooltip({ label, type, value, onChange, required, placeholder, icon, tooltip }) {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        fontSize: '0.8125rem',
        fontWeight: 600,
        marginBottom: '0.375rem',
        fontFamily: "'Inter', sans-serif",
        color: 'rgba(232,230,227,0.9)'
      }}>
        {label} {required && <span style={{ color: '#ec4899' }}>*</span>}
        <div 
          style={{ 
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center'
          }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <span style={{
            width: '14px',
            height: '14px',
            borderRadius: '50%',
              background: 'rgba(168,85,247,0.2)',
              border: '1px solid rgba(168,85,247,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.625rem',
              color: '#a855f7',
              fontWeight: 700,
              cursor: 'help'
            }}>
              ?
            </span>
            {showTooltip && (
              <div style={{
                position: 'absolute',
                left: '24px',
                top: '-8px',
                background: 'rgba(10,14,39,0.95)',
                border: '1px solid rgba(168,85,247,0.3)',
                borderRadius: '8px',
                padding: '0.75rem 1rem',
                fontSize: '0.8125rem',
                color: 'rgba(232,230,227,0.9)',
                fontWeight: 400,
                lineHeight: 1.5,
                whiteSpace: 'nowrap',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                backdropFilter: 'blur(10px)'
              }}>
                {tooltip}
              </div>
            )}
          </div>
        </label>
        <div style={{ position: 'relative' }}>
          {icon && (
            <div style={{
              position: 'absolute',
              left: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'rgba(232,230,227,0.4)',
              pointerEvents: 'none'
            }}>
              {icon}
            </div>
          )}
          <input
            type={type}
            value={value}
            onChange={onChange}
            required={required}
            placeholder={placeholder}
            style={{
              width: '100%',
              padding: icon ? '0.625rem 0.875rem 0.625rem 2.5rem' : '0.625rem 0.875rem',
              background: 'rgba(232,230,227,0.05)',
              border: '1px solid rgba(232,230,227,0.2)',
              borderRadius: '6px',
              color: '#e8e6e3',
              fontSize: '0.875rem',
              fontFamily: "'Inter', sans-serif",
              outline: 'none',
              transition: 'all 0.2s ease'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#a855f7';
              e.currentTarget.style.background = 'rgba(232,230,227,0.08)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'rgba(232,230,227,0.2)';
              e.currentTarget.style.background = 'rgba(232,230,227,0.05)';
            }}
          />
        </div>
    </div>
  );
}

// ============================================
// EARN TO REACH TRANSITION SCREEN
// ============================================

function EarnToReachTransitionScreen({ setCurrentScreen, setOnboardingType, setOnboardingStep, publisherProfile, advertiserProfile, setAdvertiserProfile }) {
  const handleContinue = () => {
    // Map publisher business model to advertiser business model
    const mapBusinessModel = (publisherModels) => {
      if (!publisherModels || publisherModels.length === 0) return '';
      const model = publisherModels[0];
      // Map similar terms
      if (model.toLowerCase().includes('dtc') || model.toLowerCase().includes('direct')) return 'Direct to Consumer';
      if (model.toLowerCase().includes('retail')) return 'Physical Retail';
      if (model.toLowerCase().includes('wholesale')) return 'Wholesale';
      if (model.toLowerCase().includes('service')) return 'Services';
      return '';
    };

    // Map publisher placement types to advertiser interested placements
    const mapPlacements = (publisherPlacements) => {
      if (!publisherPlacements || publisherPlacements.length === 0) return [];
      // Publisher placements like 'newsletter-sponsored', 'promotional-email' map to advertiser interests
      const mapped = [];
      publisherPlacements.forEach(p => {
        if (p.includes('email') || p.includes('newsletter')) mapped.push('Email/Newsletter');
        if (p.includes('social')) mapped.push('Social Media');
        if (p.includes('website') || p.includes('banner')) mapped.push('Website/Display');
        if (p.includes('backlink') || p.includes('seo')) mapped.push('SEO/Backlinks');
      });
      return [...new Set(mapped)]; // Remove duplicates
    };

    // Pre-populate advertiser profile with ALL shared data from publisher profile
    setAdvertiserProfile(prev => ({
      ...prev,
      // ===== STEP 1: Basic Context =====
      // Location data
      city: publisherProfile.city || prev.city,
      state: publisherProfile.state || prev.state,
      country: publisherProfile.country || prev.country,
      // Role data - map from publisher roles
      roleAtCompany: publisherProfile.rolesAtCompany?.[0] || prev.roleAtCompany,
      // How did you hear - already answered on Earn side
      howDidYouHear: publisherProfile.howDidYouHear || prev.howDidYouHear,
      howDidYouHearOther: publisherProfile.howDidYouHearOther || prev.howDidYouHearOther,
      // Business model - map from publisher
      primaryBusinessModel: mapBusinessModel(publisherProfile.primaryBusinessModels) || prev.primaryBusinessModel,
      // Customer locations - map from publisher regions
      customerLocations: publisherProfile.regions?.length > 0 ? publisherProfile.regions : prev.customerLocations,
      // Business category - use primary category from publisher
      businessCategory: publisherProfile.primaryCategory || (publisherProfile.primaryCategories?.[0]) || prev.businessCategory,
      
      // ===== STEP 3: Economics & Constraints =====
      // Average order value - same field exists on both sides
      averageOrderValue: publisherProfile.averageOrderValue || prev.averageOrderValue,
      
      // ===== STEP 4: Placement Preferences =====
      // Map publisher placement types to advertiser interests
      interestedPlacements: mapPlacements(publisherProfile.placementTypes) || prev.interestedPlacements,
      // Excluded categories - same field on both sides
      excludedCategories: publisherProfile.excludedCategories || prev.excludedCategories,
      
      // ===== STEP 5: Customer Affinities =====
      // Carry over affinity data from publisher side
      customerInterests: publisherProfile.customerInterests || prev.customerInterests,
      customerProducts: publisherProfile.customerProducts || prev.customerProducts,
      brandAttributes: publisherProfile.brandAttributes || prev.brandAttributes,
    }));
    
    setOnboardingType('advertiser');
    setOnboardingStep(1);
    setCurrentScreen('onboarding');
    // Scroll to top when transitioning
    window.scrollTo(0, 0);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #2a1f3a 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem'
    }}>
      <div style={{
        maxWidth: '600px',
        textAlign: 'center'
      }}>
        {/* Success checkmark */}
        <div style={{
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 2rem auto',
          fontSize: '3rem'
        }}>
          ✓
        </div>

        <h1 style={{
          fontSize: '2.5rem',
          fontWeight: 700,
          margin: '0 0 1rem 0',
          fontFamily: "'Crimson Pro', Georgia, serif",
          color: '#e8e6e3'
        }}>
          Earn Profile Complete! 🎉
        </h1>

        <p style={{
          fontSize: '1.25rem',
          color: 'rgba(232,230,227,0.8)',
          margin: '0 0 2rem 0',
          fontFamily: "'Inter', sans-serif",
          lineHeight: 1.6
        }}>
          Great job! You've set up your <strong style={{ color: '#10b981' }}>Earn</strong> profile. 
          Now let's set up your <strong style={{ color: '#a855f7' }}>Reach</strong> profile so you can 
          also advertise with other partners.
        </p>

        {/* Visual transition */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1.5rem',
          margin: '2rem 0 3rem 0'
        }}>
          <div style={{
            padding: '1.5rem 2rem',
            background: 'rgba(16,185,129,0.1)',
            border: '2px solid #10b981',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>💰</div>
            <div style={{ fontWeight: 700, color: '#10b981', fontFamily: "'Inter', sans-serif" }}>EARN</div>
            <div style={{ fontSize: '0.8125rem', color: 'rgba(232,230,227,0.6)', fontFamily: "'Inter', sans-serif" }}>Complete ✓</div>
          </div>

          <div style={{ fontSize: '2rem', color: 'rgba(232,230,227,0.4)' }}>→</div>

          <div style={{
            padding: '1.5rem 2rem',
            background: 'rgba(168,85,247,0.1)',
            border: '2px solid rgba(168,85,247,0.5)',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📢</div>
            <div style={{ fontWeight: 700, color: '#a855f7', fontFamily: "'Inter', sans-serif" }}>REACH</div>
            <div style={{ fontSize: '0.8125rem', color: 'rgba(232,230,227,0.6)', fontFamily: "'Inter', sans-serif" }}>Up Next</div>
          </div>
        </div>

        <p style={{
          fontSize: '1rem',
          color: 'rgba(232,230,227,0.6)',
          margin: '0 0 2rem 0',
          fontFamily: "'Inter', sans-serif"
        }}>
          This will only take a few more minutes.
        </p>

        <button
          onClick={handleContinue}
          style={{
            padding: '1rem 3rem',
            background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
            border: 'none',
            borderRadius: '12px',
            color: '#fff',
            fontSize: '1.125rem',
            fontWeight: 600,
            fontFamily: "'Inter', sans-serif",
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}
        >
          Continue to Reach Setup
          <span style={{ fontSize: '1.25rem' }}>→</span>
        </button>
      </div>
    </div>
  );
}

// ============================================
// ADVERTISER WAITLIST SCREEN
// ============================================

function AdvertiserWaitlistScreen({ advertiserWaitlist, setAdvertiserWaitlist, setCurrentScreen, accountType }) {
  const handleSubmit = () => {
    // Validate
    if (!advertiserWaitlist.monthlyBudget || !advertiserWaitlist.launchTimeline) {
      alert('Please complete all required fields');
      return;
    }
    
    // Save waitlist data to localStorage
    const storedUser = localStorage.getItem('mavstream_user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      user.advertiserWaitlist = advertiserWaitlist;
      user.advertiserWaitlistSubmitted = true;
      user.advertiserWaitlistDate = new Date().toISOString();
      localStorage.setItem('mavstream_user', JSON.stringify(user));
    }
    
    // Go to dashboard (for "both" users) or confirmation (for "advertiser" only users)
    if (accountType === 'both') {
      setCurrentScreen('dashboard');
    } else {
      setCurrentScreen('waitlistConfirmation');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #2a1f3a 100%)',
      fontFamily: "'Crimson Pro', 'Fraunces', Georgia, serif",
      color: '#e8e6e3',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background effects */}
      <div style={{
        position: 'fixed',
        top: '-50%',
        right: '-20%',
        width: '800px',
        height: '800px',
        background: 'radial-gradient(circle, rgba(236,72,153,0.15) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        pointerEvents: 'none'
      }} />
      <div style={{
        position: 'fixed',
        bottom: '-30%',
        left: '-10%',
        width: '600px',
        height: '600px',
        background: 'radial-gradient(circle, rgba(168,85,247,0.12) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        pointerEvents: 'none'
      }} />

      <div style={{
        width: '100%',
        maxWidth: '700px',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '3rem'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            margin: '0 auto 1.5rem',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(236,72,153,0.2) 0%, rgba(168,85,247,0.2) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid rgba(236,72,153,0.3)',
            fontSize: '2.5rem'
          }}>
            📢
          </div>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: 700,
            margin: '0 0 0.75rem 0',
            letterSpacing: '-0.02em'
          }}>
            Join the Advertiser Waitlist
          </h1>
          <p style={{
            fontSize: '1.125rem',
            color: 'rgba(232,230,227,0.7)',
            margin: 0,
            fontFamily: "'Inter', sans-serif"
          }}>
            {accountType === 'both' 
              ? "Your publisher account is ready! Get early access to our advertiser platform."
              : "Be among the first to advertise when we launch in 4 weeks"}
          </p>
        </div>

        {/* Waitlist Form */}
        <div style={{
          background: 'rgba(232,230,227,0.03)',
          border: '1px solid rgba(232,230,227,0.1)',
          borderRadius: '20px',
          padding: '3rem',
          backdropFilter: 'blur(20px)',
          marginBottom: '2rem'
        }}>
          <div style={{ display: 'grid', gap: '2rem' }}>
            {/* Monthly Budget */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: 600,
                marginBottom: '1rem',
                fontFamily: "'Inter', sans-serif",
                color: 'rgba(232,230,227,0.9)'
              }}>
                What's your monthly advertising budget? <span style={{ color: '#ec4899' }}>*</span>
              </label>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '0.75rem'
              }}>
                {['<$5k', '$5k-$10k', '$10k-$25k', '$25k-$50k', '$50k+'].map(option => (
                  <RadioButton
                    key={option}
                    selected={advertiserWaitlist.monthlyBudget === option}
                    onClick={() => setAdvertiserWaitlist({...advertiserWaitlist, monthlyBudget: option})}
                    label={option}
                  />
                ))}
              </div>
            </div>

            {/* What are you advertising */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: 600,
                marginBottom: '0.5rem',
                fontFamily: "'Inter', sans-serif",
                color: 'rgba(232,230,227,0.9)'
              }}>
                What are you looking to advertise?
              </label>
              <p style={{
                fontSize: '0.875rem',
                color: 'rgba(232,230,227,0.6)',
                margin: '0 0 1rem 0',
                fontFamily: "'Inter', sans-serif"
              }}>
                Brief description of your product or service
              </p>
              <input
                type="text"
                placeholder="e.g., Organic skincare products for women 25-45"
                value={advertiserWaitlist.advertisingGoal}
                onChange={(e) => setAdvertiserWaitlist({...advertiserWaitlist, advertisingGoal: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  background: 'rgba(232,230,227,0.05)',
                  border: '1px solid rgba(232,230,227,0.2)',
                  borderRadius: '8px',
                  color: '#e8e6e3',
                  fontSize: '1rem',
                  fontFamily: "'Inter', sans-serif",
                  outline: 'none'
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = '#ec4899'}
                onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(232,230,227,0.2)'}
              />
            </div>

            {/* Target Audience */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: 600,
                marginBottom: '1rem',
                fontFamily: "'Inter', sans-serif",
                color: 'rgba(232,230,227,0.9)'
              }}>
                Who's your target customer? (select all that apply)
              </label>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '0.75rem'
              }}>
                {[
                  'Female 25-45',
                  'Male 25-45',
                  'High income ($100k+)',
                  'Health & wellness focused',
                  'Parents',
                  'Other'
                ].map(option => {
                  const isSelected = advertiserWaitlist.targetAudience.includes(option);
                  return (
                    <CheckboxButton
                      key={option}
                      selected={isSelected}
                      onClick={() => {
                        if (isSelected) {
                          setAdvertiserWaitlist({
                            ...advertiserWaitlist,
                            targetAudience: advertiserWaitlist.targetAudience.filter(a => a !== option)
                          });
                        } else {
                          setAdvertiserWaitlist({
                            ...advertiserWaitlist,
                            targetAudience: [...advertiserWaitlist.targetAudience, option]
                          });
                        }
                      }}
                      label={option}
                    />
                  );
                })}
              </div>
            </div>

            {/* Launch Timeline */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: 600,
                marginBottom: '1rem',
                fontFamily: "'Inter', sans-serif",
                color: 'rgba(232,230,227,0.9)'
              }}>
                When do you want to start advertising? <span style={{ color: '#ec4899' }}>*</span>
              </label>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '0.75rem'
              }}>
                {[
                  'As soon as possible',
                  'Within 1-2 months',
                  'Within 3-6 months',
                  'Just exploring'
                ].map(option => (
                  <RadioButton
                    key={option}
                    selected={advertiserWaitlist.launchTimeline === option}
                    onClick={() => setAdvertiserWaitlist({...advertiserWaitlist, launchTimeline: option})}
                    label={option}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          style={{
            width: '100%',
            padding: '1.25rem',
            background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 100%)',
            border: 'none',
            borderRadius: '12px',
            color: '#fff',
            fontSize: '1.125rem',
            fontWeight: 600,
            fontFamily: "'Inter', sans-serif",
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 12px rgba(236,72,153,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(236,72,153,0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(236,72,153,0.3)';
          }}
        >
          {accountType === 'both' ? 'Complete Setup' : 'Get Early Access'}
          <ArrowRight size={20} />
        </button>

        {accountType === 'both' && (
          <p style={{
            textAlign: 'center',
            marginTop: '1.5rem',
            fontSize: '0.875rem',
            color: 'rgba(232,230,227,0.6)',
            fontFamily: "'Inter', sans-serif"
          }}>
            We'll notify you when the advertiser platform launches
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================
// ONBOARDING SCREEN COMPONENT
// ============================================

function OnboardingScreen({ step, onboardingType, publisherProfile, setPublisherProfile, advertiserProfile, setAdvertiserProfile, handleNext, handleBack }) {
  const totalSteps = onboardingType === 'publisher' ? 8 : 7;
  const progressPercentage = (step / totalSteps) * 100;
  const profileData = onboardingType === 'publisher' ? publisherProfile : advertiserProfile;
  const setProfileData = onboardingType === 'publisher' ? setPublisherProfile : setAdvertiserProfile;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #2a1f3a 100%)',
      fontFamily: "'Crimson Pro', 'Fraunces', Georgia, serif",
      color: '#e8e6e3',
      padding: '2rem',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background effects */}
      <div style={{
        position: 'fixed',
        top: '-50%',
        right: '-20%',
        width: '800px',
        height: '800px',
        background: 'radial-gradient(circle, rgba(168,85,247,0.15) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        pointerEvents: 'none'
      }} />

      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        position: 'relative'
      }}>
        {/* Progress Header */}
        <div style={{
          marginBottom: '3rem'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <h1 style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              margin: 0,
              letterSpacing: '-0.02em',
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              MavStream
            </h1>
            <div style={{
              fontSize: '0.875rem',
              color: 'rgba(232,230,227,0.6)',
              fontFamily: "'Inter', sans-serif",
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <span>Step {step} of {totalSteps}</span>
              <span style={{ color: 'rgba(232,230,227,0.4)' }}>•</span>
              <span style={{ color: '#a855f7', fontWeight: 500 }}>
                ~{Math.max(1, totalSteps - step + 1)} min remaining
              </span>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div style={{
            width: '100%',
            height: '6px',
            background: 'rgba(232,230,227,0.1)',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${progressPercentage}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #a855f7 0%, #ec4899 100%)',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        {/* Onboarding Content */}
        <div style={{
          background: 'rgba(232,230,227,0.03)',
          border: '1px solid rgba(232,230,227,0.1)',
          borderRadius: '20px',
          padding: '3rem',
          backdropFilter: 'blur(20px)',
          marginBottom: '2rem'
        }}>
          {onboardingType === 'publisher' ? (
            <>
              {step === 1 && <StepBusinessProfile profileData={profileData} setProfileData={setProfileData} />}
              {step === 2 && <StepPrimaryCategory profileData={profileData} setProfileData={setProfileData} />}
              {step === 3 && <StepEmailListSize profileData={profileData} setProfileData={setProfileData} />}
              {step === 4 && <StepGeography profileData={profileData} setProfileData={setProfileData} />}
              {step === 5 && <StepDemographics profileData={profileData} setProfileData={setProfileData} />}
              {step === 6 && <StepCustomerAffinities profileData={profileData} setProfileData={setProfileData} />}
              {step === 7 && <StepParticipation profileData={profileData} setProfileData={setProfileData} />}
              {step === 8 && <StepConsent profileData={profileData} setProfileData={setProfileData} />}
            </>
          ) : (
            <>
              {step === 1 && <StepAdvertiserBusinessProfile profileData={profileData} setProfileData={setProfileData} />}
              {step === 2 && <StepAdvertiserEconomics profileData={profileData} setProfileData={setProfileData} />}
              {step === 3 && <StepAdvertiserPlacements profileData={profileData} setProfileData={setProfileData} />}
              {step === 4 && <StepAdvertiserCustomerAffinities profileData={profileData} setProfileData={setProfileData} />}
              {step === 5 && <StepAdvertiserBudgetTiming profileData={profileData} setProfileData={setProfileData} />}
              {step === 6 && <StepAdvertiserCreative profileData={profileData} setProfileData={setProfileData} />}
              {step === 7 && <StepAdvertiserConsent profileData={profileData} setProfileData={setProfileData} />}
            </>
          )}
        </div>

        {/* Navigation */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '1rem'
        }}>
          {step > 1 ? (
            <button
              onClick={handleBack}
              style={{
                padding: '1rem 2rem',
                background: 'rgba(232,230,227,0.1)',
                border: '1px solid rgba(232,230,227,0.2)',
                borderRadius: '10px',
                color: '#e8e6e3',
                fontSize: '1rem',
                fontWeight: 600,
                fontFamily: "'Inter', sans-serif",
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(232,230,227,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(232,230,227,0.1)';
              }}
            >
              Back
            </button>
          ) : <div />}
          
          <button
            onClick={handleNext}
            style={{
              padding: '1rem 2rem',
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              border: 'none',
              borderRadius: '10px',
              color: '#fff',
              fontSize: '1rem',
              fontWeight: 600,
              fontFamily: "'Inter', sans-serif",
              cursor: 'pointer',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              boxShadow: '0 4px 12px rgba(168,85,247,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(168,85,247,0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(168,85,247,0.3)';
            }}
          >
            {step === totalSteps ? 'Complete Setup' : 'Continue'}
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

// Onboarding Step Components
function StepBusinessProfile({ profileData, setProfileData }) {
  const roleOptions = ['Owner', 'Marketing', 'Operations', 'Other'];
  const businessModelOptions = ['Direct to Consumer', 'Physical Retail', 'Wholesale', 'Services', 'Other'];

  return (
    <div>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.01em',
        fontFamily: "'Inter', sans-serif"
      }}>
        Why We Ask & What We Need
      </h2>
      <p style={{
        fontSize: '1rem',
        color: 'rgba(232,230,227,0.6)',
        margin: '0 0 2.5rem 0',
        fontFamily: "'Inter', sans-serif",
        lineHeight: 1.6
      }}>
        We use this info to match you with the right partners and ensure quality collaborations. This takes about 3 minutes.
      </p>

      <div style={{ display: 'grid', gap: '2rem' }}>
        {/* Company Description */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '0.75rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Describe your company in one sentence <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <input
            type="text"
            placeholder="e.g., We sell organic pet food directly to consumers through our website and subscription service."
            value={profileData.companyDescription}
            onChange={(e) => setProfileData({...profileData, companyDescription: e.target.value})}
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              background: 'rgba(232,230,227,0.05)',
              border: '1px solid rgba(232,230,227,0.2)',
              borderRadius: '8px',
              color: '#e8e6e3',
              fontSize: '1rem',
              fontFamily: "'Inter', sans-serif",
              outline: 'none'
            }}
          />
        </div>

        {/* Primary Business Model - Dropdown */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '0.75rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            What best describes your business? <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <select
            value={profileData.primaryBusinessModels?.[0] || ''}
            onChange={(e) => setProfileData({...profileData, primaryBusinessModels: e.target.value ? [e.target.value] : []})}
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              background: 'rgba(232,230,227,0.05)',
              border: '1px solid rgba(232,230,227,0.2)',
              borderRadius: '8px',
              color: '#e8e6e3',
              fontSize: '1rem',
              fontFamily: "'Inter', sans-serif",
              outline: 'none',
              cursor: 'pointer'
            }}
          >
            <option value="">Select your business type...</option>
            {businessModelOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>

        {/* Role at Company */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Your role at the company <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '0.75rem'
          }}>
            {roleOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.rolesAtCompany?.[0] === option}
                onClick={() => setProfileData({...profileData, rolesAtCompany: [option]})}
                label={option}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepPrimaryCategory({ profileData, setProfileData }) {
  const categoryOptions = [
    'Beauty / Skincare',
    'Wellness / Supplements',
    'Personal Care',
    'Food / Beverage',
    'CPG / Consumables',
    'Apparel / Lifestyle',
    'Home / Garden',
    'Pets',
    'Baby / Kids',
    'Fitness / Sports',
    'Technology / Electronics',
    'Travel / Hospitality',
    'Finance / Professional Services',
    'Education / Learning',
    'Entertainment / Media',
    'Other'
  ];

  // Initialize categories array if not exists
  const selectedCategories = profileData.primaryCategories || (profileData.primaryCategory ? [profileData.primaryCategory] : []);

  const toggleCategory = (category) => {
    let newCategories;
    if (selectedCategories.includes(category)) {
      newCategories = selectedCategories.filter(c => c !== category);
    } else {
      newCategories = [...selectedCategories, category];
    }
    setProfileData({
      ...profileData, 
      primaryCategories: newCategories,
      primaryCategory: newCategories[0] || '' // Keep first one as primary for backwards compatibility
    });
  };

  return (
    <div>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.01em',
        fontFamily: "'Inter', sans-serif"
      }}>
        Tell us about your business
      </h2>
      <p style={{
        fontSize: '1rem',
        color: 'rgba(232,230,227,0.6)',
        margin: '0 0 2.5rem 0',
        fontFamily: "'Inter', sans-serif"
      }}>
        Help us understand who your primary customers are
      </p>

      <div>
        <label style={{
          display: 'block',
          fontSize: '1rem',
          fontWeight: 600,
          marginBottom: '0.5rem',
          fontFamily: "'Inter', sans-serif",
          color: 'rgba(232,230,227,0.9)'
        }}>
          What categories describe your customers? <span style={{ color: '#ec4899' }}>*</span>
        </label>
        <p style={{
          fontSize: '0.875rem',
          color: 'rgba(232,230,227,0.5)',
          margin: '0 0 1rem 0',
          fontFamily: "'Inter', sans-serif"
        }}>
          Select all that apply
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '0.75rem'
        }}>
          {categoryOptions.map(option => (
            <CheckboxButton
              key={option}
              selected={selectedCategories.includes(option)}
              onClick={() => toggleCategory(option)}
              label={option}
            />
          ))}
        </div>
        {selectedCategories.length > 0 && (
          <p style={{
            fontSize: '0.875rem',
            color: 'rgba(168,85,247,0.8)',
            margin: '1rem 0 0 0',
            fontFamily: "'Inter', sans-serif"
          }}>
            ✓ {selectedCategories.length} categor{selectedCategories.length === 1 ? 'y' : 'ies'} selected
          </p>
        )}
      </div>
    </div>
  );
}

function StepEmailListSize({ profileData, setProfileData }) {
  const listSizeOptions = ['<25k', '25k–50k', '50k–100k', '100k–250k', '250k–500k', '500k+'];
  const engagementPercentOptions = ['Under 10%', '10-20%', '20-30%', '30-40%', '40-50%', '50%+'];
  const frequencyOptions = ['Daily', 'A few times a week', 'Weekly', 'Bi-weekly', 'Monthly', 'Campaign-only (promos)'];

  return (
    <div>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.01em',
        fontFamily: "'Inter', sans-serif"
      }}>
        Audience Size & Engagement
      </h2>
      <p style={{
        fontSize: '1rem',
        color: 'rgba(232,230,227,0.6)',
        margin: '0 0 2.5rem 0',
        fontFamily: "'Inter', sans-serif"
      }}>
        Help us understand your email and social media audience
      </p>

      <div style={{ display: 'grid', gap: '2rem' }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Total email subscribers <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '0.75rem'
          }}>
            {listSizeOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.listSize === option}
                onClick={() => setProfileData({...profileData, listSize: option})}
                label={option}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Estimated engaged subscribers (% who opened or clicked in last 90 days) <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '0.75rem'
          }}>
            {engagementPercentOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.engagedSubscribers === option}
                onClick={() => setProfileData({...profileData, engagedSubscribers: option})}
                label={option}
              />
            ))}
          </div>
        </div>

        {/* Social Media Followers */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Total social media followers across all platforms <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '0.75rem'
          }}>
            {['<10k', '10k–25k', '25k–50k', '50k–100k', '100k–250k', '250k–500k', '500k+'].map(option => (
              <RadioButton
                key={option}
                selected={profileData.socialFollowers === option}
                onClick={() => setProfileData({...profileData, socialFollowers: option})}
                label={option}
              />
            ))}
          </div>
          <p style={{
            fontSize: '0.875rem',
            color: 'rgba(232,230,227,0.5)',
            margin: '0.5rem 0 0 0',
            fontFamily: "'Inter', sans-serif"
          }}>
            Include Instagram, TikTok, Facebook, Twitter/X, LinkedIn, YouTube, etc.
          </p>
        </div>

        {/* Website Visitors Question */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            How many visitors do you have to your site per month? <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '0.75rem'
          }}>
            {['0-1,000', '1,000-5,000', '5,000-10,000', '10,000-50,000', '50,000-100,000', '100,000+'].map(option => (
              <RadioButton
                key={option}
                selected={profileData.websiteVisitors === option}
                onClick={() => setProfileData({...profileData, websiteVisitors: option})}
                label={option}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            How often are you emailing your customers? <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '0.75rem'
          }}>
            {frequencyOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.campaignFrequency === option}
                onClick={() => setProfileData({...profileData, campaignFrequency: option})}
                label={option}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepGeography({ profileData, setProfileData }) {
  const usRegionOptions = ['Northeast', 'Mid-Atlantic', 'Southeast', 'Midwest', 'Southwest', 'West Coast', 'Mountain West', 'Pacific Northwest'];
  const internationalRegionOptions = ['Canada', 'UK', 'Europe', 'Australia/NZ', 'Asia', 'Latin America', 'Other'];

  const toggleRegion = (region) => {
    const currentRegions = profileData.regions || [];
    if (currentRegions.includes(region)) {
      setProfileData({
        ...profileData,
        regions: currentRegions.filter(r => r !== region)
      });
    } else {
      setProfileData({
        ...profileData,
        regions: [...currentRegions, region]
      });
    }
  };

  // Determine audience type from regions
  const hasUSRegions = (profileData.regions || []).some(r => usRegionOptions.includes(r));
  const hasInternationalRegions = (profileData.regions || []).some(r => internationalRegionOptions.includes(r));
  
  // Track primary audience type
  const audienceType = profileData.audienceType || '';

  return (
    <div>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.01em',
        fontFamily: "'Inter', sans-serif"
      }}>
        Audience Geography
      </h2>
      <p style={{
        fontSize: '1rem',
        color: 'rgba(232,230,227,0.6)',
        margin: '0 0 2.5rem 0',
        fontFamily: "'Inter', sans-serif"
      }}>
        Where are your customers located?
      </p>

      <div style={{ display: 'grid', gap: '2rem' }}>
        {/* Primary Audience Type */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Where is your primary audience? <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '0.75rem'
          }}>
            <CheckboxButton
              selected={audienceType === 'US Only' || audienceType === 'Both'}
              onClick={() => {
                if (audienceType === 'US Only') {
                  setProfileData({...profileData, audienceType: '', regions: []});
                } else if (audienceType === 'International Only') {
                  setProfileData({...profileData, audienceType: 'Both'});
                } else if (audienceType === 'Both') {
                  setProfileData({...profileData, audienceType: 'International Only', regions: (profileData.regions || []).filter(r => internationalRegionOptions.includes(r))});
                } else {
                  setProfileData({...profileData, audienceType: 'US Only'});
                }
              }}
              label="United States"
            />
            <CheckboxButton
              selected={audienceType === 'International Only' || audienceType === 'Both'}
              onClick={() => {
                if (audienceType === 'International Only') {
                  setProfileData({...profileData, audienceType: '', regions: []});
                } else if (audienceType === 'US Only') {
                  setProfileData({...profileData, audienceType: 'Both'});
                } else if (audienceType === 'Both') {
                  setProfileData({...profileData, audienceType: 'US Only', regions: (profileData.regions || []).filter(r => usRegionOptions.includes(r))});
                } else {
                  setProfileData({...profileData, audienceType: 'International Only'});
                }
              }}
              label="International"
            />
          </div>
        </div>

        {/* US Regions - show if US selected */}
        {(audienceType === 'US Only' || audienceType === 'Both') && (
          <div>
            <label style={{
              display: 'block',
              fontSize: '1rem',
              fontWeight: 600,
              marginBottom: '1rem',
              fontFamily: "'Inter', sans-serif",
              color: 'rgba(232,230,227,0.9)'
            }}>
              Which US regions? (select all that apply)
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '0.75rem'
            }}>
              {usRegionOptions.map(option => (
                <CheckboxButton
                  key={option}
                  selected={(profileData.regions || []).includes(option)}
                  onClick={() => toggleRegion(option)}
                  label={option}
                />
              ))}
            </div>
          </div>
        )}

        {/* International Regions - show if International selected */}
        {(audienceType === 'International Only' || audienceType === 'Both') && (
          <div>
            <label style={{
              display: 'block',
              fontSize: '1rem',
              fontWeight: 600,
              marginBottom: '1rem',
              fontFamily: "'Inter', sans-serif",
              color: 'rgba(232,230,227,0.9)'
            }}>
              Which international regions? (select all that apply)
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '0.75rem'
            }}>
              {internationalRegionOptions.map(option => (
                <CheckboxButton
                  key={option}
                  selected={(profileData.regions || []).includes(option)}
                  onClick={() => toggleRegion(option)}
                  label={option}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StepDemographics({ profileData, setProfileData }) {
  const genderOptions = ['Mostly female', 'Mostly male', 'Roughly even'];
  const ageOptions = ['18–24', '25–34', '35–44', '45–54', '55+'];
  const incomeOptions = ['Lower Income', 'Middle Income', 'Higher Income'];
  const revenueOptions = ['<$10k', '$10k–$50k', '$50k–$100k', '$100k–$250k', '$250k–$500k', '$500k–$1M', '$1M+'];
  const aovOptions = ['Under $25', '$25-$50', '$50-$100', '$100-$200', '$200+'];

  const toggleAgeRange = (age) => {
    if (profileData.ageRanges.includes(age)) {
      setProfileData({
        ...profileData,
        ageRanges: profileData.ageRanges.filter(a => a !== age)
      });
    } else {
      setProfileData({
        ...profileData,
        ageRanges: [...profileData.ageRanges, age]
      });
    }
  };

  const toggleIncome = (income) => {
    if (profileData.householdIncomes.includes(income)) {
      setProfileData({
        ...profileData,
        householdIncomes: profileData.householdIncomes.filter(i => i !== income)
      });
    } else {
      setProfileData({
        ...profileData,
        householdIncomes: [...profileData.householdIncomes, income]
      });
    }
  };

  return (
    <div>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.01em',
        fontFamily: "'Inter', sans-serif"
      }}>
        Audience Demographics
      </h2>
      <p style={{
        fontSize: '1rem',
        color: 'rgba(232,230,227,0.6)',
        margin: '0 0 2.5rem 0',
        fontFamily: "'Inter', sans-serif"
      }}>
        High-level demographic information helps us match you with relevant brands
      </p>

      <div style={{ display: 'grid', gap: '2rem' }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Primary gender mix <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '0.75rem'
          }}>
            {genderOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.genderMix === option}
                onClick={() => setProfileData({...profileData, genderMix: option})}
                label={option}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Primary age range (select all that apply) <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '0.75rem'
          }}>
            {ageOptions.map(option => (
              <CheckboxButton
                key={option}
                selected={profileData.ageRanges.includes(option)}
                onClick={() => toggleAgeRange(option)}
                label={option}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Where do you feel the majority of your customers household income sits? (select all that apply) <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '0.75rem'
          }}>
            {incomeOptions.map(option => (
              <CheckboxButton
                key={option}
                selected={profileData.householdIncomes.includes(option)}
                onClick={() => toggleIncome(option)}
                label={option}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Monthly revenue <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '0.75rem'
          }}>
            {revenueOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.monthlyRevenue === option}
                onClick={() => setProfileData({...profileData, monthlyRevenue: option})}
                label={option}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '0.5rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Approximately, what is your Average Order Value?
          </label>
          <p style={{
            fontSize: '0.875rem',
            color: 'rgba(232,230,227,0.6)',
            margin: '0 0 1rem 0',
            fontFamily: "'Inter', sans-serif"
          }}>
            Optional - helps us understand your customer value
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '0.75rem'
          }}>
            {aovOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.averageOrderValue === option}
                onClick={() => setProfileData({...profileData, averageOrderValue: option})}
                label={option}
              />
            ))}
          </div>
        </div>

        {/* DR Score / Backlinking Section */}
        <div style={{
          background: 'rgba(168,85,247,0.05)',
          border: '1px solid rgba(168,85,247,0.2)',
          borderRadius: '12px',
          padding: '1.5rem',
          marginTop: '1rem'
        }}>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '0.75rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Domain Rating (DR) Score <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <p style={{
            fontSize: '0.9375rem',
            color: 'rgba(232,230,227,0.7)',
            margin: '0 0 1rem 0',
            fontFamily: "'Inter', sans-serif",
            lineHeight: '1.6'
          }}>
            You might be able to get paid for adding backlinks to your website which help companies get discovered via SEO and AEO (AI for SEO). This will depend on your DR score. To get your DR score, go to{' '}
            <a 
              href="https://ahrefs.com/website-authority-checker" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#a855f7', textDecoration: 'underline' }}
            >
              ahrefs.com/website-authority-checker
            </a>
            {' '}and enter your domain.
          </p>
          
          {/* DR Score explanation box */}
          <div style={{
            background: 'rgba(59,130,246,0.1)',
            border: '1px solid rgba(59,130,246,0.3)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem'
          }}>
            <p style={{
              fontSize: '0.875rem',
              color: 'rgba(232,230,227,0.9)',
              margin: 0,
              fontFamily: "'Inter', sans-serif",
              lineHeight: '1.6'
            }}>
              <strong style={{ color: '#3b82f6' }}>💡 What does DR Score mean?</strong><br />
              The higher your score, the more valuable your site is for other websites to link to. A backlink from a high DR site helps improve their search rankings.
            </p>
            <ul style={{
              margin: '0.75rem 0 0 0',
              paddingLeft: '1.25rem',
              fontSize: '0.875rem',
              color: 'rgba(232,230,227,0.8)',
              fontFamily: "'Inter', sans-serif",
              lineHeight: '1.6'
            }}>
              <li><strong>90+</strong> = Very strong (major publications, top sites)</li>
              <li><strong>60-89</strong> = Strong (established brands, popular blogs)</li>
              <li><strong>30-59</strong> = Moderate (growing sites with good authority)</li>
              <li><strong>Below 30</strong> = Building (newer or smaller sites)</li>
            </ul>
            <p style={{
              fontSize: '0.875rem',
              color: 'rgba(16,185,129,0.9)',
              margin: '0.75rem 0 0 0',
              fontFamily: "'Inter', sans-serif",
              fontStyle: 'italic'
            }}>
              💡 Even if your DR score is lower, many companies may still want to work with you and be part of your online traffic, blog, or newsletter audience!
            </p>
          </div>
          
          <label style={{
            display: 'block',
            fontSize: '0.9375rem',
            fontWeight: 600,
            marginBottom: '0.75rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.8)'
          }}>
            What is your DR Score? <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={profileData.drScore === 'N/A' ? '' : profileData.drScore}
              onChange={(e) => setProfileData({...profileData, drScore: e.target.value})}
              placeholder="e.g., 45"
              disabled={profileData.drScore === 'N/A'}
              style={{
                width: '120px',
                padding: '0.75rem 1rem',
                background: profileData.drScore === 'N/A' ? 'rgba(232,230,227,0.02)' : 'rgba(232,230,227,0.05)',
                border: '1px solid rgba(232,230,227,0.2)',
                borderRadius: '8px',
                color: profileData.drScore === 'N/A' ? 'rgba(232,230,227,0.4)' : '#e8e6e3',
                fontSize: '1rem',
                fontFamily: "'Inter', sans-serif",
                outline: 'none'
              }}
            />
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer',
              fontFamily: "'Inter', sans-serif",
              fontSize: '0.9375rem',
              color: 'rgba(232,230,227,0.8)'
            }}>
              <input
                type="checkbox"
                checked={profileData.drScore === 'N/A'}
                onChange={(e) => setProfileData({...profileData, drScore: e.target.checked ? 'N/A' : ''})}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer',
                  accentColor: '#a855f7'
                }}
              />
              N/A - I don't want to participate in backlinking
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 6: Brand Attributes (Simplified)
function StepCustomerAffinities({ profileData, setProfileData }) {
  const brandAttributeOptions = [
    'Premium',
    'Value-focused',
    'Luxury',
    'Eco-friendly',
    'Science-driven',
    'Community-oriented',
    'Performance-focused',
    'Natural/organic',
    'Clinical/medical',
    'Trend-forward'
  ];

  const toggleAttribute = (attribute) => {
    const current = profileData.brandAttributes || [];
    if (current.includes(attribute)) {
      setProfileData({...profileData, brandAttributes: current.filter(a => a !== attribute)});
    } else {
      setProfileData({...profileData, brandAttributes: [...current, attribute]});
    }
  };

  return (
    <div>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.01em',
        fontFamily: "'Inter', sans-serif"
      }}>
        Brand Attributes
      </h2>
      <p style={{
        fontSize: '1rem',
        color: 'rgba(232,230,227,0.6)',
        margin: '0 0 2.5rem 0',
        fontFamily: "'Inter', sans-serif"
      }}>
        Help us match you with complementary partners
      </p>

      <div>
        <label style={{
          display: 'block',
          fontSize: '1rem',
          fontWeight: 600,
          marginBottom: '0.5rem',
          fontFamily: "'Inter', sans-serif",
          color: 'rgba(232,230,227,0.9)'
        }}>
          Which brand attributes best describe you? <span style={{ color: '#ec4899' }}>*</span>
        </label>
        <p style={{
          fontSize: '0.875rem',
          color: 'rgba(232,230,227,0.5)',
          margin: '0 0 1rem 0',
          fontFamily: "'Inter', sans-serif"
        }}>
          Select all that apply
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '0.75rem'
        }}>
          {brandAttributeOptions.map(option => (
            <CheckboxButton
              key={option}
              selected={(profileData.brandAttributes || []).includes(option)}
              onClick={() => toggleAttribute(option)}
              label={option}
            />
          ))}
        </div>
        {(profileData.brandAttributes || []).length > 0 && (
          <p style={{
            fontSize: '0.875rem',
            color: 'rgba(168,85,247,0.8)',
            margin: '1rem 0 0 0',
            fontFamily: "'Inter', sans-serif"
          }}>
            ✓ {(profileData.brandAttributes || []).length} attribute{(profileData.brandAttributes || []).length === 1 ? '' : 's'} selected
          </p>
        )}
      </div>
    </div>
  );
}

function StepParticipation({ profileData, setProfileData }) {
  const placementOptions = [
    { 
      value: 'email', 
      label: 'Email', 
      description: 'Sponsored content in newsletters, promotional emails, or transactional messages'
    },
    { 
      value: 'website', 
      label: 'Website', 
      description: 'Sponsored placements on your homepage, content pages, or blog'
    },
    { 
      value: 'social', 
      label: 'Social', 
      description: 'Sponsored stories, feed posts, or highlighted mentions on your social channels'
    },
    { 
      value: 'backlink', 
      label: 'Backlink', 
      description: 'Sponsored resource links or "recommended partner" placements on your site'
    },
    { 
      value: 'packaging', 
      label: 'Packaging / Order Inserts', 
      description: 'Physical inserts or offers included in customer shipments'
    }
  ];

  const togglePlacement = (value) => {
    const current = profileData.placementTypes || [];
    if (current.includes(value)) {
      setProfileData({
        ...profileData,
        placementTypes: current.filter(t => t !== value)
      });
    } else {
      setProfileData({
        ...profileData,
        placementTypes: [...current, value]
      });
    }
  };

  return (
    <div>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.01em',
        fontFamily: "'Inter', sans-serif"
      }}>
        How you'd like to earn
      </h2>
      <p style={{
        fontSize: '1rem',
        color: 'rgba(232,230,227,0.6)',
        margin: '0 0 2.5rem 0',
        fontFamily: "'Inter', sans-serif"
      }}>
        Select where partner brands can appear. You're always in control of what runs.
      </p>

      <div style={{ display: 'grid', gap: '1rem' }}>
        {placementOptions.map(option => {
          const isSelected = (profileData.placementTypes || []).includes(option.value);
          return (
            <div
              key={option.value}
              onClick={() => togglePlacement(option.value)}
              style={{
                padding: '1.25rem 1.5rem',
                background: isSelected ? 'rgba(168,85,247,0.1)' : 'rgba(232,230,227,0.03)',
                border: isSelected ? '2px solid #a855f7' : '1px solid rgba(232,230,227,0.15)',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                gap: '1rem',
                alignItems: 'flex-start'
              }}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = 'rgba(168,85,247,0.3)';
                  e.currentTarget.style.background = 'rgba(232,230,227,0.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = 'rgba(232,230,227,0.15)';
                  e.currentTarget.style.background = 'rgba(232,230,227,0.03)';
                }
              }}
            >
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '6px',
                border: isSelected ? '2px solid #a855f7' : '2px solid rgba(232,230,227,0.3)',
                background: isSelected ? '#a855f7' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginTop: '2px'
              }}>
                {isSelected && (
                  <svg width="14" height="12" viewBox="0 0 12 10" fill="none">
                    <path d="M1 5L4.5 8.5L11 1.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  color: '#e8e6e3',
                  fontFamily: "'Inter', sans-serif",
                  marginBottom: '0.25rem'
                }}>
                  {option.label}
                </div>
                <div style={{
                  fontSize: '0.9375rem',
                  color: 'rgba(232,230,227,0.6)',
                  fontFamily: "'Inter', sans-serif",
                  lineHeight: 1.5
                }}>
                  {option.description}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {(profileData.placementTypes || []).length > 0 && (
        <p style={{
          fontSize: '0.9375rem',
          color: 'rgba(168,85,247,0.8)',
          margin: '1.5rem 0 0 0',
          fontFamily: "'Inter', sans-serif"
        }}>
          ✓ {(profileData.placementTypes || []).length} placement type{(profileData.placementTypes || []).length === 1 ? '' : 's'} selected
        </p>
      )}
    </div>
  );
}

function StepConsent({ profileData, setProfileData }) {
  return (
    <div>
      <div style={{
        textAlign: 'center',
        marginBottom: '3rem'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          margin: '0 auto 1.5rem',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(168,85,247,0.2) 0%, rgba(236,72,153,0.2) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid rgba(168,85,247,0.3)'
        }}>
          <Sparkles size={40} color="#a855f7" />
        </div>
        <h2 style={{
          fontSize: '2rem',
          fontWeight: 700,
          margin: '0 0 0.5rem 0',
          letterSpacing: '-0.01em',
          fontFamily: "'Inter', sans-serif"
        }}>
          You're almost there!
        </h2>
        <p style={{
          fontSize: '1rem',
          color: 'rgba(232,230,227,0.6)',
          margin: 0,
          fontFamily: "'Inter', sans-serif"
        }}>
          Just one more thing before we get started
        </p>
      </div>

      <div style={{
        background: 'rgba(168,85,247,0.05)',
        border: '1px solid rgba(168,85,247,0.2)',
        borderRadius: '12px',
        padding: '2rem',
        marginBottom: '2rem'
      }}>
        <h3 style={{
          fontSize: '1.25rem',
          fontWeight: 700,
          margin: '0 0 1rem 0',
          fontFamily: "'Inter', sans-serif",
          color: 'rgba(232,230,227,0.9)'
        }}>
          Consent & Expectations
        </h3>
        <p style={{
          fontSize: '1rem',
          lineHeight: 1.7,
          color: 'rgba(232,230,227,0.7)',
          fontFamily: "'Inter', sans-serif",
          marginBottom: '1.5rem'
        }}>
          By joining MavStream, you understand that:
        </p>
        <ul style={{
          fontSize: '1rem',
          lineHeight: 1.8,
          color: 'rgba(232,230,227,0.7)',
          fontFamily: "'Inter', sans-serif",
          paddingLeft: '1.5rem',
          marginBottom: 0
        }}>
          <li>This is a private beta program</li>
          <li>All ad placements are completely optional</li>
          <li>You maintain full control over your email content</li>
          <li>All promotions are brand-safe and subject to your final approval</li>
          <li>You can pause or stop participation at any time</li>
          <li>We respect your audience and will never compromise your brand trust</li>
        </ul>
      </div>

      <label style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '1rem',
        padding: '1.5rem',
        background: 'rgba(232,230,227,0.03)',
        border: `2px solid ${profileData.consentGiven ? '#a855f7' : 'rgba(232,230,227,0.1)'}`,
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        fontFamily: "'Inter', sans-serif"
      }}
      onMouseEnter={(e) => {
        if (!profileData.consentGiven) {
          e.currentTarget.style.borderColor = 'rgba(168,85,247,0.3)';
        }
      }}
      onMouseLeave={(e) => {
        if (!profileData.consentGiven) {
          e.currentTarget.style.borderColor = 'rgba(232,230,227,0.1)';
        }
      }}>
        <input
          type="checkbox"
          checked={profileData.consentGiven}
          onChange={(e) => setProfileData({...profileData, consentGiven: e.target.checked})}
          style={{
            width: '20px',
            height: '20px',
            marginTop: '2px',
            cursor: 'pointer',
            accentColor: '#a855f7'
          }}
        />
        <div>
          <div style={{
            fontSize: '1rem',
            fontWeight: 600,
            color: 'rgba(232,230,227,0.9)',
            marginBottom: '0.25rem'
          }}>
            I understand and agree to these terms
          </div>
          <div style={{
            fontSize: '0.875rem',
            color: 'rgba(232,230,227,0.6)'
          }}>
            I confirm that I have the authority to represent this brand and make decisions about email content
          </div>
        </div>
      </label>

      {!profileData.consentGiven && (
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          background: 'rgba(239,68,68,0.1)',
          border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: '8px',
          fontSize: '0.875rem',
          color: 'rgba(239,68,68,0.8)',
          fontFamily: "'Inter', sans-serif"
        }}>
          Please check the box above to complete your registration
        </div>
      )}
    </div>
  );
}

// ============================================
// ADVERTISER ONBOARDING STEPS
// ============================================

// Step 1: Business Profile (Same as Publisher)
function StepAdvertiserBusinessProfile({ profileData, setProfileData }) {
  const roleOptions = ['Owner', 'Marketing', 'Operations', 'Other'];
  const businessModelOptions = ['Direct to Consumer', 'Physical Retail', 'Wholesale', 'Services', 'Other'];

  return (
    <div>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.01em',
        fontFamily: "'Inter', sans-serif"
      }}>
        Why We Ask & What We Need
      </h2>
      <p style={{
        fontSize: '1rem',
        color: 'rgba(232,230,227,0.6)',
        margin: '0 0 2.5rem 0',
        fontFamily: "'Inter', sans-serif",
        lineHeight: 1.6
      }}>
        We use this info to match you with the right partners. This takes about 2 minutes.
      </p>

      <div style={{ display: 'grid', gap: '2rem' }}>
        {/* Primary Business Model - Dropdown */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '0.75rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            What best describes your business? <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <select
            value={profileData.primaryBusinessModel || ''}
            onChange={(e) => setProfileData({...profileData, primaryBusinessModel: e.target.value})}
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              background: 'rgba(232,230,227,0.05)',
              border: '1px solid rgba(232,230,227,0.2)',
              borderRadius: '8px',
              color: '#e8e6e3',
              fontSize: '1rem',
              fontFamily: "'Inter', sans-serif",
              outline: 'none',
              cursor: 'pointer'
            }}
          >
            <option value="">Select your business type...</option>
            {businessModelOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>

        {/* Role at Company */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Your role at the company <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '0.75rem'
          }}>
            {roleOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.roleAtCompany === option}
                onClick={() => setProfileData({...profileData, roleAtCompany: option})}
                label={option}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 2: Advertising Goals & Budget
function StepAdvertisingGoals({ profileData, setProfileData }) {
  const goalOptions = [
    'Brand awareness',
    'Lead generation',
    'Direct sales/conversions',
    'Customer acquisition',
    'Retargeting/retention'
  ];
  
  const budgetOptions = ['<$5k', '$5k-$10k', '$10k-$25k', '$25k-$50k', '$50k-$100k', '$100k+'];
  
  const experienceOptions = [
    'Yes, extensively',
    'Yes, a few times',
    'No, but I\'ve done other digital advertising',
    'No, this is my first time'
  ];

  return (
    <div>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.01em',
        fontFamily: "'Inter', sans-serif"
      }}>
        Advertising Goals & Budget
      </h2>
      <p style={{
        fontSize: '1rem',
        color: 'rgba(232,230,227,0.6)',
        margin: '0 0 2.5rem 0',
        fontFamily: "'Inter', sans-serif"
      }}>
        Help us understand your advertising objectives
      </p>

      <div style={{ display: 'grid', gap: '2rem' }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            What's your primary advertising goal? <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '0.75rem'
          }}>
            {goalOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.primaryGoal === option}
                onClick={() => setProfileData({...profileData, primaryGoal: option})}
                label={option}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Monthly advertising budget <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '0.75rem'
          }}>
            {budgetOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.monthlyBudget === option}
                onClick={() => setProfileData({...profileData, monthlyBudget: option})}
                label={option}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Have you advertised in email before? <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gap: '0.75rem'
          }}>
            {experienceOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.previousEmailAdvertising === option}
                onClick={() => setProfileData({...profileData, previousEmailAdvertising: option})}
                label={option}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 3: Target Audience  
function StepTargetAudience({ profileData, setProfileData }) {
  const genderOptions = ['Mostly female', 'Mostly male', 'Roughly even'];
  const ageOptions = ['18–24', '25–34', '35–44', '45–54', '55+'];
  const incomeOptions = ['<$50k', '$50k–$100k', '$100k+'];
  const regionOptions = ['Northeast', 'Mid-Atlantic', 'Southeast', 'Midwest', 'Southwest', 'West Coast', 'Mountain West', 'Pacific Northwest'];
  const [internationalDetails, setInternationalDetails] = React.useState(profileData.targetInternational || '');

  const toggleRegion = (region) => {
    if (profileData.targetRegions.includes(region)) {
      setProfileData({
        ...profileData,
        targetRegions: profileData.targetRegions.filter(r => r !== region)
      });
    } else {
      setProfileData({
        ...profileData,
        targetRegions: [...profileData.targetRegions, region]
      });
    }
  };

  return (
    <div>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.01em',
        fontFamily: "'Inter', sans-serif"
      }}>
        Target Audience
      </h2>
      <p style={{
        fontSize: '1rem',
        color: 'rgba(232,230,227,0.6)',
        margin: '0 0 2.5rem 0',
        fontFamily: "'Inter', sans-serif"
      }}>
        Define who you want to reach with your advertising
      </p>

      <div style={{ display: 'grid', gap: '2rem' }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Primary gender <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '0.75rem'
          }}>
            {genderOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.targetGender === option}
                onClick={() => setProfileData({...profileData, targetGender: option})}
                label={option}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Primary age range <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '0.75rem'
          }}>
            {ageOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.targetAgeRange === option}
                onClick={() => setProfileData({...profileData, targetAgeRange: option})}
                label={option}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Household income <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '0.75rem'
          }}>
            {incomeOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.targetIncome === option}
                onClick={() => setProfileData({...profileData, targetIncome: option})}
                label={option}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Target customer regions (select all that apply) <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '0.75rem'
          }}>
            {regionOptions.map(option => (
              <CheckboxButton
                key={option}
                selected={profileData.targetRegions.includes(option)}
                onClick={() => toggleRegion(option)}
                label={option}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '0.5rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            International customers
          </label>
          <p style={{
            fontSize: '0.875rem',
            color: 'rgba(232,230,227,0.6)',
            margin: '0 0 1rem 0',
            fontFamily: "'Inter', sans-serif"
          }}>
            Optional - specify countries or regions if targeting international customers
          </p>
          <input
            type="text"
            placeholder="e.g., Canada, UK, Australia..."
            value={internationalDetails}
            onChange={(e) => {
              setInternationalDetails(e.target.value);
              setProfileData({...profileData, targetInternational: e.target.value});
            }}
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              background: 'rgba(232,230,227,0.05)',
              border: '1px solid rgba(232,230,227,0.2)',
              borderRadius: '8px',
              color: '#e8e6e3',
              fontSize: '1rem',
              fontFamily: "'Inter', sans-serif",
              outline: 'none'
            }}
            onFocus={(e) => e.currentTarget.style.borderColor = '#ec4899'}
            onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(232,230,227,0.2)'}
          />
        </div>
      </div>
    </div>
  );
}

// Step 4: Product/Service Category
function StepProductCategory({ profileData, setProfileData }) {
  const categoryOptions = [
    'Beauty / Skincare',
    'Wellness / Supplements',
    'Personal Care',
    'Food / Beverage',
    'CPG / Consumables',
    'Apparel / Lifestyle',
    'Home / Garden',
    'Pets',
    'Baby / Kids',
    'Fitness / Sports',
    'Technology / Electronics',
    'Travel / Hospitality',
    'Finance / Professional Services',
    'Education / Learning',
    'Entertainment / Media',
    'Other'
  ];

  const toggleCategory = (category) => {
    if (profileData.preferredPublisherCategories.includes(category)) {
      setProfileData({
        ...profileData,
        preferredPublisherCategories: profileData.preferredPublisherCategories.filter(c => c !== category)
      });
    } else {
      setProfileData({
        ...profileData,
        preferredPublisherCategories: [...profileData.preferredPublisherCategories, category]
      });
    }
  };

  return (
    <div>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.01em',
        fontFamily: "'Inter', sans-serif"
      }}>
        Product/Service Category
      </h2>
      <p style={{
        fontSize: '1rem',
        color: 'rgba(232,230,227,0.6)',
        margin: '0 0 2.5rem 0',
        fontFamily: "'Inter', sans-serif"
      }}>
        Tell us what you're advertising and where you'd like to appear
      </p>

      <div style={{ display: 'grid', gap: '2rem' }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            What category are you advertising? <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '0.75rem'
          }}>
            {categoryOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.advertisingCategory === option}
                onClick={() => setProfileData({...profileData, advertisingCategory: option})}
                label={option}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Which publisher categories would you like to advertise with? (select all that apply) <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '0.75rem'
          }}>
            {categoryOptions.map(option => (
              <CheckboxButton
                key={option}
                selected={profileData.preferredPublisherCategories.includes(option)}
                onClick={() => toggleCategory(option)}
                label={option}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 5: Campaign Preferences
function StepCampaignPreferences({ profileData, setProfileData }) {
  const placementOptions = [
    'Email newsletter ads',
    'Dedicated email sends',
    'Sponsored content sections',
    'Website display ads (if publisher offers)',
    'Social media mentions (if publisher offers)'
  ];
  
  const timelineOptions = [
    'As soon as possible (within 2 weeks)',
    'Within 1 month',
    'Within 2-3 months',
    'Just exploring for now'
  ];
  
  const creativeOptions = [
    'Yes, ready to go',
    'Mostly ready, need minor help',
    'No, need help creating',
    'Not sure what I need'
  ];

  const togglePlacement = (placement) => {
    if (profileData.placementTypes.includes(placement)) {
      setProfileData({
        ...profileData,
        placementTypes: profileData.placementTypes.filter(p => p !== placement)
      });
    } else {
      setProfileData({
        ...profileData,
        placementTypes: [...profileData.placementTypes, placement]
      });
    }
  };

  return (
    <div>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.01em',
        fontFamily: "'Inter', sans-serif"
      }}>
        Campaign Preferences
      </h2>
      <p style={{
        fontSize: '1rem',
        color: 'rgba(232,230,227,0.6)',
        margin: '0 0 2.5rem 0',
        fontFamily: "'Inter', sans-serif"
      }}>
        Configure your campaign preferences and timeline
      </p>

      <div style={{ display: 'grid', gap: '2rem' }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            What types of ad placements are you interested in? (select all that apply) <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gap: '0.75rem'
          }}>
            {placementOptions.map(option => (
              <CheckboxButton
                key={option}
                selected={profileData.placementTypes.includes(option)}
                onClick={() => togglePlacement(option)}
                label={option}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            When do you want to launch your first campaign? <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gap: '0.75rem'
          }}>
            {timelineOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.launchTimeline === option}
                onClick={() => setProfileData({...profileData, launchTimeline: option})}
                label={option}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Do you have creative assets ready? <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gap: '0.75rem'
          }}>
            {creativeOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.creativeAssetsReady === option}
                onClick={() => setProfileData({...profileData, creativeAssetsReady: option})}
                label={option}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 6: Success Metrics
function StepSuccessMetrics({ profileData, setProfileData }) {
  const metricsOptions = [
    'Click-through rate (CTR)',
    'Conversions / sales',
    'Lead generation',
    'Website traffic',
    'Brand awareness / reach',
    'Return on ad spend (ROAS)'
  ];
  
  const trackingOptions = [
    'Yes, full tracking (UTM, pixels, etc.)',
    'Basic tracking (Google Analytics)',
    'No tracking yet, need help'
  ];

  const toggleMetric = (metric) => {
    if (profileData.successMetrics.includes(metric)) {
      setProfileData({
        ...profileData,
        successMetrics: profileData.successMetrics.filter(m => m !== metric)
      });
    } else {
      setProfileData({
        ...profileData,
        successMetrics: [...profileData.successMetrics, metric]
      });
    }
  };

  return (
    <div>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.01em',
        fontFamily: "'Inter', sans-serif"
      }}>
        Success Metrics & Tracking
      </h2>
      <p style={{
        fontSize: '1rem',
        color: 'rgba(232,230,227,0.6)',
        margin: '0 0 2.5rem 0',
        fontFamily: "'Inter', sans-serif"
      }}>
        Define how you'll measure campaign success
      </p>

      <div style={{ display: 'grid', gap: '2rem' }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            How do you measure campaign success? (select all that apply) <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gap: '0.75rem'
          }}>
            {metricsOptions.map(option => (
              <CheckboxButton
                key={option}
                selected={profileData.successMetrics.includes(option)}
                onClick={() => toggleMetric(option)}
                label={option}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Do you have tracking capabilities set up? <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gap: '0.75rem'
          }}>
            {trackingOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.trackingCapabilities === option}
                onClick={() => setProfileData({...profileData, trackingCapabilities: option})}
                label={option}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '0.5rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            What's your target cost per acquisition?
          </label>
          <p style={{
            fontSize: '0.875rem',
            color: 'rgba(232,230,227,0.6)',
            margin: '0 0 1rem 0',
            fontFamily: "'Inter', sans-serif"
          }}>
            Optional - what do you expect to pay per customer or conversion?
          </p>
          <input
            type="text"
            placeholder="e.g., $50, Not sure yet"
            value={profileData.targetCPA}
            onChange={(e) => setProfileData({...profileData, targetCPA: e.target.value})}
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              background: 'rgba(232,230,227,0.05)',
              border: '1px solid rgba(232,230,227,0.2)',
              borderRadius: '8px',
              color: '#e8e6e3',
              fontSize: '1rem',
              fontFamily: "'Inter', sans-serif",
              outline: 'none'
            }}
            onFocus={(e) => e.currentTarget.style.borderColor = '#ec4899'}
            onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(232,230,227,0.2)'}
          />
        </div>
      </div>
    </div>
  );
}

// Step 7: Compliance & Exclusions
function StepCompliance({ profileData, setProfileData }) {
  return (
    <div>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.01em',
        fontFamily: "'Inter', sans-serif"
      }}>
        Compliance & Exclusions
      </h2>
      <p style={{
        fontSize: '1rem',
        color: 'rgba(232,230,227,0.6)',
        margin: '0 0 2.5rem 0',
        fontFamily: "'Inter', sans-serif"
      }}>
        Any restrictions or compliance requirements we should know about?
      </p>

      <div style={{ display: 'grid', gap: '2rem' }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '0.5rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Are there categories or publishers you would NOT want to work with?
          </label>
          <p style={{
            fontSize: '0.875rem',
            color: 'rgba(232,230,227,0.6)',
            margin: '0 0 1rem 0',
            fontFamily: "'Inter', sans-serif"
          }}>
            Optional - list any categories or specific publishers you'd prefer to exclude
          </p>
          <textarea
            placeholder="e.g., alcohol, tobacco, gambling, competitors, political content..."
            value={profileData.excludedCategories}
            onChange={(e) => setProfileData({...profileData, excludedCategories: e.target.value})}
            rows={4}
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              background: 'rgba(232,230,227,0.05)',
              border: '1px solid rgba(232,230,227,0.2)',
              borderRadius: '8px',
              color: '#e8e6e3',
              fontSize: '1rem',
              fontFamily: "'Inter', sans-serif",
              outline: 'none',
              resize: 'vertical'
            }}
            onFocus={(e) => e.currentTarget.style.borderColor = '#ec4899'}
            onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(232,230,227,0.2)'}
          />
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '0.5rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Any legal/compliance requirements we should know about?
          </label>
          <p style={{
            fontSize: '0.875rem',
            color: 'rgba(232,230,227,0.6)',
            margin: '0 0 1rem 0',
            fontFamily: "'Inter', sans-serif"
          }}>
            Optional - e.g., HIPAA, financial services regulations, FDA restrictions
          </p>
          <textarea
            placeholder="Describe any compliance requirements..."
            value={profileData.complianceRequirements}
            onChange={(e) => setProfileData({...profileData, complianceRequirements: e.target.value})}
            rows={4}
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              background: 'rgba(232,230,227,0.05)',
              border: '1px solid rgba(232,230,227,0.2)',
              borderRadius: '8px',
              color: '#e8e6e3',
              fontSize: '1rem',
              fontFamily: "'Inter', sans-serif",
              outline: 'none',
              resize: 'vertical'
            }}
            onFocus={(e) => e.currentTarget.style.borderColor = '#ec4899'}
            onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(232,230,227,0.2)'}
          />
        </div>
      </div>
    </div>
  );
}

// Step 2: Purpose & Goals (Advertiser)
function StepAdvertiserPurposeGoals({ profileData, setProfileData }) {
  const purposeOptions = [
    'Brand awareness / discovery',
    'Customer acquisition / sales',
    'Retargeting / re-engagement',
    'Testing new audiences',
    'Launching a new product or offer'
  ];

  const outcomeOptions = [
    'New customers',
    'Website traffic',
    'Email signups',
    'Purchases',
    'Trial or sample requests',
    'Still exploring'
  ];

  return (
    <div>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.01em',
        fontFamily: "'Inter', sans-serif"
      }}>
        Purpose & Goals
      </h2>
      <p style={{
        fontSize: '1rem',
        color: 'rgba(232,230,227,0.6)',
        margin: '0 0 2.5rem 0',
        fontFamily: "'Inter', sans-serif"
      }}>
        Help us understand what you're looking to achieve
      </p>

      <div style={{ display: 'grid', gap: '2rem' }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '0.5rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            What is the primary purpose of your advertising? <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <p style={{
            fontSize: '0.875rem',
            color: 'rgba(232,230,227,0.5)',
            margin: '0 0 1rem 0',
            fontFamily: "'Inter', sans-serif"
          }}>
            This helps us recommend the right types of placements and partners
          </p>
          <div style={{
            display: 'grid',
            gap: '0.75rem'
          }}>
            {purposeOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.primaryPurpose === option}
                onClick={() => setProfileData({...profileData, primaryPurpose: option})}
                label={option}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            What outcome matters most for this campaign?
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '0.75rem'
          }}>
            {outcomeOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.primaryOutcome === option}
                onClick={() => setProfileData({...profileData, primaryOutcome: option})}
                label={option}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 3: Economics & Constraints (Advertiser)
function StepAdvertiserEconomics({ profileData, setProfileData }) {
  const cacOptions = [
    'Under $50',
    '$50 – $100',
    '$100 – $200',
    '$200 – $300',
    '$300+',
    'Not sure yet'
  ];

  const aovOptions = [
    'Under $50',
    '$50 – $100',
    '$100 – $200',
    '$200+',
    'Not applicable / not sure'
  ];

  return (
    <div>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.01em',
        fontFamily: "'Inter', sans-serif"
      }}>
        Economics & Constraints
      </h2>
      <p style={{
        fontSize: '1rem',
        color: 'rgba(232,230,227,0.6)',
        margin: '0 0 2.5rem 0',
        fontFamily: "'Inter', sans-serif"
      }}>
        Help us align placements with your goals
      </p>

      <div style={{ display: 'grid', gap: '2rem' }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '0.5rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            What is your target cost to acquire a new customer (CAC)?
          </label>
          <p style={{
            fontSize: '0.875rem',
            color: 'rgba(232,230,227,0.5)',
            margin: '0 0 1rem 0',
            fontFamily: "'Inter', sans-serif"
          }}>
            This helps us align placements with your goals. We don't guarantee outcomes.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '0.75rem'
          }}>
            {cacOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.targetCAC === option}
                onClick={() => setProfileData({...profileData, targetCAC: option})}
                label={option}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            What is your average order value (AOV)?
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '0.75rem'
          }}>
            {aovOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.averageOrderValue === option}
                onClick={() => setProfileData({...profileData, averageOrderValue: option})}
                label={option}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 4: Placement Preferences (Advertiser)
function StepAdvertiserPlacements({ profileData, setProfileData }) {
  const placementOptions = [
    'Sponsored email placements',
    'Website placements',
    'Social placements',
    'Link / resource placements',
    'Packaging or order inserts',
    'Open to recommendations'
  ];

  const togglePlacement = (placement) => {
    const current = profileData.interestedPlacements || [];
    if (current.includes(placement)) {
      setProfileData({...profileData, interestedPlacements: current.filter(p => p !== placement)});
    } else {
      setProfileData({...profileData, interestedPlacements: [...current, placement]});
    }
  };

  return (
    <div>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.01em',
        fontFamily: "'Inter', sans-serif"
      }}>
        Placement Preferences
      </h2>
      <p style={{
        fontSize: '1rem',
        color: 'rgba(232,230,227,0.6)',
        margin: '0 0 2.5rem 0',
        fontFamily: "'Inter', sans-serif"
      }}>
        Where would you like to appear?
      </p>

      <div style={{ display: 'grid', gap: '2rem' }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Where are you interested in appearing? <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <div style={{
            display: 'grid',
            gap: '0.75rem'
          }}>
            {placementOptions.map(option => (
              <CheckboxButton
                key={option}
                selected={(profileData.interestedPlacements || []).includes(option)}
                onClick={() => togglePlacement(option)}
                label={option}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '0.5rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Are there any brands or categories you prefer to partner with?
          </label>
          <p style={{
            fontSize: '0.875rem',
            color: 'rgba(232,230,227,0.5)',
            margin: '0 0 1rem 0',
            fontFamily: "'Inter', sans-serif"
          }}>
            Optional
          </p>
          <textarea
            placeholder="e.g., wellness brands, eco-friendly companies, fitness influencers..."
            value={profileData.preferredPartnerBrands || ''}
            onChange={(e) => setProfileData({...profileData, preferredPartnerBrands: e.target.value})}
            rows={3}
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              background: 'rgba(232,230,227,0.05)',
              border: '1px solid rgba(232,230,227,0.2)',
              borderRadius: '8px',
              color: '#e8e6e3',
              fontSize: '1rem',
              fontFamily: "'Inter', sans-serif",
              outline: 'none',
              resize: 'vertical'
            }}
            onFocus={(e) => e.currentTarget.style.borderColor = '#ec4899'}
            onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(232,230,227,0.2)'}
          />
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '0.5rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            Are there any categories you do NOT want to appear next to?
          </label>
          <p style={{
            fontSize: '0.875rem',
            color: 'rgba(232,230,227,0.5)',
            margin: '0 0 1rem 0',
            fontFamily: "'Inter', sans-serif"
          }}>
            Optional
          </p>
          <textarea
            placeholder="e.g., alcohol, tobacco, gambling, competitors..."
            value={profileData.excludedCategories || ''}
            onChange={(e) => setProfileData({...profileData, excludedCategories: e.target.value})}
            rows={3}
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              background: 'rgba(232,230,227,0.05)',
              border: '1px solid rgba(232,230,227,0.2)',
              borderRadius: '8px',
              color: '#e8e6e3',
              fontSize: '1rem',
              fontFamily: "'Inter', sans-serif",
              outline: 'none',
              resize: 'vertical'
            }}
            onFocus={(e) => e.currentTarget.style.borderColor = '#ec4899'}
            onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(232,230,227,0.2)'}
          />
        </div>
      </div>
    </div>
  );
}

// Step 5: Customer Affinities (Advertiser - targets same customers)
function StepAdvertiserCustomerAffinities({ profileData, setProfileData }) {
  const brandAttributeOptions = [
    'Premium',
    'Value-focused',
    'Luxury',
    'Eco-friendly',
    'Science-driven',
    'Community-oriented',
    'Performance-focused',
    'Natural/organic',
    'Clinical/medical',
    'Trend-forward'
  ];

  const toggleAttribute = (attribute) => {
    const current = profileData.brandAttributes || [];
    if (current.includes(attribute)) {
      setProfileData({...profileData, brandAttributes: current.filter(a => a !== attribute)});
    } else {
      setProfileData({...profileData, brandAttributes: [...current, attribute]});
    }
  };

  return (
    <div>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.01em',
        fontFamily: "'Inter', sans-serif"
      }}>
        Brand Attributes
      </h2>
      <p style={{
        fontSize: '1rem',
        color: 'rgba(232,230,227,0.6)',
        margin: '0 0 2.5rem 0',
        fontFamily: "'Inter', sans-serif"
      }}>
        Help us match you with complementary partners
      </p>

      <div>
        <label style={{
          display: 'block',
          fontSize: '1rem',
          fontWeight: 600,
          marginBottom: '0.5rem',
          fontFamily: "'Inter', sans-serif",
          color: 'rgba(232,230,227,0.9)'
        }}>
          Which brand attributes best describe you? <span style={{ color: '#ec4899' }}>*</span>
        </label>
        <p style={{
          fontSize: '0.875rem',
          color: 'rgba(232,230,227,0.5)',
          margin: '0 0 1rem 0',
          fontFamily: "'Inter', sans-serif"
        }}>
          Select all that apply
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '0.75rem'
        }}>
          {brandAttributeOptions.map(option => (
            <CheckboxButton
              key={option}
              selected={(profileData.brandAttributes || []).includes(option)}
              onClick={() => toggleAttribute(option)}
              label={option}
            />
          ))}
        </div>
        {(profileData.brandAttributes || []).length > 0 && (
          <p style={{
            fontSize: '0.875rem',
            color: 'rgba(236,72,153,0.8)',
            margin: '1rem 0 0 0',
            fontFamily: "'Inter', sans-serif"
          }}>
            ✓ {(profileData.brandAttributes || []).length} attribute{(profileData.brandAttributes || []).length === 1 ? '' : 's'} selected
          </p>
        )}
      </div>
    </div>
  );
}

// Step 6: Budget & Timing (Advertiser)
function StepAdvertiserBudgetTiming({ profileData, setProfileData }) {
  const budgetOptions = [
    '$500 (small test)',
    '$1,000',
    '$2,000',
    '$5,000+',
    'Not sure yet'
  ];

  const timelineOptions = [
    'As soon as possible',
    'Within the next 30 days',
    'Just exploring for now'
  ];

  return (
    <div>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.01em',
        fontFamily: "'Inter', sans-serif"
      }}>
        Budget & Timing
      </h2>
      <p style={{
        fontSize: '1rem',
        color: 'rgba(232,230,227,0.6)',
        margin: '0 0 2.5rem 0',
        fontFamily: "'Inter', sans-serif"
      }}>
        Let us know your budget and when you'd like to get started
      </p>

      <div style={{ display: 'grid', gap: '2rem' }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '0.5rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            What budget would you like to start with? <span style={{ color: '#ec4899' }}>*</span>
          </label>
          <p style={{
            fontSize: '0.875rem',
            color: 'rgba(232,230,227,0.5)',
            margin: '0 0 1rem 0',
            fontFamily: "'Inter', sans-serif"
          }}>
            Most partners start with a small test and scale from there
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '0.75rem'
          }}>
            {budgetOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.startingBudget === option}
                onClick={() => setProfileData({...profileData, startingBudget: option})}
                label={option}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(232,230,227,0.9)'
          }}>
            When would you like to start?
          </label>
          <div style={{
            display: 'grid',
            gap: '0.75rem'
          }}>
            {timelineOptions.map(option => (
              <RadioButton
                key={option}
                selected={profileData.startTimeline === option}
                onClick={() => setProfileData({...profileData, startTimeline: option})}
                label={option}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 7: Creative & Approvals (Advertiser)
function StepAdvertiserCreative({ profileData, setProfileData }) {
  const creativeOptions = [
    'Yes',
    'No, I\'d like help',
    'Not sure yet'
  ];

  return (
    <div>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.01em',
        fontFamily: "'Inter', sans-serif"
      }}>
        Creative & Approvals
      </h2>
      <p style={{
        fontSize: '1rem',
        color: 'rgba(232,230,227,0.6)',
        margin: '0 0 2.5rem 0',
        fontFamily: "'Inter', sans-serif"
      }}>
        Let us know about your creative assets
      </p>

      <div>
        <label style={{
          display: 'block',
          fontSize: '1rem',
          fontWeight: 600,
          marginBottom: '1rem',
          fontFamily: "'Inter', sans-serif",
          color: 'rgba(232,230,227,0.9)'
        }}>
          Do you already have creative ready?
        </label>
        <div style={{
          display: 'grid',
          gap: '0.75rem'
        }}>
          {creativeOptions.map(option => (
            <RadioButton
              key={option}
              selected={profileData.creativeReady === option}
              onClick={() => setProfileData({...profileData, creativeReady: option})}
              label={option}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// Step 8: Advertiser Consent
function StepAdvertiserConsent({ profileData, setProfileData }) {
  return (
    <div>
      <div style={{
        textAlign: 'center',
        marginBottom: '3rem'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          margin: '0 auto 1.5rem',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(236,72,153,0.2) 0%, rgba(168,85,247,0.2) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid rgba(236,72,153,0.3)'
        }}>
          <Sparkles size={40} color="#ec4899" />
        </div>
        <h2 style={{
          fontSize: '2rem',
          fontWeight: 700,
          margin: '0 0 0.75rem 0',
          letterSpacing: '-0.02em'
        }}>
          Almost There!
        </h2>
        <p style={{
          fontSize: '1.125rem',
          color: 'rgba(232,230,227,0.7)',
          margin: 0,
          fontFamily: "'Inter', sans-serif"
        }}>
          Review and accept our advertiser terms to complete setup
        </p>
      </div>

      <div style={{
        background: 'rgba(232,230,227,0.03)',
        border: '1px solid rgba(232,230,227,0.1)',
        borderRadius: '12px',
        padding: '2rem',
        marginBottom: '2rem'
      }}>
        <h3 style={{
          fontSize: '1.125rem',
          fontWeight: 600,
          margin: '0 0 1rem 0',
          fontFamily: "'Inter', sans-serif"
        }}>
          Advertiser Platform Terms
        </h3>
        <ul style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'grid',
          gap: '0.75rem'
        }}>
          {[
            'This is a private beta - features are being actively developed',
            'Campaign placements subject to publisher approval',
            'You maintain full control over your advertising budget and campaigns',
            'All placements are brand-safe and approved by both parties',
            'Performance metrics provided for transparency',
            'You can pause or adjust campaigns at any time'
          ].map((term, idx) => (
            <li key={idx} style={{
              display: 'flex',
              gap: '0.75rem',
              fontSize: '0.9375rem',
              fontFamily: "'Inter', sans-serif",
              color: 'rgba(232,230,227,0.8)'
            }}>
              <Check size={20} color="#ec4899" style={{ flexShrink: 0, marginTop: '2px' }} />
              <span>{term}</span>
            </li>
          ))}
        </ul>
      </div>

      <label style={{
        display: 'flex',
        gap: '1rem',
        cursor: 'pointer',
        padding: '1.5rem',
        background: profileData.consentGiven ? 'rgba(236,72,153,0.1)' : 'rgba(232,230,227,0.03)',
        border: `2px solid ${profileData.consentGiven ? '#ec4899' : 'rgba(232,230,227,0.1)'}`,
        borderRadius: '12px',
        transition: 'all 0.2s ease',
        fontFamily: "'Inter', sans-serif"
      }}>
        <input
          type="checkbox"
          checked={profileData.consentGiven}
          onChange={(e) => setProfileData({...profileData, consentGiven: e.target.checked})}
          style={{
            width: '20px',
            height: '20px',
            marginTop: '2px',
            cursor: 'pointer',
            accentColor: '#ec4899'
          }}
        />
        <div>
          <div style={{
            fontSize: '1rem',
            fontWeight: 600,
            color: 'rgba(232,230,227,0.9)',
            marginBottom: '0.25rem'
          }}>
            I understand and agree to these terms
          </div>
          <div style={{
            fontSize: '0.875rem',
            color: 'rgba(232,230,227,0.6)'
          }}>
            I confirm that I have the authority to represent this company and make advertising decisions
          </div>
        </div>
      </label>

      {!profileData.consentGiven && (
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          background: 'rgba(239,68,68,0.1)',
          border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: '8px',
          fontSize: '0.875rem',
          color: 'rgba(239,68,68,0.8)',
          fontFamily: "'Inter', sans-serif"
        }}>
          Please check the box above to complete your registration
        </div>
      )}
    </div>
  );
}

// Reusable UI Components
function RadioButton({ selected, onClick, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '0.875rem 1rem',
        background: selected ? 'rgba(168,85,247,0.15)' : 'rgba(232,230,227,0.05)',
        border: `2px solid ${selected ? '#a855f7' : 'rgba(232,230,227,0.2)'}`,
        borderRadius: '10px',
        color: selected ? '#e8e6e3' : 'rgba(232,230,227,0.8)',
        fontSize: '0.9375rem',
        fontWeight: selected ? 600 : 500,
        fontFamily: "'Inter', sans-serif",
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        textAlign: 'center',
        position: 'relative'
      }}
      onMouseEnter={(e) => {
        if (!selected) {
          e.currentTarget.style.borderColor = 'rgba(168,85,247,0.4)';
          e.currentTarget.style.background = 'rgba(232,230,227,0.08)';
        }
      }}
      onMouseLeave={(e) => {
        if (!selected) {
          e.currentTarget.style.borderColor = 'rgba(232,230,227,0.2)';
          e.currentTarget.style.background = 'rgba(232,230,227,0.05)';
        }
      }}
    >
      {selected && (
        <Check 
          size={16} 
          style={{
            position: 'absolute',
            top: '0.5rem',
            right: '0.5rem',
            color: '#a855f7'
          }} 
        />
      )}
      {label}
    </button>
  );
}

function CheckboxButton({ selected, onClick, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '0.875rem 1rem',
        background: selected ? 'rgba(168,85,247,0.15)' : 'rgba(232,230,227,0.05)',
        border: `2px solid ${selected ? '#a855f7' : 'rgba(232,230,227,0.2)'}`,
        borderRadius: '10px',
        color: selected ? '#e8e6e3' : 'rgba(232,230,227,0.8)',
        fontSize: '0.9375rem',
        fontWeight: selected ? 600 : 500,
        fontFamily: "'Inter', sans-serif",
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem'
      }}
      onMouseEnter={(e) => {
        if (!selected) {
          e.currentTarget.style.borderColor = 'rgba(168,85,247,0.4)';
          e.currentTarget.style.background = 'rgba(232,230,227,0.08)';
        }
      }}
      onMouseLeave={(e) => {
        if (!selected) {
          e.currentTarget.style.borderColor = 'rgba(232,230,227,0.2)';
          e.currentTarget.style.background = 'rgba(232,230,227,0.05)';
        }
      }}
    >
      <div style={{
        width: '20px',
        height: '20px',
        borderRadius: '4px',
        border: `2px solid ${selected ? '#a855f7' : 'rgba(232,230,227,0.3)'}`,
        background: selected ? '#a855f7' : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        {selected && <Check size={14} color="#fff" />}
      </div>
      {label}
    </button>
  );
}

// ============================================
// DASHBOARD SCREEN COMPONENT
// ============================================

// Payment Placeholder Modal
function PaymentPlaceholderModal({ show, onClose, type, opportunity }) {
  if (!show) return null;

  const isEarnSide = type === 'accept-opportunity';
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(10,14,39,0.95)',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '2rem'
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)',
        border: '1px solid rgba(168,85,247,0.3)',
        borderRadius: '16px',
        padding: '3rem',
        maxWidth: '500px',
        width: '100%',
        position: 'relative',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
      }}>
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'transparent',
            border: 'none',
            color: 'rgba(232,230,227,0.6)',
            fontSize: '1.5rem',
            cursor: 'pointer',
            padding: '0.5rem',
            lineHeight: 1
          }}
        >
          ×
        </button>

        {/* Icon */}
        <div style={{
          width: '80px',
          height: '80px',
          margin: '0 auto 1.5rem',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(168,85,247,0.2) 0%, rgba(236,72,153,0.2) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid rgba(168,85,247,0.3)',
          fontSize: '2.5rem'
        }}>
          {isEarnSide ? '🎉' : '📢'}
        </div>

        {/* Content */}
        <div style={{ textAlign: 'center' }}>
          <h2 style={{
            fontSize: '1.75rem',
            fontWeight: 700,
            margin: '0 0 1rem 0',
            letterSpacing: '-0.01em',
            fontFamily: "'Inter', sans-serif"
          }}>
            {isEarnSide ? "Great! You've accepted this opportunity" : 'Campaign Creation Coming Soon'}
          </h2>

          {isEarnSide && opportunity && (
            <div style={{
              background: 'rgba(232,230,227,0.05)',
              border: '1px solid rgba(232,230,227,0.1)',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1.5rem',
              textAlign: 'left'
            }}>
              <div style={{
                fontSize: '0.875rem',
                color: 'rgba(232,230,227,0.6)',
                marginBottom: '0.5rem',
                fontFamily: "'Inter', sans-serif"
              }}>
                Campaign
              </div>
              <div style={{
                fontSize: '1.125rem',
                fontWeight: 600,
                marginBottom: '1rem',
                fontFamily: "'Inter', sans-serif"
              }}>
                {opportunity.brand} ({opportunity.type})
              </div>
              <div style={{
                fontSize: '0.875rem',
                color: 'rgba(232,230,227,0.6)',
                marginBottom: '0.25rem',
                fontFamily: "'Inter', sans-serif"
              }}>
                Estimated Earnings
              </div>
              <div style={{
                fontSize: '1.75rem',
                fontWeight: 700,
                color: '#10b981',
                fontFamily: "'Inter', sans-serif"
              }}>
                ${opportunity.cpm * parseInt(opportunity.reach.replace('k', '')) / 1000}
              </div>
            </div>
          )}

          <div style={{
            background: 'rgba(59,130,246,0.1)',
            border: '1px solid rgba(59,130,246,0.3)',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '2rem'
          }}>
            <div style={{
              fontSize: '1rem',
              fontWeight: 600,
              color: '#3b82f6',
              marginBottom: '0.5rem',
              fontFamily: "'Inter', sans-serif"
            }}>
              Payment Processing Launching Soon
            </div>
            <p style={{
              fontSize: '0.9375rem',
              color: 'rgba(232,230,227,0.8)',
              margin: 0,
              lineHeight: 1.6,
              fontFamily: "'Inter', sans-serif"
            }}>
              {isEarnSide 
                ? "We'll notify you when payment processing is live. You'll connect your bank account before your first payout."
                : "Payment processing goes live soon. We'll email you when you can launch campaigns with real budgets."}
            </p>
          </div>

          <button
            onClick={onClose}
            style={{
              width: '100%',
              padding: '1rem',
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '1rem',
              fontWeight: 600,
              fontFamily: "'Inter', sans-serif",
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 12px rgba(168,85,247,0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(168,85,247,0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(168,85,247,0.3)';
            }}
          >
            {isEarnSide ? 'Continue to Dashboard' : 'Back to Dashboard'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DashboardScreen({ activeTab, setActiveTab, partnerData, publisherProfile, advertiserProfile, handleLogout, activeRole, setActiveRole, accountType, showPaymentModal, setShowPaymentModal, paymentModalType, setPaymentModalType, selectedOpportunity, setSelectedOpportunity }) {
  // Different tabs for Earn (publisher) vs Reach (advertiser)
  const earnTabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'earnings', label: 'Earnings', icon: DollarSign }
  ];
  
  const reachTabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'discover', label: 'Discover Partners', icon: Search },
    { id: 'my-profile', label: 'My Profile', icon: User }
  ];
  
  const tabs = activeRole === 'publisher' ? earnTabs : reachTabs;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #2a1f3a 100%)',
      fontFamily: "'Crimson Pro', 'Fraunces', Georgia, serif",
      color: '#e8e6e3',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Ambient background effects */}
      <div style={{
        position: 'fixed',
        top: '-50%',
        right: '-20%',
        width: '800px',
        height: '800px',
        background: 'radial-gradient(circle, rgba(168,85,247,0.15) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        pointerEvents: 'none'
      }} />
      <div style={{
        position: 'fixed',
        bottom: '-30%',
        left: '-10%',
        width: '600px',
        height: '600px',
        background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        pointerEvents: 'none'
      }} />

      {/* Header */}
      <header style={{
        borderBottom: '1px solid rgba(232,230,227,0.1)',
        background: 'rgba(10,14,39,0.8)',
        backdropFilter: 'blur(20px)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '1.5rem 2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{
              fontSize: '2rem',
              fontWeight: 700,
              margin: 0,
              letterSpacing: '-0.02em',
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              MavStream
            </h1>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '2rem'
          }}>
            {/* Role Switcher - only show if account has both roles */}
            {(accountType === 'both' || accountType === 'supplier' || accountType === 'advertiser') && (
              <div style={{
                position: 'relative'
              }}>
                <select
                  value={activeRole}
                  onChange={(e) => setActiveRole(e.target.value)}
                  style={{
                    padding: '0.5rem 2.5rem 0.5rem 1rem',
                    background: 'rgba(232,230,227,0.1)',
                    border: '1px solid rgba(232,230,227,0.2)',
                    borderRadius: '6px',
                    color: '#e8e6e3',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    fontFamily: "'Inter', sans-serif",
                    cursor: 'pointer',
                    outline: 'none',
                    appearance: 'none'
                  }}
                >
                  {(accountType === 'supplier' || accountType === 'both') && (
                    <option value="publisher">Earn</option>
                  )}
                  {(accountType === 'advertiser' || accountType === 'both') && (
                    <option value="advertiser">Reach</option>
                  )}
                </select>
                <div style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: 'rgba(232,230,227,0.6)'
                }}>
                  ▼
                </div>
              </div>
            )}
            
            <button
              onClick={handleLogout}
              style={{
                padding: '0.5rem 1rem',
                background: 'rgba(232,230,227,0.1)',
                border: '1px solid rgba(232,230,227,0.2)',
                borderRadius: '6px',
                color: 'rgba(232,230,227,0.8)',
                fontSize: '0.875rem',
                fontWeight: 500,
                fontFamily: "'Inter', sans-serif",
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(232,230,227,0.15)';
                e.currentTarget.style.borderColor = 'rgba(232,230,227,0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(232,230,227,0.1)';
                e.currentTarget.style.borderColor = 'rgba(232,230,227,0.2)';
              }}
            >
              Log Out
            </button>
            <div style={{
              textAlign: 'right',
              marginRight: '1rem'
            }}>
              <div style={{
                fontSize: '0.875rem',
                color: 'rgba(232,230,227,0.6)',
                fontFamily: "'Inter', sans-serif"
              }}>
                Signed in as
              </div>
              <div style={{
                fontSize: '1rem',
                fontWeight: 600,
                fontFamily: "'Inter', sans-serif"
              }}>
                {partnerData.name}
              </div>
            </div>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.25rem',
              fontWeight: 700,
              color: '#fff'
            }}>
              {partnerData.name.charAt(0)}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '0 2rem',
          display: 'flex',
          gap: '0.5rem',
          borderTop: '1px solid rgba(232,230,227,0.1)'
        }}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '1rem 1.5rem',
                  background: 'none',
                  border: 'none',
                  color: isActive ? '#a855f7' : 'rgba(232,230,227,0.6)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.9375rem',
                  fontWeight: 600,
                  fontFamily: "'Inter', sans-serif",
                  borderBottom: isActive ? '2px solid #a855f7' : '2px solid transparent',
                  transition: 'all 0.3s ease',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.color = '#e8e6e3';
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.color = 'rgba(232,230,227,0.6)';
                }}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </header>

      {/* Main Content */}
      <main style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '3rem 2rem',
        position: 'relative'
      }}>
        {/* Payment Coming Soon Banner */}
        {activeRole === 'publisher' && (
          <div style={{
            background: 'rgba(59,130,246,0.1)',
            border: '1px solid rgba(59,130,246,0.3)',
            borderRadius: '12px',
            padding: '1rem 1.5rem',
            marginBottom: '2rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <div style={{ fontSize: '1.5rem' }}>💰</div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '0.9375rem',
                fontWeight: 600,
                color: '#3b82f6',
                marginBottom: '0.25rem',
                fontFamily: "'Inter', sans-serif"
              }}>
                Payment Processing Coming Soon!
              </div>
              <div style={{
                fontSize: '0.875rem',
                color: 'rgba(232,230,227,0.7)',
                fontFamily: "'Inter', sans-serif"
              }}>
                Connect your bank account when we launch payment processing. You'll be notified via email.
              </div>
            </div>
          </div>
        )}

        {activeRole === 'publisher' ? (
          <>
            {activeTab === 'overview' && <OverviewTab data={partnerData} publisherProfile={publisherProfile} setShowPaymentModal={setShowPaymentModal} setPaymentModalType={setPaymentModalType} setSelectedOpportunity={setSelectedOpportunity} />}
            {activeTab === 'earnings' && <EarningsTab data={partnerData} />}
          </>
        ) : (
          <AdvertiserDashboard advertiserProfile={advertiserProfile} activeTab={activeTab} setActiveTab={setActiveTab} />
        )}
      </main>

      {/* Payment Placeholder Modal */}
      <PaymentPlaceholderModal 
        show={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        type={paymentModalType}
        opportunity={selectedOpportunity}
      />
    </div>
  );
}

// Advertiser Dashboard - Partner Discovery
function AdvertiserDashboard({ advertiserProfile, activeTab, setActiveTab }) {
  const [selectedPartner, setSelectedPartner] = React.useState(null);
  const [filters, setFilters] = React.useState({
    category: '',
    minReach: '',
    region: ''
  });
  
  // Get current user
  const currentUser = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('mavstream_user') || '{}') : {};
  const currentUserEmail = currentUser.authData?.email || '';
  
  // Get partnerships for this advertiser
  const allPartnerships = getAllPartnerships();
  const myPartnerships = allPartnerships.filter(p => 
    p.advertiserEmail === currentUserEmail && 
    (p.status === 'active' || p.status === 'accepted')
  );
  
  // Get my submitted creatives
  const myCreatives = getAllCreatives().filter(c => c.advertiserEmail === currentUserEmail);

  // Get real publisher profiles from localStorage
  const rawPublishers = getAllPublishers();
  
  // Transform to partner format for display
  const realPartners = rawPublishers.map((user, index) => {
    // Map placement type codes to display names
    const placementLabels = {
      'newsletter-sponsored': 'Newsletter – Sponsored Section',
      'promotional-email': 'Promotional Email',
      'weekly-update': 'Weekly Update Email',
      'monthly-email': 'Monthly Email',
      'post-purchase': 'Post-Purchase Email',
      'homepage-placement': 'Homepage Placement',
      'content-page': 'Content Page',
      'blog-placement': 'Blog Placement',
      'other-website': 'Other Website',
      'sponsored-story': 'Sponsored Story',
      'sponsored-feed': 'Sponsored Feed Post',
      'pinned-post': 'Pinned Post',
      'sponsored-backlink': 'Sponsored Backlink',
      'recommended-partner': 'Recommended Partner Link',
      'order-box-insert': 'Order Box Insert'
    };
    
    const placementTypes = (user.publisherProfile?.placementTypes || [])
      .filter(t => !t.includes('-na'))
      .map(t => placementLabels[t] || t);

    return {
      id: index + 1,
      email: user.authData?.email || '',
      name: user.authData?.brandNameDBA || user.authData?.companyLegalName || 'Unnamed Company',
      description: user.publisherProfile?.companyDescription || 'No description provided',
      category: user.publisherProfile?.primaryCategory || 'Not specified',
      emailSubscribers: user.publisherProfile?.listSize || 'N/A',
      monthlyVisitors: user.publisherProfile?.websiteVisitors || 'N/A',
      socialReach: user.publisherProfile?.socialFollowers || 'N/A',
      drScore: user.publisherProfile?.drScore || 'N/A',
      engagedSubscribers: user.publisherProfile?.engagedSubscribers || 'N/A',
      placementTypes: placementTypes,
      regions: user.publisherProfile?.regions || [],
      genderMix: user.publisherProfile?.genderMix || 'N/A',
      ageRanges: user.publisherProfile?.ageRanges || [],
      avgOrderValue: user.publisherProfile?.averageOrderValue || 'N/A',
      emailFrequency: user.publisherProfile?.campaignFrequency || 'N/A'
    };
  });

  // Use real partners only - no sample data
  const partners = realPartners;

  const categories = ['All Categories', 'Pet & Animals', 'Health & Wellness', 'Technology', 'Home & Garden', 'Fashion', 'Food & Beverage', 'Finance', 'Travel', 'Beauty', 'Automotive', 'Sports & Fitness', 'Education', 'Entertainment'];

  const filteredPartners = partners.filter(partner => {
    if (filters.category && filters.category !== 'All Categories' && partner.category !== filters.category) return false;
    return true;
  });

  // Handle proposing a partnership
  const handleProposePartnership = (partner) => {
    const currentUser = JSON.parse(localStorage.getItem('mavstream_user') || '{}');
    
    const newPartnership = {
      publisherId: `pub-${partner.id}`,
      publisherName: partner.name,
      publisherEmail: partner.email,
      advertiserId: currentUser.authData?.email || 'unknown',
      advertiserName: currentUser.authData?.brandNameDBA || currentUser.authData?.companyLegalName || 'Unknown Advertiser',
      advertiserEmail: currentUser.authData?.email || '',
      status: 'proposed',
      proposedBy: 'advertiser',
      notes: `Partnership proposed by advertiser`
    };
    
    savePartnership(newPartnership);
    alert(`Partnership proposal sent to ${partner.name}! They will review and respond.`);
  };

  return (
    <div>
      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div style={{ animation: 'fadeIn 0.6s ease-out' }}>
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; transform: translateY(20px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
          
          <h2 style={{
            fontSize: '2rem',
            fontWeight: 700,
            margin: '0 0 0.5rem 0',
            fontFamily: "'Inter', sans-serif",
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <span>🚀</span> Reach Overview
          </h2>
          <p style={{
            fontSize: '1rem',
            color: 'rgba(232,230,227,0.6)',
            margin: '0 0 2rem 0',
            fontFamily: "'Inter', sans-serif"
          }}>
            Track your campaign performance and reach new customers
          </p>

          {/* Stats Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(249,115,22,0.1) 0%, rgba(234,88,12,0.1) 100%)',
              border: '1px solid rgba(249,115,22,0.3)',
              borderRadius: '16px',
              padding: '1.5rem'
            }}>
              <div style={{ fontSize: '0.875rem', color: 'rgba(232,230,227,0.6)', marginBottom: '0.5rem', fontFamily: "'Inter', sans-serif" }}>
                Campaigns to Date
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: 700, color: '#f97316', fontFamily: "'Inter', sans-serif" }}>
                {myPartnerships.length}
              </div>
            </div>

            <div style={{
              background: 'rgba(232,230,227,0.03)',
              border: '1px solid rgba(232,230,227,0.1)',
              borderRadius: '16px',
              padding: '1.5rem'
            }}>
              <div style={{ fontSize: '0.875rem', color: 'rgba(232,230,227,0.6)', marginBottom: '0.5rem', fontFamily: "'Inter', sans-serif" }}>
                Active Partnerships
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: 700, color: '#22d3ee', fontFamily: "'Inter', sans-serif" }}>
                {myPartnerships.filter(p => p.status === 'active').length}
              </div>
            </div>

            <div style={{
              background: 'rgba(232,230,227,0.03)',
              border: '1px solid rgba(232,230,227,0.1)',
              borderRadius: '16px',
              padding: '1.5rem'
            }}>
              <div style={{ fontSize: '0.875rem', color: 'rgba(232,230,227,0.6)', marginBottom: '0.5rem', fontFamily: "'Inter', sans-serif" }}>
                Partners Available
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: 700, color: '#a855f7', fontFamily: "'Inter', sans-serif" }}>
                {partners.length}
              </div>
            </div>

            <div style={{
              background: 'rgba(232,230,227,0.03)',
              border: '1px solid rgba(232,230,227,0.1)',
              borderRadius: '16px',
              padding: '1.5rem'
            }}>
              <div style={{ fontSize: '0.875rem', color: 'rgba(232,230,227,0.6)', marginBottom: '0.5rem', fontFamily: "'Inter', sans-serif" }}>
                Total Spend
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: 700, color: '#10b981', fontFamily: "'Inter', sans-serif" }}>
                $0
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div style={{
            background: 'rgba(232,230,227,0.03)',
            border: '1px solid rgba(232,230,227,0.1)',
            borderRadius: '16px',
            padding: '2rem'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: 700,
              margin: '0 0 1.5rem 0',
              fontFamily: "'Inter', sans-serif"
            }}>
              🎯 Quick Actions
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <button
                onClick={() => setActiveTab('discover')}
                style={{
                  padding: '1rem 1.5rem',
                  background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: 600,
                  fontFamily: "'Inter', sans-serif",
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                🔍 Discover Partners
              </button>
              <button
                onClick={() => setActiveTab('my-profile')}
                style={{
                  padding: '1rem 1.5rem',
                  background: 'rgba(232,230,227,0.1)',
                  border: '1px solid rgba(232,230,227,0.2)',
                  borderRadius: '8px',
                  color: '#e8e6e3',
                  fontSize: '1rem',
                  fontWeight: 600,
                  fontFamily: "'Inter', sans-serif",
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                👤 View My Profile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discover Partners Tab */}
      {activeTab === 'discover' && (
        <div>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            margin: '0 0 0.5rem 0',
            fontFamily: "'Inter', sans-serif",
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <span>🔍</span> Discover Partners
          </h3>
          <p style={{
            fontSize: '1rem',
            color: 'rgba(232,230,227,0.6)',
            margin: '0 0 2rem 0',
            fontFamily: "'Inter', sans-serif"
          }}>
            Find audience owners to partner with for your campaigns
          </p>

          {/* Filters */}
          <div style={{
            background: 'rgba(232,230,227,0.03)',
            border: '1px solid rgba(232,230,227,0.1)',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '2rem',
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <select
              value={filters.category}
              onChange={(e) => setFilters({...filters, category: e.target.value})}
              style={{
                padding: '0.75rem 1rem',
                background: 'rgba(232,230,227,0.05)',
                border: '1px solid rgba(232,230,227,0.2)',
                borderRadius: '8px',
                color: '#e8e6e3',
                fontSize: '0.9375rem',
                fontFamily: "'Inter', sans-serif",
                minWidth: '200px'
              }}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Partners Grid or Empty State */}
          {filteredPartners.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '4rem 2rem',
              background: 'rgba(232,230,227,0.03)',
              border: '1px solid rgba(232,230,227,0.1)',
              borderRadius: '16px'
            }}>
              <div style={{ fontSize: '4rem', marginBottom: '1.5rem' }}>🔍</div>
              <h4 style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                margin: '0 0 1rem 0',
                fontFamily: "'Inter', sans-serif",
                color: '#e8e6e3'
              }}>
                No Partners Available Yet
              </h4>
              <p style={{
                fontSize: '1rem',
                color: 'rgba(232,230,227,0.6)',
                fontFamily: "'Inter', sans-serif",
                maxWidth: '400px',
                margin: '0 auto 1.5rem'
              }}>
                Partners who complete the Earn onboarding will appear here. Check back soon as we grow our network of audience owners.
              </p>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: 'rgba(249,115,22,0.1)',
                border: '1px solid rgba(249,115,22,0.3)',
                borderRadius: '8px',
                color: '#f97316',
                fontSize: '0.9375rem',
                fontWeight: 500,
                fontFamily: "'Inter', sans-serif"
              }}>
                <span>⏳</span> We'll notify you when new partners join
              </div>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '1.5rem'
            }}>
              {filteredPartners.map(partner => (
                <div
                  key={partner.id}
                  style={{
                    background: 'rgba(232,230,227,0.03)',
                    border: '1px solid rgba(232,230,227,0.1)',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onClick={() => setSelectedPartner(partner)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(249,115,22,0.3)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(232,230,227,0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #22d3ee 0%, #06b6d4 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      color: '#0a0e27'
                    }}>
                      {partner.name.charAt(0)}
                    </div>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, fontFamily: "'Inter', sans-serif" }}>
                        {partner.name}
                      </h4>
                      <p style={{ margin: 0, fontSize: '0.875rem', color: 'rgba(232,230,227,0.6)', fontFamily: "'Inter', sans-serif" }}>
                        {partner.category}
                      </p>
                    </div>
                  </div>
                  <p style={{
                    fontSize: '0.9375rem',
                    color: 'rgba(232,230,227,0.8)',
                    lineHeight: 1.5,
                    margin: '0 0 1rem 0',
                    fontFamily: "'Inter', sans-serif",
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {partner.description}
                  </p>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{
                      padding: '0.35rem 0.75rem',
                      background: 'rgba(34,211,238,0.15)',
                      borderRadius: '6px',
                      fontSize: '0.8125rem',
                      color: '#22d3ee',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      📧 {partner.emailSubscribers}
                    </span>
                    {partner.drScore !== 'N/A' && (
                      <span style={{
                        padding: '0.35rem 0.75rem',
                        background: 'rgba(168,85,247,0.15)',
                        borderRadius: '6px',
                        fontSize: '0.8125rem',
                        color: '#a855f7',
                        fontFamily: "'Inter', sans-serif"
                      }}>
                        DR {partner.drScore}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* My Profile Tab */}
      {activeTab === 'my-profile' && (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            margin: 0,
            fontFamily: "'Inter', sans-serif",
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <span style={{ fontSize: '1.5rem' }}>📊</span>
            My Reach Profile
          </h3>

          {/* Company Header */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(249,115,22,0.1) 0%, rgba(234,88,12,0.1) 100%)',
            border: '1px solid rgba(249,115,22,0.2)',
            borderRadius: '16px',
            padding: '2rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '1rem' }}>
              <div style={{
                width: '72px',
                height: '72px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2rem',
                fontWeight: 700,
                color: '#fff'
              }}>
                {currentUser.authData?.brandNameDBA?.charAt(0) || currentUser.authData?.companyLegalName?.charAt(0) || 'R'}
              </div>
              <div>
                <h2 style={{
                  fontSize: '1.75rem',
                  fontWeight: 700,
                  margin: '0 0 0.5rem 0',
                  fontFamily: "'Inter', sans-serif"
                }}>
                  {currentUser.authData?.brandNameDBA || currentUser.authData?.companyLegalName || 'Your Company'}
                </h2>
                <p style={{
                  fontSize: '1rem',
                  color: 'rgba(232,230,227,0.7)',
                  margin: 0,
                  fontFamily: "'Inter', sans-serif"
                }}>
                  Reach Partner • Advertiser
                </p>
              </div>
            </div>
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap'
            }}>
              {advertiserProfile?.primaryBusinessModel && (
                <span style={{
                  padding: '0.5rem 1rem',
                  background: 'rgba(249,115,22,0.2)',
                  borderRadius: '20px',
                  fontSize: '0.875rem',
                  fontFamily: "'Inter', sans-serif",
                  color: '#f97316',
                  fontWeight: 500
                }}>
                  {advertiserProfile.primaryBusinessModel}
                </span>
              )}
              {advertiserProfile?.roleAtCompany && (
                <span style={{
                  padding: '0.5rem 1rem',
                  background: 'rgba(232,230,227,0.1)',
                  borderRadius: '20px',
                  fontSize: '0.875rem',
                  fontFamily: "'Inter', sans-serif",
                  color: 'rgba(232,230,227,0.8)'
                }}>
                  👤 {advertiserProfile.roleAtCompany}
                </span>
              )}
            </div>
          </div>

          {/* Profile Details */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.5rem'
          }}>
            {/* Economics */}
            <div style={{
              background: 'rgba(232,230,227,0.03)',
              border: '1px solid rgba(232,230,227,0.1)',
              borderRadius: '12px',
              padding: '1.5rem'
            }}>
              <h4 style={{
                fontSize: '1rem',
                fontWeight: 700,
                margin: '0 0 1rem 0',
                fontFamily: "'Inter', sans-serif",
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span>💰</span> Economics
              </h4>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'rgba(232,230,227,0.6)', fontSize: '0.9375rem', fontFamily: "'Inter', sans-serif" }}>Target CAC</span>
                  <span style={{ fontWeight: 600, fontFamily: "'Inter', sans-serif" }}>{advertiserProfile?.targetCAC || 'Not set'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'rgba(232,230,227,0.6)', fontSize: '0.9375rem', fontFamily: "'Inter', sans-serif" }}>AOV</span>
                  <span style={{ fontWeight: 600, fontFamily: "'Inter', sans-serif" }}>{advertiserProfile?.averageOrderValue || 'Not set'}</span>
                </div>
              </div>
            </div>

            {/* Budget & Timeline */}
            <div style={{
              background: 'rgba(232,230,227,0.03)',
              border: '1px solid rgba(232,230,227,0.1)',
              borderRadius: '12px',
              padding: '1.5rem'
            }}>
              <h4 style={{
                fontSize: '1rem',
                fontWeight: 700,
                margin: '0 0 1rem 0',
                fontFamily: "'Inter', sans-serif",
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span>📅</span> Budget & Timeline
              </h4>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'rgba(232,230,227,0.6)', fontSize: '0.9375rem', fontFamily: "'Inter', sans-serif" }}>Starting Budget</span>
                  <span style={{ fontWeight: 600, fontFamily: "'Inter', sans-serif" }}>{advertiserProfile?.startingBudget || 'Not set'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'rgba(232,230,227,0.6)', fontSize: '0.9375rem', fontFamily: "'Inter', sans-serif" }}>Timeline</span>
                  <span style={{ fontWeight: 600, fontFamily: "'Inter', sans-serif" }}>{advertiserProfile?.launchTimeline || 'Not set'}</span>
                </div>
              </div>
            </div>

            {/* Placements */}
            <div style={{
              background: 'rgba(232,230,227,0.03)',
              border: '1px solid rgba(232,230,227,0.1)',
              borderRadius: '12px',
              padding: '1.5rem'
            }}>
              <h4 style={{
                fontSize: '1rem',
                fontWeight: 700,
                margin: '0 0 1rem 0',
                fontFamily: "'Inter', sans-serif",
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span>📍</span> Interested Placements
              </h4>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {(advertiserProfile?.interestedPlacements || []).map((placement, i) => (
                  <span key={i} style={{
                    padding: '0.35rem 0.75rem',
                    background: 'rgba(249,115,22,0.15)',
                    borderRadius: '6px',
                    fontSize: '0.8125rem',
                    color: '#f97316',
                    fontFamily: "'Inter', sans-serif"
                  }}>
                    {placement}
                  </span>
                ))}
                {(!advertiserProfile?.interestedPlacements || advertiserProfile.interestedPlacements.length === 0) && (
                  <span style={{ color: 'rgba(232,230,227,0.5)', fontSize: '0.9375rem', fontFamily: "'Inter', sans-serif" }}>
                    No placements selected
                  </span>
                )}
              </div>
            </div>

            {/* Brand Attributes */}
            <div style={{
              background: 'rgba(232,230,227,0.03)',
              border: '1px solid rgba(232,230,227,0.1)',
              borderRadius: '12px',
              padding: '1.5rem'
            }}>
              <h4 style={{
                fontSize: '1rem',
                fontWeight: 700,
                margin: '0 0 1rem 0',
                fontFamily: "'Inter', sans-serif",
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span>✨</span> Brand Attributes
              </h4>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {(advertiserProfile?.brandAttributes || []).map((attr, i) => (
                  <span key={i} style={{
                    padding: '0.35rem 0.75rem',
                    background: 'rgba(168,85,247,0.15)',
                    borderRadius: '6px',
                    fontSize: '0.8125rem',
                    color: '#a855f7',
                    fontFamily: "'Inter', sans-serif"
                  }}>
                    {attr}
                  </span>
                ))}
                {(!advertiserProfile?.brandAttributes || advertiserProfile.brandAttributes.length === 0) && (
                  <span style={{ color: 'rgba(232,230,227,0.5)', fontSize: '0.9375rem', fontFamily: "'Inter', sans-serif" }}>
                    No attributes selected
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Partner Detail Modal */}
      {selectedPartner && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }} onClick={() => setSelectedPartner(null)}>
          <div style={{
            background: '#1a1f3a',
            borderRadius: '20px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            padding: '2rem'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #22d3ee 0%, #06b6d4 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: '#0a0e27'
                }}>
                  {selectedPartner.name.charAt(0)}
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, fontFamily: "'Inter', sans-serif" }}>
                    {selectedPartner.name}
                  </h3>
                  <p style={{ margin: 0, color: 'rgba(232,230,227,0.6)', fontFamily: "'Inter', sans-serif" }}>
                    {selectedPartner.category}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedPartner(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(232,230,227,0.6)',
                  fontSize: '1.5rem',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>

            <p style={{ color: 'rgba(232,230,227,0.8)', lineHeight: 1.6, marginBottom: '1.5rem', fontFamily: "'Inter', sans-serif" }}>
              {selectedPartner.description}
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={{ background: 'rgba(232,230,227,0.05)', borderRadius: '8px', padding: '1rem' }}>
                <div style={{ fontSize: '0.875rem', color: 'rgba(232,230,227,0.6)', marginBottom: '0.25rem' }}>Email Subscribers</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{selectedPartner.emailSubscribers}</div>
              </div>
              <div style={{ background: 'rgba(232,230,227,0.05)', borderRadius: '8px', padding: '1rem' }}>
                <div style={{ fontSize: '0.875rem', color: 'rgba(232,230,227,0.6)', marginBottom: '0.25rem' }}>DR Score</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{selectedPartner.drScore}</div>
              </div>
            </div>

            {selectedPartner.placementTypes.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem', fontFamily: "'Inter', sans-serif" }}>Available Placements</h4>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {selectedPartner.placementTypes.map((type, i) => (
                    <span key={i} style={{
                      padding: '0.35rem 0.75rem',
                      background: 'rgba(34,211,238,0.15)',
                      borderRadius: '6px',
                      fontSize: '0.8125rem',
                      color: '#22d3ee',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      {type}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={() => {
                handleProposePartnership(selectedPartner);
                setSelectedPartner(null);
              }}
              style={{
                width: '100%',
                padding: '1rem',
                background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem',
                fontWeight: 600,
                fontFamily: "'Inter', sans-serif",
                cursor: 'pointer'
              }}
            >
              Propose Partnership →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================
// ADMIN DASHBOARD - For MavStream Team
// ============================================
function AdminDashboard({ setCurrentScreen }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPartnership, setSelectedPartnership] = useState(null);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [selectedPublisher, setSelectedPublisher] = useState(null);
  const [selectedAdvertiser, setSelectedAdvertiser] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0); // For forcing re-render
  const [matchNotes, setMatchNotes] = useState('');
  const [matchPlacementType, setMatchPlacementType] = useState('');
  const [matchBudget, setMatchBudget] = useState('');

  // Get real data from localStorage
  const rawPublishers = getAllPublishers();
  const rawAdvertisers = getAllAdvertisers();
  const partnerships = getAllPartnerships();
  const allUsers = getAllUsers();

  // Transform publisher data for display
  const publishers = rawPublishers.map((user, index) => ({
    id: `pub-${index + 1}`,
    visibleId: user.authData?.email || `pub-${index + 1}`,
    email: user.authData?.email || 'N/A',
    name: user.authData?.brandNameDBA || user.authData?.companyLegalName || 'Unnamed Company',
    companyLegalName: user.authData?.companyLegalName || '',
    description: user.publisherProfile?.companyDescription || 'No description provided',
    category: user.publisherProfile?.primaryCategory || 'Not specified',
    location: user.publisherProfile?.city && user.publisherProfile?.state 
      ? `${user.publisherProfile.city}, ${user.publisherProfile.state}` 
      : 'Not specified',
    phone: user.authData?.phone || '',
    website: user.authData?.website || '',
    signupDate: user.createdAt || new Date().toISOString(),
    status: 'active',
    emailSubscribers: user.publisherProfile?.listSize || 'N/A',
    engagedSubscribers: user.publisherProfile?.engagedSubscribers || 'N/A',
    monthlyVisitors: user.publisherProfile?.websiteVisitors || 'N/A',
    socialReach: user.publisherProfile?.socialFollowers || 'N/A',
    drScore: user.publisherProfile?.drScore || 'N/A',
    placementTypes: user.publisherProfile?.placementTypes || [],
    regions: user.publisherProfile?.regions || [],
    genderMix: user.publisherProfile?.genderMix || 'N/A',
    ageRanges: user.publisherProfile?.ageRanges || [],
    householdIncomes: user.publisherProfile?.householdIncomes || [],
    monthlyRevenue: user.publisherProfile?.monthlyRevenue || 'N/A',
    avgOrderValue: user.publisherProfile?.averageOrderValue || 'N/A',
    emailFrequency: user.publisherProfile?.campaignFrequency || 'N/A',
    primaryBusinessModels: user.publisherProfile?.primaryBusinessModels || [],
    maxPlacementsPerMonth: user.publisherProfile?.maxPlacementsPerMonth || 'N/A',
    activePartnerships: partnerships.filter(p => p.publisherEmail === user.authData?.email && p.status === 'active').length,
    totalEarnings: 0
  }));

  // Transform advertiser data for display
  const advertisers = rawAdvertisers.map((user, index) => ({
    id: `adv-${index + 1}`,
    email: user.authData?.email || 'N/A',
    name: user.authData?.brandNameDBA || user.authData?.companyLegalName || 'Unnamed Company',
    description: user.advertiserProfile?.companyDescription || 'No description provided',
    category: user.advertiserProfile?.primaryCategory || 'Not specified',
    location: user.advertiserProfile?.city && user.advertiserProfile?.state 
      ? `${user.advertiserProfile.city}, ${user.advertiserProfile.state}` 
      : 'Not specified',
    signupDate: user.createdAt || new Date().toISOString(),
    status: 'active',
    monthlyBudget: user.advertiserProfile?.monthlyBudget || 'Not specified',
    targetCategories: user.advertiserProfile?.targetCategories || [],
    campaignGoal: user.advertiserProfile?.campaignGoal || 'Not specified',
    activePartnerships: partnerships.filter(p => p.advertiserEmail === user.authData?.email && p.status === 'active').length,
    totalSpend: 0
  }));

  // Generate real activity feed from actual data
  const generateActivityFeed = () => {
    const activities = [];
    
    // Add publisher signups
    rawPublishers.forEach(user => {
      activities.push({
        type: 'publisher_signup',
        action: 'New publisher signed up',
        detail: user.authData?.brandNameDBA || user.authData?.companyLegalName || 'Unknown',
        email: user.authData?.email,
        timestamp: user.createdAt || new Date().toISOString(),
        icon: '📤'
      });
    });
    
    // Add advertiser signups
    rawAdvertisers.forEach(user => {
      activities.push({
        type: 'advertiser_signup',
        action: 'New advertiser signed up',
        detail: user.authData?.brandNameDBA || user.authData?.companyLegalName || 'Unknown',
        email: user.authData?.email,
        timestamp: user.createdAt || new Date().toISOString(),
        icon: '📥'
      });
    });
    
    // Add partnership activities
    partnerships.forEach(p => {
      let action = '';
      let icon = '🤝';
      
      if (p.status === 'proposed') {
        action = 'Partnership proposed';
        icon = '📨';
      } else if (p.status === 'accepted' || p.status === 'active') {
        action = 'Partnership accepted';
        icon = '✅';
      } else if (p.status === 'declined') {
        action = 'Partnership declined';
        icon = '❌';
      }
      
      if (action) {
        activities.push({
          type: 'partnership',
          action: action,
          detail: `${p.publisherName || 'Publisher'} ↔ ${p.advertiserName || 'Advertiser'}`,
          timestamp: p.updatedAt || p.createdAt || new Date().toISOString(),
          icon: icon,
          partnershipId: p.id
        });
      }
    });
    
    // Sort by timestamp (newest first)
    activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    
    return activities.slice(0, 10); // Return last 10 activities
  };
  
  const recentActivity = generateActivityFeed();
  
  // Format relative time
  const getRelativeTime = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return formatDate(timestamp);
  };

  // Calculate stats
  const stats = {
    totalPublishers: publishers.length,
    totalAdvertisers: advertisers.length,
    activePartnerships: partnerships.filter(p => p.status === 'active' || p.status === 'accepted').length,
    pendingApprovals: partnerships.filter(p => p.status === 'pending_approval' || p.status === 'proposed').length,
    totalRevenue: 0,
    monthlyGrowth: 0
  };

  // Handle creating a match with full details
  const handleCreateMatch = () => {
    if (!selectedPublisher || !selectedAdvertiser) return;
    
    const newPartnership = {
      publisherId: selectedPublisher.id,
      publisherName: selectedPublisher.name,
      publisherEmail: selectedPublisher.email,
      advertiserId: selectedAdvertiser.id,
      advertiserName: selectedAdvertiser.name,
      advertiserEmail: selectedAdvertiser.email,
      status: 'proposed',
      proposedBy: 'admin',
      placementType: matchPlacementType || 'To be determined',
      proposedBudget: matchBudget || 'To be negotiated',
      notes: matchNotes || `Match created by MavStream team`
    };
    
    savePartnership(newPartnership);
    alert(`Partnership proposed between ${selectedPublisher.name} and ${selectedAdvertiser.name}!\n\nBoth parties will be notified.`);
    setSelectedPublisher(null);
    setSelectedAdvertiser(null);
    setMatchNotes('');
    setMatchPlacementType('');
    setMatchBudget('');
    setRefreshKey(prev => prev + 1);
  };
  
  // Handle approving a partnership
  const handleApprovePartnership = (partnershipId) => {
    updatePartnershipStatus(partnershipId, 'active');
    alert('Partnership approved and activated!');
    setRefreshKey(prev => prev + 1);
  };
  
  // Handle declining a partnership
  const handleDeclinePartnership = (partnershipId) => {
    if (window.confirm('Are you sure you want to decline this partnership?')) {
      updatePartnershipStatus(partnershipId, 'declined');
      alert('Partnership declined.');
      setRefreshKey(prev => prev + 1);
    }
  };
  
  // Placement type options for matching
  const placementTypeOptions = [
    'Newsletter – Sponsored Section',
    'Promotional Email',
    'Homepage Placement',
    'Blog Placement',
    'Sponsored Story',
    'Sponsored Backlink',
    'Social Media Post',
    'Other'
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #2a1f3a 100%)',
      fontFamily: "'Crimson Pro', 'Fraunces', Georgia, serif",
      color: '#e8e6e3'
    }}>
      {/* Admin Header */}
      <header style={{
        borderBottom: '1px solid rgba(232,230,227,0.1)',
        background: 'rgba(10,14,39,0.95)',
        backdropFilter: 'blur(20px)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{
          maxWidth: '1600px',
          margin: '0 auto',
          padding: '1rem 2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <h1 style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              margin: 0,
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              MavStream
            </h1>
            <span style={{
              padding: '0.25rem 0.75rem',
              background: 'rgba(245,158,11,0.2)',
              border: '1px solid rgba(245,158,11,0.3)',
              borderRadius: '4px',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: '#f59e0b',
              fontFamily: "'Inter', sans-serif",
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Admin
            </span>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            {/* Export Buttons */}
            <button
              onClick={() => {
                const data = {
                  publishers: publishers,
                  advertisers: advertisers,
                  partnerships: partnerships,
                  exportedAt: new Date().toISOString(),
                  stats: stats
                };
                const blob = new Blob([JSON.stringify(data, null, 2)], {type: 'application/json'});
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `mavstream-export-${new Date().toISOString().split('T')[0]}.json`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              style={{
                padding: '0.5rem 1rem',
                background: 'rgba(168,85,247,0.15)',
                border: '1px solid rgba(168,85,247,0.3)',
                borderRadius: '6px',
                color: '#a855f7',
                fontSize: '0.875rem',
                fontFamily: "'Inter', sans-serif",
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              📥 Export JSON
            </button>
            <button
              onClick={() => {
                // Create CSV for publishers
                const headers = ['Company Name', 'Legal Name', 'Email', 'Phone', 'Website', 'Category', 'Location', 'Email Subscribers', 'Monthly Visitors', 'Social Reach', 'DR Score', 'Signup Date'];
                const rows = publishers.map(p => [
                  p.name,
                  p.companyLegalName,
                  p.email,
                  p.phone,
                  p.website,
                  p.category,
                  p.location,
                  p.emailSubscribers,
                  p.monthlyVisitors,
                  p.socialReach,
                  p.drScore,
                  formatDate(p.signupDate)
                ]);
                
                const csvContent = [headers, ...rows]
                  .map(row => row.map(cell => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(','))
                  .join('\n');
                
                const blob = new Blob([csvContent], {type: 'text/csv'});
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `mavstream-publishers-${new Date().toISOString().split('T')[0]}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              style={{
                padding: '0.5rem 1rem',
                background: 'rgba(16,185,129,0.15)',
                border: '1px solid rgba(16,185,129,0.3)',
                borderRadius: '6px',
                color: '#10b981',
                fontSize: '0.875rem',
                fontFamily: "'Inter', sans-serif",
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              📊 Export CSV
            </button>
            <button
              onClick={() => setCurrentScreen('auth')}
              style={{
                padding: '0.5rem 1rem',
                background: 'transparent',
                border: '1px solid rgba(232,230,227,0.2)',
                borderRadius: '6px',
                color: 'rgba(232,230,227,0.7)',
                fontSize: '0.875rem',
                fontFamily: "'Inter', sans-serif",
                cursor: 'pointer'
              }}
            >
              Exit Admin
            </button>
          </div>
        </div>
      </header>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '240px 1fr',
        minHeight: 'calc(100vh - 60px)'
      }}>
        {/* Sidebar */}
        <aside style={{
          background: 'rgba(232,230,227,0.02)',
          borderRight: '1px solid rgba(232,230,227,0.1)',
          padding: '1.5rem 1rem'
        }}>
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {[
              { id: 'overview', label: '📊 Overview', icon: '📊' },
              { id: 'publishers', label: '📤 Publishers (Earn)', icon: '📤' },
              { id: 'advertisers', label: '📥 Advertisers (Reach)', icon: '📥' },
              { id: 'partnerships', label: '🤝 Partnerships', icon: '🤝' },
              { id: 'matchmaking', label: '✨ Matchmaking', icon: '✨' },
              { id: 'creatives', label: '🎨 Creatives', icon: '🎨' },
              { id: 'approvals', label: '✅ Pending Approvals', icon: '✅' },
              { id: 'analytics', label: '📈 Analytics', icon: '📈' }
            ].map(item => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                style={{
                  padding: '0.75rem 1rem',
                  background: activeTab === item.id ? 'rgba(168,85,247,0.15)' : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: activeTab === item.id ? '#a855f7' : 'rgba(232,230,227,0.7)',
                  fontSize: '0.9375rem',
                  fontWeight: activeTab === item.id ? 600 : 500,
                  fontFamily: "'Inter', sans-serif",
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s ease'
                }}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main style={{ padding: '2rem', overflowY: 'auto' }}>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div>
              <h2 style={{ fontSize: '1.75rem', fontWeight: 700, margin: '0 0 2rem 0', fontFamily: "'Inter', sans-serif" }}>
                Dashboard Overview
              </h2>

              {/* Stats Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem'
              }}>
                {[
                  { label: 'Total Publishers', value: stats.totalPublishers, color: '#a855f7', icon: '📤' },
                  { label: 'Total Advertisers', value: stats.totalAdvertisers, color: '#ec4899', icon: '📥' },
                  { label: 'Active Partnerships', value: stats.activePartnerships, color: '#10b981', icon: '🤝' },
                  { label: 'Pending Approvals', value: stats.pendingApprovals, color: '#f59e0b', icon: '⏳' },
                  { label: 'Total Revenue', value: `$${stats.totalRevenue.toLocaleString()}`, color: '#3b82f6', icon: '💰' },
                  { label: 'Monthly Growth', value: `+${stats.monthlyGrowth}%`, color: '#10b981', icon: '📈' }
                ].map((stat, idx) => (
                  <div key={idx} style={{
                    background: 'rgba(232,230,227,0.03)',
                    border: '1px solid rgba(232,230,227,0.1)',
                    borderRadius: '12px',
                    padding: '1.25rem'
                  }}>
                    <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{stat.icon}</div>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(232,230,227,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem', fontFamily: "'Inter', sans-serif" }}>
                      {stat.label}
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: stat.color, fontFamily: "'Inter', sans-serif" }}>
                      {stat.value}
                    </div>
                  </div>
                ))}
              </div>

              {/* Recent Activity */}
              <div style={{
                background: 'rgba(232,230,227,0.03)',
                border: '1px solid rgba(232,230,227,0.1)',
                borderRadius: '12px',
                padding: '1.5rem'
              }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 700, margin: '0 0 1rem 0', fontFamily: "'Inter', sans-serif" }}>
                  Recent Activity
                </h3>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {recentActivity.length > 0 ? recentActivity.map((activity, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      padding: '0.75rem',
                      background: 'rgba(232,230,227,0.02)',
                      borderRadius: '8px'
                    }}>
                      <span style={{ fontSize: '1.25rem' }}>
                        {activity.icon}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.9375rem', fontFamily: "'Inter', sans-serif" }}>{activity.action}</div>
                        <div style={{ fontSize: '0.8125rem', color: 'rgba(232,230,227,0.6)', fontFamily: "'Inter', sans-serif" }}>{activity.detail}</div>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'rgba(232,230,227,0.5)', fontFamily: "'Inter', sans-serif" }}>{getRelativeTime(activity.timestamp)}</div>
                    </div>
                  )) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '2rem',
                      color: 'rgba(232,230,227,0.5)',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📭</div>
                      <p>No activity yet. Activity will appear here as users sign up and partnerships are created.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Publishers Tab */}
          {activeTab === 'publishers' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.75rem', fontWeight: 700, margin: 0, fontFamily: "'Inter', sans-serif" }}>
                  Publishers (Earn Side)
                </h2>
                <span style={{
                  padding: '0.5rem 1rem',
                  background: 'rgba(168,85,247,0.15)',
                  borderRadius: '20px',
                  fontSize: '0.875rem',
                  fontFamily: "'Inter', sans-serif",
                  color: '#a855f7'
                }}>
                  {publishers.length} Total
                </span>
              </div>

              <div style={{ display: 'grid', gap: '1rem' }}>
                {publishers.map(pub => (
                  <div key={pub.id} style={{
                    background: 'rgba(232,230,227,0.03)',
                    border: '1px solid rgba(232,230,227,0.1)',
                    borderRadius: '12px',
                    padding: '1.5rem'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '10px',
                          background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.25rem',
                          fontWeight: 700,
                          color: '#fff'
                        }}>
                          {pub.name.charAt(0)}
                        </div>
                        <div>
                          <h4 style={{ fontSize: '1.125rem', fontWeight: 700, margin: '0 0 0.25rem 0', fontFamily: "'Inter', sans-serif" }}>
                            {pub.name}
                          </h4>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <span style={{
                              fontSize: '0.75rem',
                              padding: '0.125rem 0.5rem',
                              background: 'rgba(168,85,247,0.15)',
                              borderRadius: '4px',
                              color: '#a855f7',
                              fontFamily: "'Inter', sans-serif"
                            }}>
                              {pub.category}
                            </span>
                            <span style={{
                              fontSize: '0.75rem',
                              padding: '0.125rem 0.5rem',
                              background: pub.status === 'active' ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)',
                              borderRadius: '4px',
                              color: pub.status === 'active' ? '#10b981' : '#f59e0b',
                              fontFamily: "'Inter', sans-serif"
                            }}>
                              {pub.status === 'active' ? 'Active' : 'Pending Review'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => setActiveTab('matchmaking')}
                        style={{
                          padding: '0.5rem 1rem',
                          background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '0.8125rem',
                          fontWeight: 600,
                          fontFamily: "'Inter', sans-serif",
                          cursor: 'pointer'
                        }}
                      >
                        Find Match
                      </button>
                    </div>

                    <p style={{ fontSize: '0.875rem', color: 'rgba(232,230,227,0.7)', margin: '0 0 1rem 0', fontFamily: "'Inter', sans-serif" }}>
                      {pub.description}
                    </p>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                      gap: '1rem',
                      padding: '1rem',
                      background: 'rgba(232,230,227,0.02)',
                      borderRadius: '8px'
                    }}>
                      <div>
                        <div style={{ fontSize: '0.6875rem', color: 'rgba(232,230,227,0.5)', textTransform: 'uppercase', fontFamily: "'Inter', sans-serif" }}>Email List</div>
                        <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#a855f7', fontFamily: "'Inter', sans-serif" }}>{pub.emailSubscribers}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.6875rem', color: 'rgba(232,230,227,0.5)', textTransform: 'uppercase', fontFamily: "'Inter', sans-serif" }}>Monthly Visitors</div>
                        <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#ec4899', fontFamily: "'Inter', sans-serif" }}>{pub.monthlyVisitors}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.6875rem', color: 'rgba(232,230,227,0.5)', textTransform: 'uppercase', fontFamily: "'Inter', sans-serif" }}>DR Score</div>
                        <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#10b981', fontFamily: "'Inter', sans-serif" }}>{pub.drScore}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.6875rem', color: 'rgba(232,230,227,0.5)', textTransform: 'uppercase', fontFamily: "'Inter', sans-serif" }}>Active Partnerships</div>
                        <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#3b82f6', fontFamily: "'Inter', sans-serif" }}>{pub.activePartnerships}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.6875rem', color: 'rgba(232,230,227,0.5)', textTransform: 'uppercase', fontFamily: "'Inter', sans-serif" }}>Total Earnings</div>
                        <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#f59e0b', fontFamily: "'Inter', sans-serif" }}>${pub.totalEarnings.toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Advertisers Tab */}
          {activeTab === 'advertisers' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.75rem', fontWeight: 700, margin: 0, fontFamily: "'Inter', sans-serif" }}>
                  Advertisers (Reach Side)
                </h2>
                <span style={{
                  padding: '0.5rem 1rem',
                  background: 'rgba(236,72,153,0.15)',
                  borderRadius: '20px',
                  fontSize: '0.875rem',
                  fontFamily: "'Inter', sans-serif",
                  color: '#ec4899'
                }}>
                  {advertisers.length} Total
                </span>
              </div>

              <div style={{ display: 'grid', gap: '1rem' }}>
                {advertisers.map(adv => (
                  <div key={adv.id} style={{
                    background: 'rgba(232,230,227,0.03)',
                    border: '1px solid rgba(232,230,227,0.1)',
                    borderRadius: '12px',
                    padding: '1.5rem'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '10px',
                          background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.25rem',
                          fontWeight: 700,
                          color: '#fff'
                        }}>
                          {adv.name.charAt(0)}
                        </div>
                        <div>
                          <h4 style={{ fontSize: '1.125rem', fontWeight: 700, margin: '0 0 0.25rem 0', fontFamily: "'Inter', sans-serif" }}>
                            {adv.name}
                          </h4>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <span style={{
                              fontSize: '0.75rem',
                              padding: '0.125rem 0.5rem',
                              background: 'rgba(236,72,153,0.15)',
                              borderRadius: '4px',
                              color: '#ec4899',
                              fontFamily: "'Inter', sans-serif"
                            }}>
                              {adv.category}
                            </span>
                            <span style={{
                              fontSize: '0.75rem',
                              padding: '0.125rem 0.5rem',
                              background: adv.status === 'active' ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)',
                              borderRadius: '4px',
                              color: adv.status === 'active' ? '#10b981' : '#f59e0b',
                              fontFamily: "'Inter', sans-serif"
                            }}>
                              {adv.status === 'active' ? 'Active' : 'Pending Review'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => setActiveTab('matchmaking')}
                        style={{
                          padding: '0.5rem 1rem',
                          background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 100%)',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '0.8125rem',
                          fontWeight: 600,
                          fontFamily: "'Inter', sans-serif",
                          cursor: 'pointer'
                        }}
                      >
                        Find Match
                      </button>
                    </div>

                    <p style={{ fontSize: '0.875rem', color: 'rgba(232,230,227,0.7)', margin: '0 0 1rem 0', fontFamily: "'Inter', sans-serif" }}>
                      {adv.description}
                    </p>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                      gap: '1rem',
                      padding: '1rem',
                      background: 'rgba(232,230,227,0.02)',
                      borderRadius: '8px'
                    }}>
                      <div>
                        <div style={{ fontSize: '0.6875rem', color: 'rgba(232,230,227,0.5)', textTransform: 'uppercase', fontFamily: "'Inter', sans-serif" }}>Monthly Budget</div>
                        <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#10b981', fontFamily: "'Inter', sans-serif" }}>{adv.monthlyBudget}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.6875rem', color: 'rgba(232,230,227,0.5)', textTransform: 'uppercase', fontFamily: "'Inter', sans-serif" }}>Campaign Goal</div>
                        <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#a855f7', fontFamily: "'Inter', sans-serif" }}>{adv.campaignGoal}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.6875rem', color: 'rgba(232,230,227,0.5)', textTransform: 'uppercase', fontFamily: "'Inter', sans-serif" }}>Target Categories</div>
                        <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#ec4899', fontFamily: "'Inter', sans-serif" }}>{adv.targetCategories.join(', ')}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.6875rem', color: 'rgba(232,230,227,0.5)', textTransform: 'uppercase', fontFamily: "'Inter', sans-serif" }}>Active Partnerships</div>
                        <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#3b82f6', fontFamily: "'Inter', sans-serif" }}>{adv.activePartnerships}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.6875rem', color: 'rgba(232,230,227,0.5)', textTransform: 'uppercase', fontFamily: "'Inter', sans-serif" }}>Total Spend</div>
                        <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#f59e0b', fontFamily: "'Inter', sans-serif" }}>${adv.totalSpend.toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Partnerships Tab */}
          {activeTab === 'partnerships' && (
            <div>
              <h2 style={{ fontSize: '1.75rem', fontWeight: 700, margin: '0 0 1.5rem 0', fontFamily: "'Inter', sans-serif" }}>
                All Partnerships
              </h2>

              <div style={{ display: 'grid', gap: '1rem' }}>
                {partnerships.map(ptr => (
                  <div key={ptr.id} style={{
                    background: 'rgba(232,230,227,0.03)',
                    border: '1px solid rgba(232,230,227,0.1)',
                    borderRadius: '12px',
                    padding: '1.5rem'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '8px',
                          background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1rem',
                          fontWeight: 700,
                          color: '#fff'
                        }}>
                          {ptr.publisher.name.charAt(0)}
                        </div>
                        <div style={{ fontSize: '1.25rem', color: 'rgba(232,230,227,0.5)' }}>↔</div>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '8px',
                          background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1rem',
                          fontWeight: 700,
                          color: '#fff'
                        }}>
                          {ptr.advertiser.name.charAt(0)}
                        </div>
                        <div>
                          <div style={{ fontSize: '1rem', fontWeight: 600, fontFamily: "'Inter', sans-serif" }}>
                            {ptr.publisher.name} × {ptr.advertiser.name}
                          </div>
                          <div style={{ fontSize: '0.8125rem', color: 'rgba(232,230,227,0.6)', fontFamily: "'Inter', sans-serif" }}>
                            {ptr.placementType}
                          </div>
                        </div>
                      </div>
                      <span style={{
                        padding: '0.375rem 0.75rem',
                        background: ptr.status === 'active' ? 'rgba(16,185,129,0.15)' : ptr.status === 'proposed' ? 'rgba(59,130,246,0.15)' : 'rgba(245,158,11,0.15)',
                        borderRadius: '20px',
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        color: ptr.status === 'active' ? '#10b981' : ptr.status === 'proposed' ? '#3b82f6' : '#f59e0b',
                        fontFamily: "'Inter', sans-serif",
                        textTransform: 'capitalize'
                      }}>
                        {ptr.status.replace('_', ' ')}
                      </span>
                    </div>

                    {ptr.notes && (
                      <p style={{ fontSize: '0.875rem', color: 'rgba(232,230,227,0.6)', margin: '0 0 1rem 0', fontFamily: "'Inter', sans-serif", fontStyle: 'italic' }}>
                        "{ptr.notes}"
                      </p>
                    )}

                    {ptr.performance && (
                      <div style={{
                        display: 'flex',
                        gap: '2rem',
                        padding: '0.75rem 1rem',
                        background: 'rgba(16,185,129,0.1)',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        fontFamily: "'Inter', sans-serif"
                      }}>
                        <span><strong>{ptr.performance.impressions.toLocaleString()}</strong> impressions</span>
                        <span><strong>{ptr.performance.clicks.toLocaleString()}</strong> clicks</span>
                        <span><strong>{ptr.performance.ctr}</strong> CTR</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Matchmaking Tab */}
          {activeTab === 'matchmaking' && (
            <div>
              <h2 style={{ fontSize: '1.75rem', fontWeight: 700, margin: '0 0 0.5rem 0', fontFamily: "'Inter', sans-serif" }}>
                ✨ Matchmaking Center
              </h2>
              <p style={{ fontSize: '1rem', color: 'rgba(232,230,227,0.6)', margin: '0 0 2rem 0', fontFamily: "'Inter', sans-serif" }}>
                Select a publisher and an advertiser to create a partnership proposal
              </p>

              {publishers.length === 0 && advertisers.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '4rem 2rem',
                  background: 'rgba(232,230,227,0.03)',
                  border: '1px solid rgba(232,230,227,0.1)',
                  borderRadius: '12px'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
                  <h3 style={{ margin: '0 0 0.5rem 0', fontFamily: "'Inter', sans-serif", color: 'rgba(232,230,227,0.8)' }}>No Partners to Match Yet</h3>
                  <p style={{ color: 'rgba(232,230,227,0.5)', fontFamily: "'Inter', sans-serif" }}>
                    Once publishers and advertisers sign up, you'll be able to create matches here.
                  </p>
                </div>
              ) : (
                <>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr',
                    gap: '2rem',
                    alignItems: 'start'
                  }}>
                    {/* Publisher Selection */}
                    <div>
                      <h3 style={{ fontSize: '1.125rem', fontWeight: 700, margin: '0 0 1rem 0', fontFamily: "'Inter', sans-serif", color: '#a855f7' }}>
                        📤 Select Publisher ({publishers.length})
                      </h3>
                      <div style={{ display: 'grid', gap: '0.75rem', maxHeight: '400px', overflowY: 'auto', paddingRight: '0.5rem' }}>
                        {publishers.length > 0 ? publishers.map(pub => (
                          <div
                            key={pub.id}
                            onClick={() => setSelectedPublisher(pub)}
                            style={{
                              padding: '1rem',
                              background: selectedPublisher?.id === pub.id ? 'rgba(168,85,247,0.15)' : 'rgba(232,230,227,0.03)',
                              border: selectedPublisher?.id === pub.id ? '2px solid #a855f7' : '1px solid rgba(232,230,227,0.1)',
                              borderRadius: '10px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <div style={{ fontWeight: 600, marginBottom: '0.25rem', fontFamily: "'Inter', sans-serif" }}>{pub.name}</div>
                            <div style={{ fontSize: '0.8125rem', color: 'rgba(232,230,227,0.6)', fontFamily: "'Inter', sans-serif" }}>{pub.category} • {pub.emailSubscribers} subs</div>
                            <div style={{ fontSize: '0.75rem', color: 'rgba(232,230,227,0.5)', fontFamily: "'Inter', sans-serif", marginTop: '0.25rem' }}>{pub.email}</div>
                          </div>
                        )) : (
                          <div style={{ padding: '1rem', color: 'rgba(232,230,227,0.5)', fontFamily: "'Inter', sans-serif", textAlign: 'center' }}>
                            No publishers yet
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Match Arrow */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      paddingTop: '4rem'
                    }}>
                      <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🤝</div>
                      <button
                        onClick={handleCreateMatch}
                        disabled={!selectedPublisher || !selectedAdvertiser}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: selectedPublisher && selectedAdvertiser ? 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)' : 'rgba(232,230,227,0.1)',
                          border: 'none',
                          borderRadius: '8px',
                          color: selectedPublisher && selectedAdvertiser ? '#fff' : 'rgba(232,230,227,0.4)',
                          fontSize: '0.9375rem',
                          fontWeight: 600,
                          fontFamily: "'Inter', sans-serif",
                          cursor: selectedPublisher && selectedAdvertiser ? 'pointer' : 'not-allowed'
                        }}
                      >
                        Create Match
                      </button>
                    </div>

                    {/* Advertiser Selection */}
                    <div>
                      <h3 style={{ fontSize: '1.125rem', fontWeight: 700, margin: '0 0 1rem 0', fontFamily: "'Inter', sans-serif", color: '#ec4899' }}>
                        📥 Select Advertiser ({advertisers.length})
                      </h3>
                      <div style={{ display: 'grid', gap: '0.75rem', maxHeight: '400px', overflowY: 'auto', paddingRight: '0.5rem' }}>
                        {advertisers.length > 0 ? advertisers.map(adv => (
                          <div
                            key={adv.id}
                            onClick={() => setSelectedAdvertiser(adv)}
                            style={{
                              padding: '1rem',
                              background: selectedAdvertiser?.id === adv.id ? 'rgba(236,72,153,0.15)' : 'rgba(232,230,227,0.03)',
                              border: selectedAdvertiser?.id === adv.id ? '2px solid #ec4899' : '1px solid rgba(232,230,227,0.1)',
                              borderRadius: '10px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <div style={{ fontWeight: 600, marginBottom: '0.25rem', fontFamily: "'Inter', sans-serif" }}>{adv.name}</div>
                            <div style={{ fontSize: '0.8125rem', color: 'rgba(232,230,227,0.6)', fontFamily: "'Inter', sans-serif" }}>{adv.category} • {adv.monthlyBudget}</div>
                            <div style={{ fontSize: '0.75rem', color: 'rgba(232,230,227,0.5)', fontFamily: "'Inter', sans-serif", marginTop: '0.25rem' }}>{adv.email}</div>
                          </div>
                        )) : (
                          <div style={{ padding: '1rem', color: 'rgba(232,230,227,0.5)', fontFamily: "'Inter', sans-serif", textAlign: 'center' }}>
                            No advertisers yet
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Match Details Form */}
                  {selectedPublisher && selectedAdvertiser && (
                    <div style={{
                      marginTop: '2rem',
                      padding: '1.5rem',
                      background: 'linear-gradient(135deg, rgba(168,85,247,0.1) 0%, rgba(236,72,153,0.1) 100%)',
                      border: '1px solid rgba(168,85,247,0.2)',
                      borderRadius: '12px'
                    }}>
                      <h4 style={{ fontSize: '1.125rem', fontWeight: 700, margin: '0 0 1.5rem 0', fontFamily: "'Inter', sans-serif" }}>
                        📝 Partnership Details
                      </h4>
                      
                      {/* Match Preview */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '1.5rem', fontSize: '0.9375rem', fontFamily: "'Inter', sans-serif" }}>
                        <div style={{ padding: '1rem', background: 'rgba(168,85,247,0.1)', borderRadius: '8px' }}>
                          <div style={{ color: '#a855f7', fontWeight: 600, marginBottom: '0.5rem' }}>📤 Publisher</div>
                          <div style={{ fontWeight: 600, fontSize: '1rem' }}>{selectedPublisher.name}</div>
                          <div style={{ color: 'rgba(232,230,227,0.7)', fontSize: '0.875rem' }}>{selectedPublisher.category}</div>
                          <div style={{ color: 'rgba(232,230,227,0.6)', fontSize: '0.8125rem', marginTop: '0.5rem' }}>
                            {selectedPublisher.emailSubscribers} subscribers • DR {selectedPublisher.drScore}
                          </div>
                        </div>
                        <div style={{ padding: '1rem', background: 'rgba(236,72,153,0.1)', borderRadius: '8px' }}>
                          <div style={{ color: '#ec4899', fontWeight: 600, marginBottom: '0.5rem' }}>📥 Advertiser</div>
                          <div style={{ fontWeight: 600, fontSize: '1rem' }}>{selectedAdvertiser.name}</div>
                          <div style={{ color: 'rgba(232,230,227,0.7)', fontSize: '0.875rem' }}>{selectedAdvertiser.category}</div>
                          <div style={{ color: 'rgba(232,230,227,0.6)', fontSize: '0.8125rem', marginTop: '0.5rem' }}>
                            {selectedAdvertiser.monthlyBudget} budget • {selectedAdvertiser.campaignGoal}
                          </div>
                        </div>
                      </div>
                      
                      {/* Partnership Configuration */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.375rem', fontFamily: "'Inter', sans-serif", color: 'rgba(232,230,227,0.8)' }}>
                            Placement Type
                          </label>
                          <select
                            value={matchPlacementType}
                            onChange={(e) => setMatchPlacementType(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '0.625rem 0.875rem',
                              background: 'rgba(232,230,227,0.05)',
                              border: '1px solid rgba(232,230,227,0.2)',
                              borderRadius: '6px',
                              color: '#e8e6e3',
                              fontSize: '0.875rem',
                              fontFamily: "'Inter', sans-serif"
                            }}
                          >
                            <option value="">Select placement type...</option>
                            {placementTypeOptions.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.375rem', fontFamily: "'Inter', sans-serif", color: 'rgba(232,230,227,0.8)' }}>
                            Proposed Budget
                          </label>
                          <input
                            type="text"
                            value={matchBudget}
                            onChange={(e) => setMatchBudget(e.target.value)}
                            placeholder="e.g., $500, $1,000/month"
                            style={{
                              width: '100%',
                              padding: '0.625rem 0.875rem',
                              background: 'rgba(232,230,227,0.05)',
                              border: '1px solid rgba(232,230,227,0.2)',
                              borderRadius: '6px',
                              color: '#e8e6e3',
                              fontSize: '0.875rem',
                              fontFamily: "'Inter', sans-serif"
                            }}
                          />
                        </div>
                      </div>
                      
                      <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.375rem', fontFamily: "'Inter', sans-serif", color: 'rgba(232,230,227,0.8)' }}>
                          Notes / Match Rationale
                        </label>
                        <textarea
                          value={matchNotes}
                          onChange={(e) => setMatchNotes(e.target.value)}
                          placeholder="Why is this a good match? Any special terms or considerations..."
                          rows={3}
                          style={{
                            width: '100%',
                            padding: '0.625rem 0.875rem',
                            background: 'rgba(232,230,227,0.05)',
                            border: '1px solid rgba(232,230,227,0.2)',
                            borderRadius: '6px',
                            color: '#e8e6e3',
                            fontSize: '0.875rem',
                            fontFamily: "'Inter', sans-serif",
                            resize: 'vertical'
                          }}
                        />
                      </div>
                      
                      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => {
                            setSelectedPublisher(null);
                            setSelectedAdvertiser(null);
                            setMatchNotes('');
                            setMatchPlacementType('');
                            setMatchBudget('');
                          }}
                          style={{
                            padding: '0.75rem 1.5rem',
                            background: 'transparent',
                            border: '1px solid rgba(232,230,227,0.2)',
                            borderRadius: '8px',
                            color: 'rgba(232,230,227,0.7)',
                            fontSize: '0.9375rem',
                            fontFamily: "'Inter', sans-serif",
                            cursor: 'pointer'
                          }}
                        >
                          Clear Selection
                        </button>
                        <button
                          onClick={handleCreateMatch}
                          style={{
                            padding: '0.75rem 2rem',
                            background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                            border: 'none',
                            borderRadius: '8px',
                            color: '#fff',
                            fontSize: '0.9375rem',
                            fontWeight: 600,
                            fontFamily: "'Inter', sans-serif",
                            cursor: 'pointer',
                            boxShadow: '0 4px 12px rgba(168,85,247,0.3)'
                          }}
                        >
                          🚀 Create Partnership
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Pending Approvals Tab */}
          {activeTab === 'approvals' && (
            <div>
              <h2 style={{ fontSize: '1.75rem', fontWeight: 700, margin: '0 0 1.5rem 0', fontFamily: "'Inter', sans-serif" }}>
                Pending Approvals
              </h2>

              {/* Pending Partnerships */}
              {partnerships.filter(p => p.status === 'proposed' || p.status === 'pending_approval').length > 0 ? (
                <div style={{ display: 'grid', gap: '1rem' }}>
                  {partnerships.filter(p => p.status === 'proposed' || p.status === 'pending_approval').map(ptr => (
                    <div key={ptr.id} style={{
                      background: 'rgba(232,230,227,0.03)',
                      border: '1px solid rgba(59,130,246,0.3)',
                      borderRadius: '12px',
                      padding: '1.5rem'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                            <span style={{ 
                              fontSize: '0.75rem', 
                              padding: '0.25rem 0.5rem',
                              background: ptr.proposedBy === 'admin' ? 'rgba(168,85,247,0.2)' : 'rgba(59,130,246,0.2)',
                              color: ptr.proposedBy === 'admin' ? '#a855f7' : '#3b82f6',
                              borderRadius: '4px',
                              fontFamily: "'Inter', sans-serif", 
                              textTransform: 'uppercase' 
                            }}>
                              {ptr.proposedBy === 'admin' ? '✨ Admin Match' : '📨 Partnership Proposal'}
                            </span>
                            <span style={{ fontSize: '0.75rem', color: 'rgba(232,230,227,0.5)', fontFamily: "'Inter', sans-serif" }}>
                              {getRelativeTime(ptr.createdAt)}
                            </span>
                          </div>
                          <h4 style={{ fontSize: '1.125rem', fontWeight: 700, margin: '0.25rem 0', fontFamily: "'Inter', sans-serif" }}>
                            {ptr.publisherName || 'Publisher'} ↔ {ptr.advertiserName || 'Advertiser'}
                          </h4>
                          <div style={{ fontSize: '0.875rem', color: 'rgba(232,230,227,0.7)', margin: '0.5rem 0', fontFamily: "'Inter', sans-serif" }}>
                            {ptr.placementType && <span>📍 {ptr.placementType}</span>}
                            {ptr.proposedBudget && <span style={{ marginLeft: '1rem' }}>💰 {ptr.proposedBudget}</span>}
                          </div>
                          {ptr.notes && (
                            <p style={{ fontSize: '0.8125rem', color: 'rgba(232,230,227,0.5)', margin: '0.5rem 0 0 0', fontFamily: "'Inter', sans-serif", fontStyle: 'italic' }}>
                              "{ptr.notes}"
                            </p>
                          )}
                          <div style={{ fontSize: '0.75rem', color: 'rgba(232,230,227,0.5)', marginTop: '0.5rem', fontFamily: "'Inter', sans-serif" }}>
                            Publisher: {ptr.publisherEmail} | Advertiser: {ptr.advertiserEmail}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button 
                            onClick={() => handleApprovePartnership(ptr.id)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: '#10b981',
                              border: 'none',
                              borderRadius: '6px',
                              color: '#fff',
                              fontSize: '0.8125rem',
                              fontWeight: 600,
                              fontFamily: "'Inter', sans-serif",
                              cursor: 'pointer'
                            }}
                          >
                            ✓ Approve
                          </button>
                          <button 
                            onClick={() => handleDeclinePartnership(ptr.id)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'transparent',
                              border: '1px solid rgba(239,68,68,0.5)',
                              borderRadius: '6px',
                              color: '#ef4444',
                              fontSize: '0.8125rem',
                              fontWeight: 600,
                              fontFamily: "'Inter', sans-serif",
                              cursor: 'pointer'
                            }}
                          >
                            ✗ Decline
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '4rem 2rem',
                  background: 'rgba(232,230,227,0.03)',
                  border: '1px solid rgba(232,230,227,0.1)',
                  borderRadius: '12px'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
                  <h3 style={{ margin: '0 0 0.5rem 0', fontFamily: "'Inter', sans-serif", color: 'rgba(232,230,227,0.8)' }}>All Caught Up!</h3>
                  <p style={{ color: 'rgba(232,230,227,0.5)', fontFamily: "'Inter', sans-serif" }}>
                    No pending approvals at the moment. New partnership proposals will appear here.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Analytics Tab - Placeholder */}
          {activeTab === 'analytics' && (
            <div style={{
              textAlign: 'center',
              padding: '4rem 2rem',
              color: 'rgba(232,230,227,0.5)',
              fontFamily: "'Inter', sans-serif"
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📈</div>
              <h3 style={{ margin: '0 0 0.5rem 0', color: 'rgba(232,230,227,0.7)' }}>Analytics Dashboard</h3>
              <p>Detailed analytics and reporting coming soon.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

// Dashboard Tab Components (same as before)
function OverviewTab({ data, publisherProfile, setShowPaymentModal, setPaymentModalType, setSelectedOpportunity }) {
  const [selectedTab, setSelectedTab] = React.useState('overview');
  const [selectedCreative, setSelectedCreative] = React.useState(null);
  const [feedbackText, setFeedbackText] = React.useState('');
  const [refreshKey, setRefreshKey] = React.useState(0);
  
  // Get real opportunities/partnerships for this publisher from localStorage
  const currentUser = JSON.parse(localStorage.getItem('mavstream_user') || '{}');
  const currentUserEmail = currentUser.authData?.email || '';
  
  const allPartnerships = getAllPartnerships();
  
  // Filter partnerships relevant to this publisher
  const myPartnerships = allPartnerships.filter(p => 
    p.publisherEmail === currentUserEmail || 
    p.publisherName === data.name
  );
  
  // Get pending creatives for this publisher
  const allCreatives = getAllCreatives();
  const pendingCreatives = allCreatives.filter(c => 
    (c.publisherEmail === currentUserEmail || c.publisherName === data.name) && 
    c.status === 'pending'
  );
  const allMyCreatives = allCreatives.filter(c => 
    c.publisherEmail === currentUserEmail || c.publisherName === data.name
  );
  
  // Transform to opportunities format
  const realOpportunities = myPartnerships.map((p, idx) => ({
    id: p.id || idx + 1,
    brand: p.advertiserName || 'Unknown Brand',
    type: p.placementType || 'Partnership',
    cpm: p.proposedBudget || 0,
    reach: 'TBD',
    deadline: p.createdAt ? formatDate(p.createdAt) : 'N/A',
    status: p.status || 'proposed',
    notes: p.notes || ''
  }));

  // Use real opportunities if any, otherwise show sample
  const opportunities = realOpportunities.length > 0 ? realOpportunities : [
    {
      id: 0,
      brand: 'No Opportunities Yet',
      type: 'N/A',
      cpm: 0,
      reach: 'N/A',
      deadline: 'N/A',
      status: 'waiting',
      notes: 'Partnership opportunities from advertisers will appear here'
    }
  ];

  // Handle accepting an opportunity
  const handleAcceptOpportunity = (opp) => {
    if (opp.id === 0) return; // Placeholder
    updatePartnershipStatus(opp.id, 'accepted');
    alert(`You've accepted the partnership with ${opp.brand}! They will be notified.`);
  };

  // Handle declining an opportunity
  const handleDeclineOpportunity = (opp) => {
    if (opp.id === 0) return; // Placeholder
    updatePartnershipStatus(opp.id, 'declined');
    alert(`You've declined the partnership with ${opp.brand}.`);
  };

  return (
    <div style={{
      animation: 'fadeIn 0.6s ease-out'
    }}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Overview Card */}
      <div style={{
        background: 'rgba(232,230,227,0.03)',
        border: '1px solid rgba(232,230,227,0.1)',
        borderRadius: '16px',
        padding: '2rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '1.5rem'
        }}>
          <span style={{ fontSize: '1.5rem' }}>💰</span>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            margin: 0,
            fontFamily: "'Inter', sans-serif"
          }}>
            Overview
          </h2>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: '2rem',
          marginBottom: '0.5rem'
        }}>
          <div>
            <div style={{
              fontSize: '0.875rem',
              color: 'rgba(232,230,227,0.6)',
              marginBottom: '0.5rem',
              fontFamily: "'Inter', sans-serif"
            }}>
              This Month
            </div>
            <div style={{
              fontSize: '2rem',
              fontWeight: 700,
              color: '#10b981',
              fontFamily: "'Inter', sans-serif"
            }}>
              ${data.earnings?.thisMonth || 0}
            </div>
          </div>

          <div>
            <div style={{
              fontSize: '0.875rem',
              color: 'rgba(232,230,227,0.6)',
              marginBottom: '0.5rem',
              fontFamily: "'Inter', sans-serif"
            }}>
              Total Earnings
            </div>
            <div style={{
              fontSize: '2rem',
              fontWeight: 700,
              color: '#3b82f6',
              fontFamily: "'Inter', sans-serif"
            }}>
              ${data.earnings?.total || 0}
            </div>
          </div>

          <div>
            <div style={{
              fontSize: '0.875rem',
              color: 'rgba(232,230,227,0.6)',
              marginBottom: '0.5rem',
              fontFamily: "'Inter', sans-serif"
            }}>
              Available
            </div>
            <div style={{
              fontSize: '2rem',
              fontWeight: 700,
              color: '#a855f7',
              fontFamily: "'Inter', sans-serif"
            }}>
              {opportunities.filter(o => o.id !== 0).length} Opportunities
            </div>
          </div>
        </div>

        {/* YTD / Historical Earnings Toggle */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '1.5rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid rgba(232,230,227,0.1)'
        }}>
          <button style={{
            padding: '0.5rem 1rem',
            background: 'transparent',
            border: 'none',
            color: '#a855f7',
            fontSize: '0.875rem',
            fontWeight: 600,
            fontFamily: "'Inter', sans-serif",
            cursor: 'pointer',
            textDecoration: 'underline',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#ec4899';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#a855f7';
          }}>
            YTD Earnings
          </button>
          
          <span style={{
            color: 'rgba(232,230,227,0.3)'
          }}>|</span>
          
          <button style={{
            padding: '0.5rem 1rem',
            background: 'transparent',
            border: 'none',
            color: 'rgba(232,230,227,0.6)',
            fontSize: '0.875rem',
            fontWeight: 600,
            fontFamily: "'Inter', sans-serif",
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#a855f7';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'rgba(232,230,227,0.6)';
          }}>
            Historical Earnings
          </button>
        </div>

        <div style={{
          display: 'flex',
          gap: '1rem'
        }}>
          <button 
            onClick={() => setSelectedTab('opportunities')}
            style={{
            padding: '0.75rem 1.5rem',
            background: 'transparent',
            border: '1px solid rgba(168,85,247,0.5)',
            borderRadius: '8px',
            color: '#a855f7',
            fontSize: '0.9375rem',
            fontWeight: 600,
            fontFamily: "'Inter', sans-serif",
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(168,85,247,0.1)';
            e.currentTarget.style.borderColor = '#a855f7';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = 'rgba(168,85,247,0.5)';
          }}>
            View Opportunities
          </button>

          <button 
            onClick={() => setSelectedTab('ad-options')}
            style={{
            padding: '0.75rem 1.5rem',
            background: 'transparent',
            border: '1px solid rgba(236,72,153,0.5)',
            borderRadius: '8px',
            color: '#ec4899',
            fontSize: '0.9375rem',
            fontWeight: 600,
            fontFamily: "'Inter', sans-serif",
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(236,72,153,0.1)';
            e.currentTarget.style.borderColor = '#ec4899';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = 'rgba(236,72,153,0.5)';
          }}>
            Update Ad Options
          </button>
        </div>
      </div>

      {/* Below the Fold Divider */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        margin: '3rem 0 2rem 0'
      }}>
        <div style={{ fontSize: '1.5rem' }}>💰</div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: '0.9375rem',
            fontWeight: 600,
            color: '#3b82f6',
            marginBottom: '0.25rem',
            fontFamily: "'Inter', sans-serif"
          }}>
            Payment Processing Coming Soon!
          </div>
          <div style={{
            fontSize: '0.875rem',
            color: 'rgba(232,230,227,0.7)',
            fontFamily: "'Inter', sans-serif"
          }}>
            Connect your bank account when we launch payment processing. You'll be notified via email.
          </div>
        </div>
      </div>

      {/* Main Content Area with Left Nav */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '240px 1fr',
        gap: '2rem'
      }}>
        {/* Left Navigation */}
        <div>

          <nav style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem'
          }}>
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'my-profile', label: 'My Profile' },
              { id: 'opportunities', label: 'Opportunities' },
              { id: 'approvals', label: `Approvals${pendingCreatives.length > 0 ? ` (${pendingCreatives.length})` : ''}` },
              { id: 'history', label: 'Campaign History' },
              { id: 'payouts', label: 'Payouts' }
            ].map(item => (
              <button
                key={item.id}
                onClick={() => setSelectedTab(item.id)}
                style={{
                  padding: '0.75rem 1rem',
                  background: selectedTab === item.id ? 'rgba(168,85,247,0.15)' : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: selectedTab === item.id ? '#a855f7' : 'rgba(232,230,227,0.7)',
                  fontSize: '0.9375rem',
                  fontWeight: selectedTab === item.id ? 600 : 500,
                  fontFamily: "'Inter', sans-serif",
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (selectedTab !== item.id) {
                    e.currentTarget.style.background = 'rgba(232,230,227,0.05)';
                    e.currentTarget.style.color = '#e8e6e3';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedTab !== item.id) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'rgba(232,230,227,0.7)';
                  }
                }}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content Area */}
        <div>
          {selectedTab === 'opportunities' && (
            <div>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                margin: '0 0 1.5rem 0',
                fontFamily: "'Inter', sans-serif"
              }}>
                Available Opportunities
              </h3>

              <div style={{
                display: 'grid',
                gap: '1rem'
              }}>
                {opportunities.map(opp => (
                  <div key={opp.id} style={{
                    background: 'rgba(232,230,227,0.03)',
                    border: '1px solid rgba(232,230,227,0.1)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(168,85,247,0.3)';
                    e.currentTarget.style.background = 'rgba(232,230,227,0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(232,230,227,0.1)';
                    e.currentTarget.style.background = 'rgba(232,230,227,0.03)';
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '1rem'
                    }}>
                      <div>
                        <div style={{
                          fontSize: '1.125rem',
                          fontWeight: 600,
                          marginBottom: '0.5rem',
                          fontFamily: "'Inter', sans-serif"
                        }}>
                          {opp.brand} ({opp.type})
                        </div>
                        <div style={{
                          display: 'flex',
                          gap: '1.5rem',
                          fontSize: '0.9375rem',
                          color: 'rgba(232,230,227,0.7)',
                          fontFamily: "'Inter', sans-serif"
                        }}>
                          <span style={{ color: '#10b981', fontWeight: 600 }}>
                            ${opp.cpm} CPM
                          </span>
                          <span>
                            {opp.reach} reach
                          </span>
                        </div>
                      </div>

                      <div style={{
                        fontSize: '0.875rem',
                        color: 'rgba(232,230,227,0.6)',
                        fontFamily: "'Inter', sans-serif"
                      }}>
                        Send by {opp.deadline}
                      </div>
                    </div>

                    <div style={{
                      display: 'flex',
                      gap: '1rem'
                    }}>
                      <button style={{
                        padding: '0.5rem 1rem',
                        background: 'transparent',
                        border: '1px solid rgba(168,85,247,0.5)',
                        borderRadius: '6px',
                        color: '#a855f7',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        fontFamily: "'Inter', sans-serif",
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(168,85,247,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}>
                        View
                      </button>

                      <button
                        onClick={() => {
                          handleAcceptOpportunity(opp);
                          setSelectedOpportunity(opp);
                          setPaymentModalType('accept-opportunity');
                          setShowPaymentModal(true);
                        }}
                        disabled={opp.id === 0}
                        style={{
                        padding: '0.5rem 1rem',
                        background: opp.id === 0 ? 'rgba(232,230,227,0.1)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        border: 'none',
                        borderRadius: '6px',
                        color: opp.id === 0 ? 'rgba(232,230,227,0.4)' : '#fff',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        fontFamily: "'Inter', sans-serif",
                        cursor: opp.id === 0 ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (opp.id !== 0) {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(16,185,129,0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}>
                        Accept
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedTab === 'my-profile' && (
            <div>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                margin: '0 0 1.5rem 0',
                fontFamily: "'Inter', sans-serif"
              }}>
                My Profile
              </h3>

              {/* Company Header */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(168,85,247,0.1) 0%, rgba(236,72,153,0.1) 100%)',
                border: '1px solid rgba(168,85,247,0.2)',
                borderRadius: '16px',
                padding: '2rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '1rem' }}>
                  <div style={{
                    width: '72px',
                    height: '72px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2rem',
                    fontWeight: 700,
                    color: '#fff'
                  }}>
                    {data.name?.charAt(0) || 'C'}
                  </div>
                  <div>
                    <h2 style={{
                      fontSize: '1.75rem',
                      fontWeight: 700,
                      margin: '0 0 0.5rem 0',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      {data.name || 'Your Company'}
                    </h2>
                    <p style={{
                      fontSize: '1rem',
                      color: 'rgba(232,230,227,0.7)',
                      margin: 0,
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      {publisherProfile?.companyDescription || 'No description provided'}
                    </p>
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  gap: '0.5rem',
                  flexWrap: 'wrap'
                }}>
                  {(publisherProfile?.primaryCategories?.length > 0 
                    ? publisherProfile.primaryCategories 
                    : (publisherProfile?.primaryCategory ? [publisherProfile.primaryCategory] : ['Category not set'])
                  ).map((cat, idx) => (
                    <span key={idx} style={{
                      padding: '0.5rem 1rem',
                      background: 'rgba(168,85,247,0.2)',
                      borderRadius: '20px',
                      fontSize: '0.875rem',
                      fontFamily: "'Inter', sans-serif",
                      color: '#a855f7',
                      fontWeight: 500
                    }}>
                      {cat}
                    </span>
                  ))}
                  <span style={{
                    padding: '0.5rem 1rem',
                    background: 'rgba(232,230,227,0.1)',
                    borderRadius: '20px',
                    fontSize: '0.875rem',
                    fontFamily: "'Inter', sans-serif",
                    color: 'rgba(232,230,227,0.8)'
                  }}>
                    📍 {publisherProfile?.city && publisherProfile?.state ? `${publisherProfile.city}, ${publisherProfile.state}` : 'Location not set'}
                  </span>
                </div>
              </div>

              {/* Reach Snapshot */}
              <div style={{
                background: 'rgba(232,230,227,0.03)',
                border: '1px solid rgba(232,230,227,0.1)',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '1.5rem'
              }}>
                <h4 style={{
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  margin: '0 0 1.25rem 0',
                  fontFamily: "'Inter', sans-serif",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  📊 Reach Snapshot
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '1rem'
                }}>
                  <div style={{
                    background: 'rgba(232,230,227,0.05)',
                    borderRadius: '8px',
                    padding: '1rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(232,230,227,0.5)', marginBottom: '0.25rem', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Email Subscribers
                    </div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#a855f7', fontFamily: "'Inter', sans-serif" }}>
                      {publisherProfile?.listSize || '—'}
                    </div>
                  </div>
                  <div style={{
                    background: 'rgba(232,230,227,0.05)',
                    borderRadius: '8px',
                    padding: '1rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(232,230,227,0.5)', marginBottom: '0.25rem', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Monthly Visitors
                    </div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#ec4899', fontFamily: "'Inter', sans-serif" }}>
                      {publisherProfile?.websiteVisitors || '—'}
                    </div>
                  </div>
                  <div style={{
                    background: 'rgba(232,230,227,0.05)',
                    borderRadius: '8px',
                    padding: '1rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(232,230,227,0.5)', marginBottom: '0.25rem', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Social Reach
                    </div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#3b82f6', fontFamily: "'Inter', sans-serif" }}>
                      {publisherProfile?.socialFollowers || '—'}
                    </div>
                  </div>
                  <div style={{
                    background: 'rgba(232,230,227,0.05)',
                    borderRadius: '8px',
                    padding: '1rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(232,230,227,0.5)', marginBottom: '0.25rem', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      DR Score
                    </div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#10b981', fontFamily: "'Inter', sans-serif" }}>
                      {publisherProfile?.drScore || '—'}
                    </div>
                  </div>
                  <div style={{
                    background: 'rgba(232,230,227,0.05)',
                    borderRadius: '8px',
                    padding: '1rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(232,230,227,0.5)', marginBottom: '0.25rem', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Engaged Subscribers
                    </div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#f59e0b', fontFamily: "'Inter', sans-serif" }}>
                      {publisherProfile?.engagedSubscribers || '—'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Placement Types Available */}
              <div style={{
                background: 'rgba(232,230,227,0.03)',
                border: '1px solid rgba(232,230,227,0.1)',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '1.5rem'
              }}>
                <h4 style={{
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  margin: '0 0 1.25rem 0',
                  fontFamily: "'Inter', sans-serif",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  📢 Placement Types Available
                </h4>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.5rem'
                }}>
                  {publisherProfile?.placementTypes?.length > 0 ? (
                    publisherProfile.placementTypes
                      .filter(t => !t.includes('-na'))
                      .map(type => {
                        const labels = {
                          'newsletter-sponsored': 'Newsletter – Sponsored Section',
                          'promotional-email': 'Promotional Email',
                          'weekly-update': 'Weekly Update Email',
                          'monthly-email': 'Monthly Email',
                          'post-purchase': 'Post-Purchase Email',
                          'homepage-placement': 'Homepage Placement',
                          'content-page': 'Content Page',
                          'blog-placement': 'Blog Placement',
                          'other-website': 'Other Website',
                          'sponsored-story': 'Sponsored Story',
                          'sponsored-feed': 'Sponsored Feed Post',
                          'pinned-post': 'Pinned Post',
                          'sponsored-backlink': 'Sponsored Backlink',
                          'recommended-partner': 'Recommended Partner Link',
                          'order-box-insert': 'Order Box Insert',
                          'packaging-other': 'Other Packaging'
                        };
                        return (
                          <span key={type} style={{
                            padding: '0.375rem 0.75rem',
                            background: 'rgba(168,85,247,0.15)',
                            borderRadius: '6px',
                            fontSize: '0.8125rem',
                            fontFamily: "'Inter', sans-serif",
                            color: 'rgba(232,230,227,0.9)'
                          }}>
                            {labels[type] || type}
                          </span>
                        );
                      })
                  ) : (
                    <span style={{ color: 'rgba(232,230,227,0.5)', fontFamily: "'Inter', sans-serif", fontSize: '0.9375rem' }}>
                      No placement types selected
                    </span>
                  )}
                </div>
              </div>

              {/* Audience Demographics */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '1.5rem',
                marginBottom: '1.5rem'
              }}>
                {/* Primary Geography */}
                <div style={{
                  background: 'rgba(232,230,227,0.03)',
                  border: '1px solid rgba(232,230,227,0.1)',
                  borderRadius: '12px',
                  padding: '1.5rem'
                }}>
                  <h4 style={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    margin: '0 0 1rem 0',
                    fontFamily: "'Inter', sans-serif",
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    🌍 Primary Geography
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {publisherProfile?.regions?.length > 0 ? (
                      publisherProfile.regions.map(region => (
                        <span key={region} style={{
                          padding: '0.375rem 0.75rem',
                          background: 'rgba(59,130,246,0.15)',
                          borderRadius: '6px',
                          fontSize: '0.8125rem',
                          fontFamily: "'Inter', sans-serif",
                          color: 'rgba(232,230,227,0.9)'
                        }}>
                          {region}
                        </span>
                      ))
                    ) : (
                      <span style={{ color: 'rgba(232,230,227,0.5)', fontFamily: "'Inter', sans-serif", fontSize: '0.875rem' }}>Not specified</span>
                    )}
                  </div>
                </div>

                {/* Audience Demographics */}
                <div style={{
                  background: 'rgba(232,230,227,0.03)',
                  border: '1px solid rgba(232,230,227,0.1)',
                  borderRadius: '12px',
                  padding: '1.5rem'
                }}>
                  <h4 style={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    margin: '0 0 1rem 0',
                    fontFamily: "'Inter', sans-serif",
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    👥 Audience Demographics
                  </h4>
                  <div style={{ display: 'grid', gap: '0.75rem', fontSize: '0.875rem', fontFamily: "'Inter', sans-serif" }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'rgba(232,230,227,0.6)' }}>Gender Mix:</span>
                      <span style={{ color: 'rgba(232,230,227,0.9)', fontWeight: 500 }}>{publisherProfile?.genderMix || '—'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'rgba(232,230,227,0.6)' }}>Age Ranges:</span>
                      <span style={{ color: 'rgba(232,230,227,0.9)', fontWeight: 500 }}>{publisherProfile?.ageRanges?.join(', ') || '—'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'rgba(232,230,227,0.6)' }}>HH Income:</span>
                      <span style={{ color: 'rgba(232,230,227,0.9)', fontWeight: 500 }}>{publisherProfile?.householdIncomes?.join(', ') || '—'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer Affinities */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '1.5rem',
                marginBottom: '1.5rem'
              }}>
                {/* Customer Interests */}
                <div style={{
                  background: 'rgba(232,230,227,0.03)',
                  border: '1px solid rgba(232,230,227,0.1)',
                  borderRadius: '12px',
                  padding: '1.5rem'
                }}>
                  <h4 style={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    margin: '0 0 1rem 0',
                    fontFamily: "'Inter', sans-serif",
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    💡 Customer Interests
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {publisherProfile?.customerInterests?.length > 0 ? (
                      publisherProfile.customerInterests.map(interest => (
                        <span key={interest} style={{
                          padding: '0.375rem 0.75rem',
                          background: 'rgba(168,85,247,0.15)',
                          borderRadius: '6px',
                          fontSize: '0.8125rem',
                          fontFamily: "'Inter', sans-serif",
                          color: 'rgba(232,230,227,0.9)'
                        }}>
                          {interest}
                        </span>
                      ))
                    ) : (
                      <span style={{ color: 'rgba(232,230,227,0.5)', fontFamily: "'Inter', sans-serif", fontSize: '0.875rem' }}>Not specified</span>
                    )}
                  </div>
                </div>

                {/* Brand Attributes */}
                <div style={{
                  background: 'rgba(232,230,227,0.03)',
                  border: '1px solid rgba(232,230,227,0.1)',
                  borderRadius: '12px',
                  padding: '1.5rem'
                }}>
                  <h4 style={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    margin: '0 0 1rem 0',
                    fontFamily: "'Inter', sans-serif",
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    ✨ Brand Attributes
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {publisherProfile?.brandAttributes?.length > 0 ? (
                      publisherProfile.brandAttributes.map(attr => (
                        <span key={attr} style={{
                          padding: '0.375rem 0.75rem',
                          background: 'rgba(236,72,153,0.15)',
                          borderRadius: '6px',
                          fontSize: '0.8125rem',
                          fontFamily: "'Inter', sans-serif",
                          color: 'rgba(232,230,227,0.9)'
                        }}>
                          {attr}
                        </span>
                      ))
                    ) : (
                      <span style={{ color: 'rgba(232,230,227,0.5)', fontFamily: "'Inter', sans-serif", fontSize: '0.875rem' }}>Not specified</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Products Customers Buy */}
              {publisherProfile?.customerProducts?.length > 0 && !publisherProfile.customerProducts.includes('N/A') && (
                <div style={{
                  background: 'rgba(232,230,227,0.03)',
                  border: '1px solid rgba(232,230,227,0.1)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  marginBottom: '1.5rem'
                }}>
                  <h4 style={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    margin: '0 0 1rem 0',
                    fontFamily: "'Inter', sans-serif",
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    🛒 Products Customers Buy
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {publisherProfile.customerProducts.map(product => (
                      <span key={product} style={{
                        padding: '0.375rem 0.75rem',
                        background: 'rgba(16,185,129,0.15)',
                        borderRadius: '6px',
                        fontSize: '0.8125rem',
                        fontFamily: "'Inter', sans-serif",
                        color: 'rgba(232,230,227,0.9)'
                      }}>
                        {product}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Business Details */}
              <div style={{
                background: 'rgba(232,230,227,0.03)',
                border: '1px solid rgba(232,230,227,0.1)',
                borderRadius: '12px',
                padding: '1.5rem'
              }}>
                <h4 style={{
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  margin: '0 0 1.25rem 0',
                  fontFamily: "'Inter', sans-serif",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  💼 Business Details
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  fontSize: '0.9375rem',
                  fontFamily: "'Inter', sans-serif"
                }}>
                  <div>
                    <div style={{ color: 'rgba(232,230,227,0.5)', marginBottom: '0.25rem', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Business Model</div>
                    <div style={{ color: 'rgba(232,230,227,0.9)' }}>{publisherProfile?.primaryBusinessModels?.join(', ') || '—'}</div>
                  </div>
                  <div>
                    <div style={{ color: 'rgba(232,230,227,0.5)', marginBottom: '0.25rem', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Monthly Revenue</div>
                    <div style={{ color: 'rgba(232,230,227,0.9)' }}>{publisherProfile?.monthlyRevenue || '—'}</div>
                  </div>
                  <div>
                    <div style={{ color: 'rgba(232,230,227,0.5)', marginBottom: '0.25rem', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Avg Order Value</div>
                    <div style={{ color: 'rgba(232,230,227,0.9)' }}>{publisherProfile?.averageOrderValue || '—'}</div>
                  </div>
                  <div>
                    <div style={{ color: 'rgba(232,230,227,0.5)', marginBottom: '0.25rem', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email Frequency</div>
                    <div style={{ color: 'rgba(232,230,227,0.9)' }}>{publisherProfile?.campaignFrequency || '—'}</div>
                  </div>
                  <div>
                    <div style={{ color: 'rgba(232,230,227,0.5)', marginBottom: '0.25rem', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Max Placements/Month</div>
                    <div style={{ color: 'rgba(232,230,227,0.9)' }}>{publisherProfile?.maxPlacementsPerMonth || '—'}</div>
                  </div>
                  <div>
                    <div style={{ color: 'rgba(232,230,227,0.5)', marginBottom: '0.25rem', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Creative Approval</div>
                    <div style={{ color: 'rgba(232,230,227,0.9)' }}>{publisherProfile?.creativeApproval || '—'}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {selectedTab === 'overview' && (
            <div style={{ display: 'grid', gap: '1.5rem' }}>
              {/* Earnings Snapshot */}
              <div style={{
                background: 'rgba(232,230,227,0.03)',
                border: '1px solid rgba(232,230,227,0.1)',
                borderRadius: '12px',
                padding: '1.5rem'
              }}>
                <h3 style={{
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  margin: '0 0 1.5rem 0',
                  fontFamily: "'Inter', sans-serif",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span>💰</span> Earnings Snapshot
                </h3>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: '2rem'
                }}>
                  <div>
                    <div style={{
                      fontSize: '0.875rem',
                      color: 'rgba(232,230,227,0.6)',
                      marginBottom: '0.5rem',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      This Month
                    </div>
                    <div style={{
                      fontSize: '1.75rem',
                      fontWeight: 700,
                      color: '#10b981',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      $1,240
                    </div>
                  </div>

                  <div>
                    <div style={{
                      fontSize: '0.875rem',
                      color: 'rgba(232,230,227,0.6)',
                      marginBottom: '0.5rem',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      YTD
                    </div>
                    <div style={{
                      fontSize: '1.75rem',
                      fontWeight: 700,
                      color: '#3b82f6',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      $6,480
                    </div>
                  </div>

                  <div>
                    <div style={{
                      fontSize: '0.875rem',
                      color: 'rgba(232,230,227,0.6)',
                      marginBottom: '0.5rem',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      Upcoming
                    </div>
                    <div style={{
                      fontSize: '1.75rem',
                      fontWeight: 700,
                      color: '#a855f7',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      $860
                    </div>
                  </div>
                </div>
              </div>

              {/* Monetization Status & Quick Actions */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1.5rem'
              }}>
                {/* Monetization Status */}
                <div style={{
                  background: 'rgba(232,230,227,0.03)',
                  border: '1px solid rgba(232,230,227,0.1)',
                  borderRadius: '12px',
                  padding: '1.5rem'
                }}>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: 700,
                    margin: '0 0 1.5rem 0',
                    fontFamily: "'Inter', sans-serif",
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span>🚦</span> Monetization Status
                  </h3>
                  
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '0.9375rem',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      <span style={{ color: 'rgba(232,230,227,0.8)' }}>Email:</span>
                      <span style={{
                        color: '#10b981',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: '#10b981'
                        }} />
                        Enabled
                      </span>
                    </div>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '0.9375rem',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      <span style={{ color: 'rgba(232,230,227,0.8)' }}>Website:</span>
                      <span style={{
                        color: '#10b981',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: '#10b981'
                        }} />
                        Enabled
                      </span>
                    </div>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '0.9375rem',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      <span style={{ color: 'rgba(232,230,227,0.8)' }}>Payouts:</span>
                      <span style={{
                        color: '#10b981',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: '#10b981'
                        }} />
                        Connected
                      </span>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div style={{
                  background: 'rgba(232,230,227,0.03)',
                  border: '1px solid rgba(232,230,227,0.1)',
                  borderRadius: '12px',
                  padding: '1.5rem'
                }}>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: 700,
                    margin: '0 0 1.5rem 0',
                    fontFamily: "'Inter', sans-serif",
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span>▶️</span> Quick Actions
                  </h3>
                  
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    <button style={{
                      padding: '0.75rem 1rem',
                      background: 'transparent',
                      border: '1px solid rgba(168,85,247,0.3)',
                      borderRadius: '8px',
                      color: '#a855f7',
                      fontSize: '0.9375rem',
                      fontWeight: 600,
                      fontFamily: "'Inter', sans-serif",
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(168,85,247,0.1)';
                      e.currentTarget.style.borderColor = '#a855f7';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.borderColor = 'rgba(168,85,247,0.3)';
                    }}>
                      View Opportunities
                    </button>

                    <button style={{
                      padding: '0.75rem 1rem',
                      background: 'transparent',
                      border: '1px solid rgba(168,85,247,0.3)',
                      borderRadius: '8px',
                      color: '#a855f7',
                      fontSize: '0.9375rem',
                      fontWeight: 600,
                      fontFamily: "'Inter', sans-serif",
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(168,85,247,0.1)';
                      e.currentTarget.style.borderColor = '#a855f7';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.borderColor = 'rgba(168,85,247,0.3)';
                    }}>
                      Update Options
                    </button>

                    <button style={{
                      padding: '0.75rem 1rem',
                      background: 'transparent',
                      border: '1px solid rgba(168,85,247,0.3)',
                      borderRadius: '8px',
                      color: '#a855f7',
                      fontSize: '0.9375rem',
                      fontWeight: 600,
                      fontFamily: "'Inter', sans-serif",
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(168,85,247,0.1)';
                      e.currentTarget.style.borderColor = '#a855f7';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.borderColor = 'rgba(168,85,247,0.3)';
                    }}>
                      Review Approvals
                    </button>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div style={{
                background: 'rgba(232,230,227,0.03)',
                border: '1px solid rgba(232,230,227,0.1)',
                borderRadius: '12px',
                padding: '1.5rem'
              }}>
                <h3 style={{
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  margin: '0 0 1.5rem 0',
                  fontFamily: "'Inter', sans-serif",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span>📊</span> Recent Activity
                </h3>
                
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    fontSize: '0.9375rem',
                    fontFamily: "'Inter', sans-serif",
                    color: 'rgba(232,230,227,0.8)'
                  }}>
                    <span style={{ color: '#10b981' }}>•</span>
                    <span>Campaign delivered – <strong style={{ color: '#10b981' }}>$320</strong></span>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    fontSize: '0.9375rem',
                    fontFamily: "'Inter', sans-serif",
                    color: 'rgba(232,230,227,0.8)'
                  }}>
                    <span style={{ color: '#3b82f6' }}>•</span>
                    <span>Opportunity accepted</span>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    fontSize: '0.9375rem',
                    fontFamily: "'Inter', sans-serif",
                    color: 'rgba(232,230,227,0.8)'
                  }}>
                    <span style={{ color: '#10b981' }}>•</span>
                    <span>Payout sent – <strong style={{ color: '#10b981' }}>$740</strong></span>
                  </div>
                </div>
              </div>
            </div>
          )}


          {selectedTab === 'approvals' && (
            <div>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                margin: '0 0 0.5rem 0',
                fontFamily: "'Inter', sans-serif"
              }}>
                Creative Approvals
              </h3>
              <p style={{
                color: 'rgba(232,230,227,0.6)',
                fontFamily: "'Inter', sans-serif",
                marginBottom: '2rem'
              }}>
                Review and approve creative submissions from advertisers before they go live.
              </p>

              {pendingCreatives.length === 0 && allMyCreatives.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '4rem 2rem',
                  background: 'rgba(232,230,227,0.03)',
                  border: '1px solid rgba(232,230,227,0.1)',
                  borderRadius: '12px'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                  <h4 style={{ margin: '0 0 0.5rem 0', fontFamily: "'Inter', sans-serif", color: 'rgba(232,230,227,0.8)' }}>No Creatives Yet</h4>
                  <p style={{ color: 'rgba(232,230,227,0.5)', fontFamily: "'Inter', sans-serif" }}>
                    When advertisers submit creative for your approval, they will appear here.
                  </p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  {/* Pending Creatives */}
                  {pendingCreatives.length > 0 && (
                    <div>
                      <h4 style={{ 
                        fontSize: '1.125rem', 
                        fontWeight: 600, 
                        margin: '0 0 1rem 0', 
                        fontFamily: "'Inter', sans-serif",
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          background: 'rgba(245,158,11,0.2)',
                          color: '#f59e0b',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 700
                        }}>
                          {pendingCreatives.length}
                        </span>
                        Pending Review
                      </h4>
                      
                      {pendingCreatives.map(creative => (
                        <div key={creative.id} style={{
                          background: 'rgba(245,158,11,0.05)',
                          border: '1px solid rgba(245,158,11,0.2)',
                          borderRadius: '12px',
                          marginBottom: '1rem',
                          overflow: 'hidden'
                        }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0' }}>
                            {/* Creative Details */}
                            <div style={{ padding: '1.5rem', borderRight: '1px solid rgba(232,230,227,0.1)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                                <span style={{
                                  padding: '0.25rem 0.5rem',
                                  background: 'rgba(245,158,11,0.2)',
                                  color: '#f59e0b',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  fontFamily: "'Inter', sans-serif"
                                }}>
                                  ⏳ Pending Review
                                </span>
                                <span style={{ fontSize: '0.75rem', color: 'rgba(232,230,227,0.5)', fontFamily: "'Inter', sans-serif" }}>
                                  Submitted {formatDate(creative.createdAt)}
                                </span>
                              </div>
                              
                              <div style={{ marginBottom: '1rem' }}>
                                <div style={{ fontSize: '0.8125rem', color: 'rgba(232,230,227,0.5)', fontFamily: "'Inter', sans-serif", marginBottom: '0.25rem' }}>
                                  From Advertiser
                                </div>
                                <div style={{ fontWeight: 600, fontSize: '1rem', fontFamily: "'Inter', sans-serif" }}>
                                  {creative.advertiserName || creative.companyName}
                                </div>
                                <div style={{ fontSize: '0.8125rem', color: 'rgba(232,230,227,0.6)', fontFamily: "'Inter', sans-serif" }}>
                                  {creative.advertiserEmail}
                                </div>
                              </div>
                              
                              <div style={{ marginBottom: '1rem' }}>
                                <div style={{ fontSize: '0.8125rem', color: 'rgba(232,230,227,0.5)', fontFamily: "'Inter', sans-serif", marginBottom: '0.25rem' }}>
                                  Placement Type
                                </div>
                                <div style={{ fontSize: '0.9375rem', fontFamily: "'Inter', sans-serif" }}>
                                  {creative.placementType || 'Newsletter Placement'}
                                </div>
                              </div>
                              
                              <div style={{ marginBottom: '1rem' }}>
                                <div style={{ fontSize: '0.8125rem', color: 'rgba(232,230,227,0.5)', fontFamily: "'Inter', sans-serif", marginBottom: '0.25rem' }}>
                                  CTA Link
                                </div>
                                <a href={creative.ctaUrl} target="_blank" rel="noopener noreferrer" style={{ 
                                  fontSize: '0.875rem', 
                                  color: '#a855f7', 
                                  fontFamily: "'Inter', sans-serif",
                                  wordBreak: 'break-all'
                                }}>
                                  {creative.ctaUrl}
                                </a>
                              </div>
                              
                              {/* Feedback Input */}
                              <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.375rem', fontFamily: "'Inter', sans-serif", color: 'rgba(232,230,227,0.8)' }}>
                                  Feedback (optional)
                                </label>
                                <textarea
                                  value={selectedCreative?.id === creative.id ? feedbackText : ''}
                                  onChange={(e) => {
                                    setSelectedCreative(creative);
                                    setFeedbackText(e.target.value);
                                  }}
                                  onFocus={() => setSelectedCreative(creative)}
                                  placeholder="Add feedback if requesting changes..."
                                  rows={2}
                                  style={{
                                    width: '100%',
                                    padding: '0.5rem 0.75rem',
                                    background: 'rgba(232,230,227,0.05)',
                                    border: '1px solid rgba(232,230,227,0.2)',
                                    borderRadius: '6px',
                                    color: '#e8e6e3',
                                    fontSize: '0.8125rem',
                                    fontFamily: "'Inter', sans-serif",
                                    resize: 'none'
                                  }}
                                />
                              </div>
                              
                              {/* Action Buttons */}
                              <div style={{ display: 'flex', gap: '0.75rem' }}>
                                <button
                                  onClick={() => {
                                    updateCreativeStatus(creative.id, 'approved');
                                    alert(`Creative approved! ${creative.advertiserName} will be notified.`);
                                    setRefreshKey(prev => prev + 1);
                                  }}
                                  style={{
                                    flex: 1,
                                    padding: '0.625rem 1rem',
                                    background: '#10b981',
                                    border: 'none',
                                    borderRadius: '6px',
                                    color: '#fff',
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    fontFamily: "'Inter', sans-serif",
                                    cursor: 'pointer'
                                  }}
                                >
                                  ✓ Approve
                                </button>
                                <button
                                  onClick={() => {
                                    if (!feedbackText && selectedCreative?.id === creative.id) {
                                      alert('Please provide feedback explaining what changes are needed.');
                                      return;
                                    }
                                    updateCreativeStatus(creative.id, 'changes_requested', feedbackText || 'Please revise and resubmit.');
                                    alert(`Changes requested. ${creative.advertiserName} will be notified with your feedback.`);
                                    setFeedbackText('');
                                    setSelectedCreative(null);
                                    setRefreshKey(prev => prev + 1);
                                  }}
                                  style={{
                                    padding: '0.625rem 1rem',
                                    background: 'transparent',
                                    border: '1px solid rgba(245,158,11,0.5)',
                                    borderRadius: '6px',
                                    color: '#f59e0b',
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    fontFamily: "'Inter', sans-serif",
                                    cursor: 'pointer'
                                  }}
                                >
                                  ⚠ Request Changes
                                </button>
                                <button
                                  onClick={() => {
                                    if (window.confirm('Are you sure you want to reject this creative?')) {
                                      updateCreativeStatus(creative.id, 'rejected', feedbackText || 'Creative does not meet guidelines.');
                                      alert(`Creative rejected. ${creative.advertiserName} will be notified.`);
                                      setFeedbackText('');
                                      setSelectedCreative(null);
                                      setRefreshKey(prev => prev + 1);
                                    }
                                  }}
                                  style={{
                                    padding: '0.625rem 1rem',
                                    background: 'transparent',
                                    border: '1px solid rgba(239,68,68,0.5)',
                                    borderRadius: '6px',
                                    color: '#ef4444',
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    fontFamily: "'Inter', sans-serif",
                                    cursor: 'pointer'
                                  }}
                                >
                                  ✗ Reject
                                </button>
                              </div>
                            </div>
                            
                            {/* Creative Preview */}
                            <div style={{ padding: '1.5rem', background: 'rgba(232,230,227,0.02)' }}>
                              <div style={{ fontSize: '0.8125rem', color: 'rgba(232,230,227,0.5)', fontFamily: "'Inter', sans-serif", marginBottom: '0.75rem' }}>
                                📱 Email Preview
                              </div>
                              
                              {/* Klaviyo Block Preview */}
                              <div style={{
                                background: '#ffffff',
                                borderRadius: '8px',
                                padding: '1.25rem',
                                color: '#1a1a1a'
                              }}>
                                <div style={{ 
                                  fontSize: '0.625rem', 
                                  color: '#888', 
                                  textTransform: 'uppercase', 
                                  letterSpacing: '0.05em',
                                  marginBottom: '0.5rem',
                                  fontFamily: "'Inter', sans-serif"
                                }}>
                                  Sponsored
                                </div>

                                {creative.imageUrl && (
                                  <div style={{
                                    width: '100%',
                                    height: '100px',
                                    background: `url(${creative.imageUrl}) center/cover no-repeat`,
                                    borderRadius: '4px',
                                    marginBottom: '0.75rem',
                                    backgroundColor: '#f0f0f0'
                                  }} />
                                )}

                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                  {creative.logoUrl ? (
                                    <img src={creative.logoUrl} alt="Logo" style={{ width: '28px', height: '28px', borderRadius: '4px', objectFit: 'cover' }} />
                                  ) : (
                                    <div style={{ 
                                      width: '28px', 
                                      height: '28px', 
                                      borderRadius: '4px', 
                                      background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: '#fff',
                                      fontWeight: 700,
                                      fontSize: '0.75rem'
                                    }}>
                                      {creative.companyName?.charAt(0) || 'A'}
                                    </div>
                                  )}
                                  <div style={{ fontWeight: 600, fontSize: '0.8125rem', fontFamily: "'Inter', sans-serif" }}>
                                    {creative.companyName}
                                  </div>
                                </div>

                                <h5 style={{ 
                                  fontSize: '1rem', 
                                  fontWeight: 700, 
                                  margin: '0 0 0.375rem 0',
                                  fontFamily: "'Inter', sans-serif",
                                  color: '#1a1a1a'
                                }}>
                                  {creative.headline}
                                </h5>

                                <p style={{ 
                                  fontSize: '0.8125rem', 
                                  color: '#4a4a4a', 
                                  margin: '0 0 0.5rem 0',
                                  fontFamily: "'Inter', sans-serif",
                                  lineHeight: 1.4
                                }}>
                                  {creative.bodyText}
                                </p>

                                {creative.offerDetails && (
                                  <div style={{
                                    background: '#f5f5f5',
                                    padding: '0.5rem',
                                    borderRadius: '4px',
                                    marginBottom: '0.75rem',
                                    fontSize: '0.75rem',
                                    fontFamily: "'Inter', sans-serif",
                                    color: '#333'
                                  }}>
                                    💡 {creative.offerDetails}
                                  </div>
                                )}

                                <button style={{
                                  display: 'inline-block',
                                  padding: '0.5rem 1.25rem',
                                  background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                                  border: 'none',
                                  borderRadius: '4px',
                                  color: '#fff',
                                  fontSize: '0.8125rem',
                                  fontWeight: 600,
                                  fontFamily: "'Inter', sans-serif"
                                }}>
                                  {creative.ctaText}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Previously Reviewed Creatives */}
                  {allMyCreatives.filter(c => c.status !== 'pending').length > 0 && (
                    <div>
                      <h4 style={{ 
                        fontSize: '1.125rem', 
                        fontWeight: 600, 
                        margin: '0 0 1rem 0', 
                        fontFamily: "'Inter', sans-serif"
                      }}>
                        Previously Reviewed
                      </h4>
                      
                      <div style={{ display: 'grid', gap: '0.75rem' }}>
                        {allMyCreatives.filter(c => c.status !== 'pending').map(creative => (
                          <div key={creative.id} style={{
                            padding: '1rem',
                            background: 'rgba(232,230,227,0.03)',
                            border: '1px solid rgba(232,230,227,0.1)',
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: '0.9375rem', fontFamily: "'Inter', sans-serif", marginBottom: '0.25rem' }}>
                                {creative.headline}
                              </div>
                              <div style={{ fontSize: '0.8125rem', color: 'rgba(232,230,227,0.6)', fontFamily: "'Inter', sans-serif" }}>
                                {creative.advertiserName} • {creative.placementType}
                              </div>
                            </div>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              fontFamily: "'Inter', sans-serif",
                              background: creative.status === 'approved' ? 'rgba(16,185,129,0.2)' : 
                                          creative.status === 'rejected' ? 'rgba(239,68,68,0.2)' : 
                                          'rgba(245,158,11,0.2)',
                              color: creative.status === 'approved' ? '#10b981' : 
                                     creative.status === 'rejected' ? '#ef4444' : 
                                     '#f59e0b'
                            }}>
                              {creative.status === 'approved' ? '✓ Approved' : 
                               creative.status === 'rejected' ? '✗ Rejected' : 
                               '⚠ Changes Requested'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {selectedTab === 'history' && (
            <div>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                margin: '0 0 1rem 0',
                fontFamily: "'Inter', sans-serif"
              }}>
                Campaign History
              </h3>
              <p style={{
                color: 'rgba(232,230,227,0.7)',
                fontFamily: "'Inter', sans-serif"
              }}>
                View your past campaigns and performance here.
              </p>
            </div>
          )}

          {selectedTab === 'payouts' && (
            <div>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                margin: '0 0 1rem 0',
                fontFamily: "'Inter', sans-serif"
              }}>
                Payouts
              </h3>
              <p style={{
                color: 'rgba(232,230,227,0.7)',
                fontFamily: "'Inter', sans-serif"
              }}>
                Track your earnings and payout history here.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, change, positive, delay }) {
  return (
    <div style={{
      background: 'rgba(232,230,227,0.03)',
      border: '1px solid rgba(232,230,227,0.1)',
      borderRadius: '16px',
      padding: '1.75rem',
      transition: 'all 0.3s ease',
      cursor: 'default',
      animation: `slideUp 0.6s ease-out ${delay} backwards`
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = 'rgba(232,230,227,0.06)';
      e.currentTarget.style.borderColor = 'rgba(168,85,247,0.3)';
      e.currentTarget.style.transform = 'translateY(-4px)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = 'rgba(232,230,227,0.03)';
      e.currentTarget.style.borderColor = 'rgba(232,230,227,0.1)';
      e.currentTarget.style.transform = 'translateY(0)';
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        marginBottom: '1rem'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, rgba(168,85,247,0.2) 0%, rgba(236,72,153,0.2) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid rgba(168,85,247,0.3)'
        }}>
          <Icon size={24} color="#a855f7" />
        </div>
        <div>
          <div style={{
            fontSize: '0.8125rem',
            color: 'rgba(232,230,227,0.6)',
            marginBottom: '0.25rem',
            fontFamily: "'Inter', sans-serif",
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            {label}
          </div>
          <div style={{
            fontSize: '1.875rem',
            fontWeight: 700,
            fontFamily: "'Inter', sans-serif",
            letterSpacing: '-0.02em'
          }}>
            {value}
          </div>
        </div>
      </div>
      <div style={{
        fontSize: '0.875rem',
        color: positive ? '#10b981' : '#ef4444',
        fontFamily: "'Inter', sans-serif",
        fontWeight: 500
      }}>
        {change}
      </div>
    </div>
  );
}

function CampaignsTab({ data }) {
  return (
    <div style={{ animation: 'fadeIn 0.6s ease-out' }}>
      <h2 style={{
        fontSize: '2.5rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.02em'
      }}>
        Campaigns
      </h2>
      <p style={{
        fontSize: '1.125rem',
        color: 'rgba(232,230,227,0.7)',
        margin: '0 0 3rem 0',
        fontFamily: "'Inter', sans-serif"
      }}>
        Manage your email campaigns and ad insertion settings
      </p>

      <div style={{
        background: 'rgba(232,230,227,0.03)',
        border: '1px solid rgba(232,230,227,0.1)',
        borderRadius: '16px',
        padding: '2rem'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '4rem 2rem'
        }}>
          <FileText size={64} style={{ color: 'rgba(232,230,227,0.3)', margin: '0 auto 1.5rem' }} />
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: 600,
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif"
          }}>
            Configure Your Campaigns
          </h3>
          <p style={{
            fontSize: '1rem',
            color: 'rgba(232,230,227,0.6)',
            marginBottom: '2rem',
            fontFamily: "'Inter', sans-serif",
            maxWidth: '500px',
            margin: '0 auto 2rem'
          }}>
            Set up your email campaigns, define ad placement rules, and start monetizing your email inventory
          </p>
          <button style={{
            padding: '1rem 2rem',
            background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '1rem',
            fontWeight: 600,
            fontFamily: "'Inter', sans-serif",
            cursor: 'pointer',
            transition: 'transform 0.2s ease',
            boxShadow: '0 4px 12px rgba(168,85,247,0.3)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
          }}>
            Add Your First Campaign
          </button>
        </div>
      </div>
    </div>
  );
}

function EarningsTab({ data }) {
  return (
    <div style={{ animation: 'fadeIn 0.6s ease-out' }}>
      <h2 style={{
        fontSize: '2.5rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.02em'
      }}>
        Earnings
      </h2>
      <p style={{
        fontSize: '1.125rem',
        color: 'rgba(232,230,227,0.7)',
        margin: '0 0 3rem 0',
        fontFamily: "'Inter', sans-serif"
      }}>
        Track your revenue and payment history
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '1.5rem',
        marginBottom: '3rem'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, rgba(168,85,247,0.1) 0%, rgba(236,72,153,0.1) 100%)',
          border: '1px solid rgba(168,85,247,0.3)',
          borderRadius: '16px',
          padding: '2rem'
        }}>
          <div style={{
            fontSize: '0.875rem',
            color: 'rgba(232,230,227,0.6)',
            marginBottom: '0.5rem',
            fontFamily: "'Inter', sans-serif",
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Total Earnings
          </div>
          <div style={{
            fontSize: '3rem',
            fontWeight: 700,
            fontFamily: "'Inter', sans-serif",
            letterSpacing: '-0.02em',
            marginBottom: '0.5rem'
          }}>
            ${data.earnings.total.toLocaleString()}
          </div>
          <div style={{
            fontSize: '0.875rem',
            color: '#10b981',
            fontFamily: "'Inter', sans-serif",
            fontWeight: 500
          }}>
            All time revenue
          </div>
        </div>

        <div style={{
          background: 'rgba(232,230,227,0.03)',
          border: '1px solid rgba(232,230,227,0.1)',
          borderRadius: '16px',
          padding: '2rem'
        }}>
          <div style={{
            fontSize: '0.875rem',
            color: 'rgba(232,230,227,0.6)',
            marginBottom: '0.5rem',
            fontFamily: "'Inter', sans-serif",
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            This Month
          </div>
          <div style={{
            fontSize: '3rem',
            fontWeight: 700,
            fontFamily: "'Inter', sans-serif",
            letterSpacing: '-0.02em',
            marginBottom: '0.5rem'
          }}>
            ${data.earnings.thisMonth.toLocaleString()}
          </div>
          <div style={{
            fontSize: '0.875rem',
            color: data.earnings.thisMonth > 0 ? '#10b981' : 'rgba(232,230,227,0.5)',
            fontFamily: "'Inter', sans-serif",
            fontWeight: 500
          }}>
            {data.earnings.thisMonth > 0 ? '+10.1% vs last month' : 'No earnings yet'}
          </div>
        </div>

        <div style={{
          background: 'rgba(232,230,227,0.03)',
          border: '1px solid rgba(232,230,227,0.1)',
          borderRadius: '16px',
          padding: '2rem'
        }}>
          <div style={{
            fontSize: '0.875rem',
            color: 'rgba(232,230,227,0.6)',
            marginBottom: '0.5rem',
            fontFamily: "'Inter', sans-serif",
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Last Month
          </div>
          <div style={{
            fontSize: '3rem',
            fontWeight: 700,
            fontFamily: "'Inter', sans-serif",
            letterSpacing: '-0.02em',
            marginBottom: '0.5rem'
          }}>
            ${data.earnings.lastMonth.toLocaleString()}
          </div>
          <div style={{
            fontSize: '0.875rem',
            color: 'rgba(232,230,227,0.6)',
            fontFamily: "'Inter', sans-serif"
          }}>
            January 2026
          </div>
        </div>
      </div>

      <div style={{
        background: 'rgba(232,230,227,0.03)',
        border: '1px solid rgba(232,230,227,0.1)',
        borderRadius: '16px',
        padding: '2rem'
      }}>
        <h3 style={{
          fontSize: '1.5rem',
          fontWeight: 700,
          margin: '0 0 1.5rem 0',
          letterSpacing: '-0.01em'
        }}>
          Payment History
        </h3>
        <div style={{
          fontSize: '1rem',
          color: 'rgba(232,230,227,0.6)',
          fontFamily: "'Inter', sans-serif",
          textAlign: 'center',
          padding: '2rem'
        }}>
          Your payment history will appear here once your first payout is processed
        </div>
      </div>
    </div>
  );
}

function SettingsTab({ data }) {
  const [frequency, setFrequency] = useState('2');
  const [minCTR, setMinCTR] = useState('2.0');

  return (
    <div style={{ animation: 'fadeIn 0.6s ease-out' }}>
      <h2 style={{
        fontSize: '2.5rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        letterSpacing: '-0.02em'
      }}>
        Settings
      </h2>
      <p style={{
        fontSize: '1.125rem',
        color: 'rgba(232,230,227,0.7)',
        margin: '0 0 3rem 0',
        fontFamily: "'Inter', sans-serif"
      }}>
        Configure your account and ad insertion preferences
      </p>

      <div style={{
        display: 'grid',
        gap: '2rem',
        maxWidth: '800px'
      }}>
        {/* Company Info */}
        <div style={{
          background: 'rgba(232,230,227,0.03)',
          border: '1px solid rgba(232,230,227,0.1)',
          borderRadius: '16px',
          padding: '2rem'
        }}>
          <h3 style={{
            fontSize: '1.25rem',
            fontWeight: 700,
            margin: '0 0 1.5rem 0',
            fontFamily: "'Inter', sans-serif"
          }}>
            Company Information
          </h3>
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 600,
                marginBottom: '0.5rem',
                fontFamily: "'Inter', sans-serif",
                color: 'rgba(232,230,227,0.8)'
              }}>
                Company Name
              </label>
              <input
                type="text"
                defaultValue={data.name}
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  background: 'rgba(232,230,227,0.05)',
                  border: '1px solid rgba(232,230,227,0.2)',
                  borderRadius: '8px',
                  color: '#e8e6e3',
                  fontSize: '1rem',
                  fontFamily: "'Inter', sans-serif",
                  outline: 'none',
                  transition: 'all 0.2s ease'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#a855f7';
                  e.currentTarget.style.background = 'rgba(232,230,227,0.08)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(232,230,227,0.2)';
                  e.currentTarget.style.background = 'rgba(232,230,227,0.05)';
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 600,
                marginBottom: '0.5rem',
                fontFamily: "'Inter', sans-serif",
                color: 'rgba(232,230,227,0.8)'
              }}>
                Email Database Size
              </label>
              <input
                type="text"
                value={data.emailDatabase.toLocaleString()}
                readOnly
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  background: 'rgba(232,230,227,0.02)',
                  border: '1px solid rgba(232,230,227,0.1)',
                  borderRadius: '8px',
                  color: 'rgba(232,230,227,0.6)',
                  fontSize: '1rem',
                  fontFamily: "'Inter', sans-serif",
                  cursor: 'not-allowed'
                }}
              />
            </div>
          </div>
        </div>

        {/* Ad Preferences */}
        <div style={{
          background: 'rgba(232,230,227,0.03)',
          border: '1px solid rgba(232,230,227,0.1)',
          borderRadius: '16px',
          padding: '2rem'
        }}>
          <h3 style={{
            fontSize: '1.25rem',
            fontWeight: 700,
            margin: '0 0 1.5rem 0',
            fontFamily: "'Inter', sans-serif"
          }}>
            Ad Insertion Preferences
          </h3>
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 600,
                marginBottom: '0.5rem',
                fontFamily: "'Inter', sans-serif",
                color: 'rgba(232,230,227,0.8)'
              }}>
                Maximum Ads per Email
              </label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  background: 'rgba(232,230,227,0.05)',
                  border: '1px solid rgba(232,230,227,0.2)',
                  borderRadius: '8px',
                  color: '#e8e6e3',
                  fontSize: '1rem',
                  fontFamily: "'Inter', sans-serif",
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                <option value="1">1 ad per email</option>
                <option value="2">2 ads per email</option>
                <option value="3">3 ads per email</option>
                <option value="4">4 ads per email</option>
              </select>
            </div>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 600,
                marginBottom: '0.5rem',
                fontFamily: "'Inter', sans-serif",
                color: 'rgba(232,230,227,0.8)'
              }}>
                Minimum CTR Threshold
              </label>
              <input
                type="text"
                value={minCTR}
                onChange={(e) => setMinCTR(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  background: 'rgba(232,230,227,0.05)',
                  border: '1px solid rgba(232,230,227,0.2)',
                  borderRadius: '8px',
                  color: '#e8e6e3',
                  fontSize: '1rem',
                  fontFamily: "'Inter', sans-serif",
                  outline: 'none'
                }}
              />
              <div style={{
                fontSize: '0.8125rem',
                color: 'rgba(232,230,227,0.5)',
                marginTop: '0.5rem',
                fontFamily: "'Inter', sans-serif"
              }}>
                Only show ads with at least this CTR to maintain quality
              </div>
            </div>
          </div>
        </div>

        <button style={{
          padding: '1rem 2rem',
          background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
          border: 'none',
          borderRadius: '8px',
          color: '#fff',
          fontSize: '1rem',
          fontWeight: 600,
          fontFamily: "'Inter', sans-serif",
          cursor: 'pointer',
          transition: 'transform 0.2s ease',
          boxShadow: '0 4px 12px rgba(168,85,247,0.3)',
          justifySelf: 'start'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
        }}>
          Save Changes
        </button>
      </div>
    </div>
  );
}

